import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import axios from "axios";
import * as XLSX from "xlsx";
import AppShell from "../components/layout/AppShell";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Icon from "../components/ui/Icon";
import Modal from "../components/ui/Modal";
import Input from "../components/ui/Input";
import { Parse } from "../lib/parse";
import { getMenuItems, isAdminRole, reportViewById } from "../config/navigation";
import { useAlert } from "../contexts/AlertContext";

const NEED_YOUR_SIGN_REPORT_ID = "4Hhwbp482K";
const IN_PROGRESS_REPORT_ID = "1MwEuxLEkF";
const COMPLETED_REPORT_ID = "kQUoW4hUXz";
const DECLINED_REPORT_ID = "UPr2Fm5WY3";
const EXPIRED_REPORT_ID = "zNqBHXHsYH";
const TEMPLATE_REPORT_ID = "6TeaPr321t";
const CONTACTS_REPORT_ID = "contacts";
const DRAFTS_REPORT_ID = "ByHuevtCFY";
const DRIVE_PAGE_LIMIT = 50;
const HISTORY_PAGE_LIMIT = 50;
const CONTACT_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CONTACT_IMPORT_MAX_ROWS = 100;
const CONTACT_IMPORT_PAGE_SIZE = 6;
const normalizeContactImportHeader = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]/g, "");

const resolveContactImportHeader = (value) => {
  const normalizedKey = normalizeContactImportHeader(value);
  if (!normalizedKey) {
    return "";
  }
  if (["name", "nombre"].includes(normalizedKey)) {
    return "Name";
  }
  if (["email", "correo", "correoelectronico", "eemail"].includes(normalizedKey)) {
    return "Email";
  }
  if (["phone", "telefono", "tel", "movil", "celular"].includes(normalizedKey)) {
    return "Phone";
  }
  return "";
};

const localeByLanguage = {
  es: "es-MX",
  en: "en-US",
  fr: "fr-FR",
  de: "de-DE",
  it: "it-IT"
};

const parseStoredJson = (key) => {
  try {
    const rawValue = localStorage.getItem(key);
    return rawValue ? JSON.parse(rawValue) : null;
  } catch (error) {
    return null;
  }
};

const normalizeDocumentPayload = (response) => {
  if (!response) return null;
  if (response?.error) return null;
  if (response?.result?.error) return null;
  return response?.result || response;
};

const toPlainObject = (value) => {
  if (!value) return null;
  if (typeof value.toJSON === "function") {
    return value.toJSON();
  }
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (error) {
    return value;
  }
};

const normalizeCloudResult = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload?.result)) {
    return payload.result;
  }
  return [];
};

const normalizeTemplatePayload = (payload) => {
  if (!payload) {
    return null;
  }
  if (payload?.error || payload?.result?.error) {
    return null;
  }
  const normalized = payload?.result || payload;
  if (!normalized || typeof normalized !== "object") {
    return null;
  }
  return toPlainObject(normalized) || null;
};

const fetchTemplateRecordById = async (templateId) => {
  const normalizedTemplateId = String(templateId || "").trim();
  if (!normalizedTemplateId) {
    return null;
  }

  try {
    const cloudPayload = await Parse.Cloud.run("getTemplate", {
      templateId: normalizedTemplateId
    });
    const cloudTemplate = normalizeTemplatePayload(cloudPayload);
    if (cloudTemplate?.objectId) {
      return cloudTemplate;
    }
  } catch (error) {
    // Fallback to REST cloud function if Parse.Cloud.run fails.
  }

  const baseUrl = getParseFunctionBaseUrl();
  if (!baseUrl) {
    throw new Error("Template endpoint is not available.");
  }
  const response = await axios.post(
    `${baseUrl}functions/getTemplate`,
    { templateId: normalizedTemplateId },
    { headers: getParseFunctionHeaders() }
  );
  const restTemplate = normalizeTemplatePayload(response?.data);
  if (!restTemplate?.objectId) {
    throw new Error("Template not found.");
  }
  return restTemplate;
};

const hasInProgressRecipients = (record) => {
  const placeholders = Array.isArray(record?.Placeholders) ? record.Placeholders : [];
  const signers = Array.isArray(record?.Signers) ? record.Signers : [];
  return placeholders.length > 0 || signers.length > 0;
};

const isInProgressDocumentRecord = (record) => {
  if (!record || record?.Type === "Folder") {
    return false;
  }
  if (record?.IsArchive || record?.IsCompleted || record?.IsDeclined) {
    return false;
  }
  if (record?.IsDraft || record?.Draft) {
    return false;
  }
  const expiry = parseDocDate(record?.ExpiryDate);
  if (expiry && expiry.getTime() <= Date.now()) {
    return false;
  }
  return hasInProgressRecipients(record);
};

const applyInProgressIncludes = (query) => {
  query.include("AuditTrail.UserPtr");
  query.include("Placeholders.signerPtr");
  query.include("Signers");
  query.include("Signers.UserId");
  query.include("ExtUserPtr");
  query.include("ExtUserPtr.TenantId");
  query.include("Folder");
};

const getParseFunctionBaseUrl = () => {
  const baseUrl = String(localStorage.getItem("baseUrl") || "").trim();
  if (!baseUrl) {
    return "";
  }
  if (baseUrl.startsWith(":")) {
    return `${window.location.protocol}//${window.location.hostname}${baseUrl}`;
  }
  return baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
};

const getParseFunctionHeaders = () => {
  const parseAppId = String(localStorage.getItem("parseAppId") || "").trim();
  const sessionToken = String(localStorage.getItem("accesstoken") || "").trim();
  return {
    "Content-Type": "application/json",
    ...(parseAppId ? { "X-Parse-Application-Id": parseAppId } : {}),
    ...(sessionToken ? { sessiontoken: sessionToken } : {}),
    ...(sessionToken ? { "X-Parse-Session-Token": sessionToken } : {})
  };
};

const parseDocDate = (value) => {
  if (!value) {
    return null;
  }
  if (value instanceof Date) {
    return value;
  }
  if (typeof value === "object" && value.iso) {
    const parsedIso = new Date(value.iso);
    return Number.isNaN(parsedIso.getTime()) ? null : parsedIso;
  }
  if (typeof value === "string") {
    const parsedString = new Date(value);
    return Number.isNaN(parsedString.getTime()) ? null : parsedString;
  }
  return null;
};

const formatDate = (date, i18n, dateOnly = false) => {
  if (!date) {
    return "--";
  }

  const currentLanguage = (i18n.resolvedLanguage || i18n.language || "es").split("-")[0];
  const locale = localeByLanguage[currentLanguage] || localeByLanguage.es;

  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    ...(dateOnly
      ? {}
      : {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false
        })
  }).format(date);
};

const triggerDownload = (url) => {
  if (!url) return;
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.target = "_blank";
  anchor.rel = "noopener noreferrer";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
};

const triggerTextDownload = (fileName, content, mimeType = "application/json;charset=utf-8") => {
  const blob = new Blob([content], { type: mimeType });
  const blobUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = blobUrl;
  anchor.download = String(fileName || "payload.json").trim() || "payload.json";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(blobUrl);
};

const normalizeRoleKey = (value) => String(value || "").trim().toLowerCase();

const slugifyRecipientKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const normalizeBooleanOrNull = (value) => {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "number") {
    return value === 1 ? true : value === 0 ? false : null;
  }
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["1", "true", "yes", "y", "required", "enabled"].includes(normalized)) {
      return true;
    }
    if (["0", "false", "no", "n", "disabled"].includes(normalized)) {
      return false;
    }
  }
  return null;
};

const sanitizeVerificationSettings = (rawSettings) => {
  if (!rawSettings || typeof rawSettings !== "object" || Array.isArray(rawSettings)) {
    return null;
  }
  const threshold = Number.parseInt(rawSettings.threshold, 10);
  const requireDocumentBack = normalizeBooleanOrNull(rawSettings.requireDocumentBack);
  const notes = String(rawSettings.notes || "").trim();
  return {
    enabled: rawSettings.enabled !== false,
    threshold: Number.isFinite(threshold) ? threshold : 85,
    requireDocumentBack: requireDocumentBack === null ? true : requireDocumentBack,
    notes
  };
};

const normalizeTemplateRecipientEntry = (recipient, index = 0) => {
  const objectId = String(
    recipient?.objectId || recipient?.id || recipient?.signerObjId || recipient?.signerPtr?.objectId || ""
  ).trim();
  const email = String(recipient?.email || recipient?.Email || "").trim().toLowerCase();
  const name =
    String(recipient?.name || recipient?.Name || recipient?.role || recipient?.Role || "").trim() ||
    email ||
    `Signer ${index + 1}`;
  const providedId = String(recipient?.id || recipient?.Id || "").trim();
  const fallbackToken = slugifyRecipientKey(objectId || email || name) || `recipient-${index + 1}`;
  const explicitVerification = sanitizeVerificationSettings(recipient?.verificationSettings);
  const identityVerification =
    recipient?.identityVerification &&
    typeof recipient.identityVerification === "object" &&
    !Array.isArray(recipient.identityVerification)
      ? recipient.identityVerification
      : recipient?.IdentityVerification &&
          typeof recipient.IdentityVerification === "object" &&
          !Array.isArray(recipient.IdentityVerification)
        ? recipient.IdentityVerification
        : null;
  const requiresIdentityVerification =
    normalizeBooleanOrNull(recipient?.requiresIdentityVerification) ??
    normalizeBooleanOrNull(recipient?.RequiresIdentityVerification);
  const rawRequireBiometric = normalizeBooleanOrNull(recipient?.requireBiometric);
  const requireBiometric =
    rawRequireBiometric !== null
      ? rawRequireBiometric
      : requiresIdentityVerification !== null
        ? requiresIdentityVerification
        : Boolean(identityVerification || explicitVerification?.enabled);
  const verificationSettings =
    explicitVerification ||
    (identityVerification
      ? sanitizeVerificationSettings({
          enabled: true,
          threshold: identityVerification.threshold,
          requireDocumentBack: identityVerification.requireDocumentBack,
          notes: identityVerification.notes
        })
      : requireBiometric
        ? { enabled: true, threshold: 85, requireDocumentBack: true, notes: "" }
        : {});

  return {
    id: providedId || objectId || email || `recipient-${fallbackToken}`,
    objectId,
    name,
    email,
    requireBiometric,
    verificationSettings
  };
};

const buildTemplateRecipients = (template = {}) => {
  const recipients = [];
  const seen = new Set();
  const templateSigners = Array.isArray(template?.Signers) ? template.Signers : [];
  const signerById = new Map(
    templateSigners
      .map((signer) => {
        const objectId = String(signer?.objectId || signer?.id || "").trim();
        if (!objectId) {
          return null;
        }
        return [objectId, signer];
      })
      .filter(Boolean)
  );

  const pushRecipient = (rawRecipient) => {
    const normalized = normalizeTemplateRecipientEntry(rawRecipient, recipients.length);
    const dedupeKey =
      normalized.objectId || normalized.email || slugifyRecipientKey(normalized.name) || normalized.id;
    if (!dedupeKey || seen.has(dedupeKey)) {
      return;
    }
    seen.add(dedupeKey);
    recipients.push(normalized);
  };

  (Array.isArray(template?.NewUIRecipients) ? template.NewUIRecipients : []).forEach(pushRecipient);
  templateSigners.forEach(pushRecipient);

  (Array.isArray(template?.Placeholders) ? template.Placeholders : [])
    .filter((group) => normalizeRoleKey(group?.Role) !== "prefill")
    .forEach((group) => {
      const roleName = String(group?.Role || "").trim();
      if (!roleName) {
        return;
      }
      const signerObjId = String(group?.signerObjId || group?.signerPtr?.objectId || "").trim();
      const linkedSigner = signerObjId ? signerById.get(signerObjId) : null;
      pushRecipient({
        role: roleName,
        name: roleName,
        objectId: signerObjId,
        email:
          String(group?.email || linkedSigner?.Email || linkedSigner?.email || "")
            .trim()
            .toLowerCase() || "",
        identityVerification: group?.IdentityVerification,
        requiresIdentityVerification: group?.RequiresIdentityVerification
      });
    });

  return recipients;
};

const safeClone = (value, fallback = null) => {
  if (value === undefined) {
    return fallback;
  }
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (error) {
    return fallback;
  }
};

const toFiniteNumber = (value, fallback = undefined) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeBooleanOrUndefined = (value) => {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "number") {
    return value === 1 ? true : value === 0 ? false : undefined;
  }
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["1", "true", "yes", "y", "required", "enabled"].includes(normalized)) {
      return true;
    }
    if (["0", "false", "no", "n", "disabled"].includes(normalized)) {
      return false;
    }
  }
  return undefined;
};

const sanitizeJsonFileName = (value, fallback = "template-createdocument") => {
  const normalized = String(value || "")
    .trim()
    .replace(/[^a-zA-Z0-9-_]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
  return normalized || fallback;
};

const arrayBufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = "";
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    const chunk = bytes.subarray(offset, offset + chunkSize);
    binary += String.fromCharCode(...chunk);
  }
  return btoa(binary);
};

const DEFAULT_WIDGET_ORIGIN_X = 80;
const DEFAULT_WIDGET_ORIGIN_Y = 120;
const DEFAULT_WIDGET_VERTICAL_SPACING = 70;
const DEFAULT_WIDGET_SIZE = {
  signature: { width: 200, height: 50 },
  initials: { width: 120, height: 45 },
  stamp: { width: 180, height: 60 },
  image: { width: 180, height: 120 },
  checkbox: { width: 16, height: 16 },
  "radio button": { width: 16, height: 16 },
  text: { width: 220, height: 24 },
  textbox: { width: 220, height: 24 },
  "text input": { width: 220, height: 24 },
  name: { width: 220, height: 24 },
  email: { width: 240, height: 24 },
  date: { width: 140, height: 24 },
  company: { width: 220, height: 24 },
  "job title": { width: 220, height: 24 }
};

const normalizeWidgetTypeForApi = (value) => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  if (!normalized) {
    return "";
  }
  if (["textbox", "prefill_textbox", "prefill-textbox", "prefill textbox"].includes(normalized)) {
    return "text";
  }
  if (["textinput", "text-input", "text_input"].includes(normalized)) {
    return "text input";
  }
  if (["radio", "radio-button", "radio_button", "radiobutton"].includes(normalized)) {
    return "radio button";
  }
  return normalized;
};

const getDefaultWidgetSize = (widgetType) => {
  const normalizedType = normalizeWidgetTypeForApi(widgetType);
  return DEFAULT_WIDGET_SIZE[normalizedType] || { width: 200, height: 24 };
};

const inferWidgetRequiredFlag = (options = {}, widget = {}) => {
  if (typeof options.required === "boolean") {
    return options.required;
  }
  const status = String(options.status || widget?.status || "").trim().toLowerCase();
  if (status === "required") {
    return true;
  }
  if (status === "optional") {
    return false;
  }
  return false;
};

const normalizeWidgetOptionsForApi = (widget, pageNumber, widgetIndex, widgetType) => {
  const safeOptions =
    widget?.options && typeof widget.options === "object" ? safeClone(widget.options, {}) : {};
  const options = safeOptions && typeof safeOptions === "object" ? safeOptions : {};

  if (!String(options?.name || "").trim()) {
    options.name = `${widgetType || "field"}_${Math.max(1, Math.trunc(pageNumber))}_${widgetIndex + 1}`;
  }
  if (typeof options.required !== "boolean") {
    options.required = inferWidgetRequiredFlag(options, widget);
  }
  if (!String(options?.status || "").trim()) {
    options.status = options.required ? "required" : "optional";
  }

  if (
    (widgetType === "checkbox" || widgetType === "radio button") &&
    (!Array.isArray(options.values) || options.values.length === 0)
  ) {
    const labelFallback = String(options.label || widget?.label || widget?.textValue || "Option 1").trim();
    options.values = [labelFallback || "Option 1"];
  }

  if (widget?.response !== undefined && options.response === undefined) {
    options.response = widget.response;
  }
  if (widget?.defaultValue !== undefined && options.defaultValue === undefined) {
    options.defaultValue = widget.defaultValue;
  }
  if (widget?.textValue !== undefined && options.defaultValue === undefined) {
    options.defaultValue = widget.textValue;
  }

  return options;
};

const buildSampleWidgetForRole = (role, roleIndex) => {
  const widgetIndex = Math.max(0, roleIndex);
  const x = DEFAULT_WIDGET_ORIGIN_X;
  const y = DEFAULT_WIDGET_ORIGIN_Y + widgetIndex * DEFAULT_WIDGET_VERTICAL_SPACING;
  const width = DEFAULT_WIDGET_SIZE.signature.width;
  const height = DEFAULT_WIDGET_SIZE.signature.height;
  return {
    key: `signature_${String(role || "role").replace(/\s+/g, "_").toLowerCase()}_${widgetIndex + 1}`,
    type: "signature",
    page: 1,
    x,
    y,
    w: width,
    h: height,
    width,
    height,
    xPosition: x,
    yPosition: y,
    Width: width,
    Height: height,
    scale: 1,
    zIndex: 1,
    options: {
      name: `firma_${String(role || "firmante").replace(/\s+/g, "_").toLowerCase()}`,
      required: true,
      status: "required"
    }
  };
};

const mapTemplateWidgetToApiWidget = (widget, pageNumber, fallbackKey, widgetIndex = 0) => {
  const type = String(widget?.type || widget?.widgetType || "").trim();
  if (!type) {
    return null;
  }
  const normalizedType = normalizeWidgetTypeForApi(type);
  if (!normalizedType) {
    return null;
  }

  const resolvedPage = Math.max(
    1,
    Math.trunc(
      toFiniteNumber(widget?.page ?? widget?.pageNumber ?? pageNumber, 1)
    )
  );
  const defaultSize = getDefaultWidgetSize(normalizedType);
  const fallbackX = DEFAULT_WIDGET_ORIGIN_X;
  const fallbackY = DEFAULT_WIDGET_ORIGIN_Y + widgetIndex * DEFAULT_WIDGET_VERTICAL_SPACING;

  const x = toFiniteNumber(widget?.xPosition ?? widget?.x, fallbackX);
  const y = toFiniteNumber(widget?.yPosition ?? widget?.y, fallbackY);
  const width = toFiniteNumber(widget?.Width ?? widget?.width ?? widget?.w, defaultSize.width);
  const height = toFiniteNumber(widget?.Height ?? widget?.height ?? widget?.h, defaultSize.height);

  const apiWidget = {
    key: String(widget?.key || widget?.id || fallbackKey || "").trim() || fallbackKey,
    type: normalizedType,
    page: resolvedPage,
    x,
    y,
    w: width,
    h: height,
    width,
    height,
    xPosition: x,
    yPosition: y,
    Width: width,
    Height: height
  };

  apiWidget.scale = toFiniteNumber(widget?.scale, 1);
  apiWidget.zIndex = toFiniteNumber(widget?.zIndex, 1);
  apiWidget.options = normalizeWidgetOptionsForApi(widget, resolvedPage, widgetIndex, normalizedType);
  if (widget?.IdentityVerification && typeof widget.IdentityVerification === "object") {
    apiWidget.IdentityVerification = safeClone(widget.IdentityVerification, {});
  }

  return apiWidget;
};

const extractApiWidgetsFromTemplateGroup = (group) => {
  const pages = Array.isArray(group?.placeHolder)
    ? group.placeHolder
    : Array.isArray(group?.pos)
      ? [{ pageNumber: group?.pageNumber || 1, pos: group.pos }]
      : Array.isArray(group?.widgets)
        ? [{ pageNumber: group?.pageNumber || 1, pos: group.widgets }]
        : [];
  const widgets = [];
  pages.forEach((page, pageIndex) => {
    const pageNumber = toFiniteNumber(page?.pageNumber ?? page?.page, pageIndex + 1);
    const pageWidgets = Array.isArray(page?.pos) ? page.pos : [];
    pageWidgets.forEach((widget, widgetIndex) => {
      const mapped = mapTemplateWidgetToApiWidget(
        widget,
        pageNumber,
        `${String(widget?.type || "widget").trim() || "widget"}-${pageNumber}-${widgetIndex + 1}`,
        widgetIndex
      );
      if (mapped) {
        widgets.push(mapped);
      }
    });
  });
  return widgets;
};

const extractIdentityConfigFromEntity = (entity) => {
  const identityObject =
    (entity?.IdentityVerification && typeof entity.IdentityVerification === "object"
      ? entity.IdentityVerification
      : null) ||
    (entity?.identityVerification && typeof entity.identityVerification === "object"
      ? entity.identityVerification
      : null) ||
    null;

  const requiresFlag = normalizeBooleanOrUndefined(
    entity?.RequiresIdentityVerification ??
      entity?.requiresIdentityVerification ??
      entity?.requireBiometric ??
      identityObject?.enabled ??
      identityObject?.required
  );

  if (identityObject) {
    return {
      identityVerification: safeClone(identityObject, {}),
      requiresIdentityVerification: requiresFlag
    };
  }

  if (requiresFlag !== undefined) {
    return {
      identityVerification: null,
      requiresIdentityVerification: requiresFlag
    };
  }

  return {
    identityVerification: null,
    requiresIdentityVerification: undefined
  };
};

const pickIdentityConfig = (entries = []) => {
  let fallbackFlag;
  for (const entry of entries) {
    if (!entry || typeof entry !== "object") {
      continue;
    }
    if (entry.identityVerification && typeof entry.identityVerification === "object") {
      return {
        identityVerification: safeClone(entry.identityVerification, {}),
        requiresIdentityVerification:
          entry.requiresIdentityVerification !== undefined ? entry.requiresIdentityVerification : true
      };
    }
    if (entry.requiresIdentityVerification !== undefined && fallbackFlag === undefined) {
      fallbackFlag = entry.requiresIdentityVerification;
    }
  }
  return {
    identityVerification: null,
    requiresIdentityVerification: fallbackFlag
  };
};

const buildCreateDocumentPayloadFromTemplate = (template, fileBase64) => {
  const templatePlaceholders = Array.isArray(template?.Placeholders) ? template.Placeholders : [];
  const templateSigners = Array.isArray(template?.Signers) ? template.Signers : [];
  const templateBcc = Array.isArray(template?.Bcc) ? template.Bcc : [];

  const signerMap = new Map(
    templateSigners
      .map((signer) => {
        const objectId = String(signer?.objectId || signer?.id || "").trim();
        return objectId ? [objectId, signer] : null;
      })
      .filter(Boolean)
  );

  const interactiveGroups = templatePlaceholders.filter(
    (group) => normalizeRoleKey(group?.Role) !== "prefill"
  );
  const prefillGroup =
    templatePlaceholders.find((group) => normalizeRoleKey(group?.Role) === "prefill") || null;

  const placeholders = interactiveGroups.map((group, index) => {
    const role = String(group?.Role || `Role ${index + 1}`).trim() || `Role ${index + 1}`;
    const widgets = extractApiWidgetsFromTemplateGroup(group);
    const resolvedWidgets = widgets.length > 0 ? widgets : [buildSampleWidgetForRole(role, index)];
    const mergedIdentity = pickIdentityConfig([
      extractIdentityConfigFromEntity(group),
      ...resolvedWidgets.map((widget) => extractIdentityConfigFromEntity(widget))
    ]);
    const placeholder = {
      role,
      color: String(group?.blockColor || "").trim() || undefined,
      widgets: resolvedWidgets
    };
    if (mergedIdentity.identityVerification) {
      placeholder.identityVerification = mergedIdentity.identityVerification;
    } else if (mergedIdentity.requiresIdentityVerification !== undefined) {
      placeholder.requiresIdentityVerification = mergedIdentity.requiresIdentityVerification;
    }
    if (!placeholder.color) {
      delete placeholder.color;
    }
    return placeholder;
  });
  const normalizedPlaceholders =
    placeholders.length > 0
      ? placeholders
      : [
          {
            role: "Role 1",
            color: "#93a3db",
            widgets: [buildSampleWidgetForRole("Role 1", 0)]
          }
        ];

  const signers = interactiveGroups.map((group, index) => {
    const role = String(group?.Role || `Role ${index + 1}`).trim() || `Role ${index + 1}`;
    const widgets = extractApiWidgetsFromTemplateGroup(group);
    const resolvedWidgets = widgets.length > 0 ? widgets : [buildSampleWidgetForRole(role, index)];
    const signerObjId = String(group?.signerObjId || group?.signerPtr?.objectId || "").trim();
    const signer =
      (group?.signerPtr && typeof group.signerPtr === "object" ? group.signerPtr : null) ||
      signerMap.get(signerObjId) ||
      null;
    const email = String(group?.email || signer?.Email || signer?.email || "")
      .trim()
      .toLowerCase();
    const name = String(signer?.Name || signer?.name || group?.Role || `Signer ${index + 1}`).trim();
    const phone = String(signer?.Phone || signer?.phone || "").trim();
    const fallbackEmail = `signer${index + 1}@example.com`;

    const signerPayload = {
      role,
      email: email || fallbackEmail
    };
    signerPayload.name = name || `Signer ${index + 1}`;
    if (phone) {
      signerPayload.phone = phone;
    } else {
      signerPayload.phone = "+10000000000";
    }

    const mergedIdentity = pickIdentityConfig([
      extractIdentityConfigFromEntity(group),
      ...resolvedWidgets.map((widget) => extractIdentityConfigFromEntity(widget))
    ]);
    if (mergedIdentity.identityVerification) {
      signerPayload.identityVerification = mergedIdentity.identityVerification;
    } else if (mergedIdentity.requiresIdentityVerification !== undefined) {
      signerPayload.requiresIdentityVerification = mergedIdentity.requiresIdentityVerification;
    }

    return signerPayload;
  });
  const normalizedSigners =
    signers.length > 0
      ? signers
      : [
          {
            role: normalizedPlaceholders[0]?.role || "Role 1",
            name: "Signer 1",
            email: "signer1@example.com",
            phone: "+10000000000"
          }
        ];

  const prefill = prefillGroup ? extractApiWidgetsFromTemplateGroup(prefillGroup) : [];

  const settings = {
    sendInOrder: Boolean(template?.SendinOrder),
    automaticReminders: Boolean(template?.AutomaticReminders),
    remindOnceInEvery: Number.parseInt(template?.RemindOnceInEvery, 10) || 5,
    timeToCompleteDays: Number.parseInt(template?.TimeToCompleteDays, 10) || 15,
    isEnableOTP: Boolean(template?.IsEnableOTP),
    allowModifications: Boolean(template?.AllowModifications),
    notifyOnSignatures: Boolean(template?.NotifyOnSignatures),
    isTourEnabled: Boolean(template?.IsTourEnabled)
  };
  if (template?.RedirectUrl) {
    settings.redirectUrl = String(template.RedirectUrl).trim();
  }
  if (template?.RequestSubject) {
    settings.requestSubject = String(template.RequestSubject);
  }
  if (template?.RequestBody) {
    settings.requestBody = String(template.RequestBody);
  }
  if (Array.isArray(template?.SignatureType) && template.SignatureType.length > 0) {
    settings.signatureType = safeClone(template.SignatureType, []);
  }

  const bcc = templateBcc
    .map((contact) => {
      const email = String(contact?.Email || contact?.email || "")
        .trim()
        .toLowerCase();
      const name = String(contact?.Name || contact?.name || "").trim();
      const phone = String(contact?.Phone || contact?.phone || "").trim();
      if (!email) {
        return null;
      }
      return {
        ...(name ? { name } : {}),
        email,
        ...(phone ? { phone } : {})
      };
    })
    .filter(Boolean);
  settings.bcc = bcc;

  const templateIdentity = extractIdentityConfigFromEntity(template);
  const templateMetadata = template?.Metadata && typeof template.Metadata === "object"
    ? safeClone(template.Metadata, {})
    : {};
  const metadata = {
    ...(templateMetadata && typeof templateMetadata === "object" ? templateMetadata : {})
  };
  if (!String(metadata.externalId || "").trim()) {
    metadata.externalId = `external-${String(template?.objectId || "template").trim() || "template"}-001`;
  }
  if (!Array.isArray(metadata.tags) || metadata.tags.length === 0) {
    metadata.tags = ["template", "api"];
  }

  const fallbackTitle = String(template?.Name || template?.Title || "").trim() || "Template document";
  const fallbackFilename = `${sanitizeJsonFileName(fallbackTitle, "document")}.pdf`;

  const payload = {
    title: fallbackTitle,
    filename: String(template?.FileName || "").trim() || fallbackFilename,
    file: fileBase64,
    signers: normalizedSigners,
    placeholders: normalizedPlaceholders,
    prefill,
    defaults: [],
    settings,
    metadata,
    note: String(template?.Note || "").trim() || "Ejemplo: nota del documento a firmar.",
    description:
      String(template?.Description || "").trim() ||
      "Ejemplo: descripción del documento generado desde plantilla."
  };

  if (templateIdentity.identityVerification) {
    payload.identityVerification = templateIdentity.identityVerification;
  }
  if (templateIdentity.requiresIdentityVerification !== undefined) {
    payload.requiresIdentityVerification = templateIdentity.requiresIdentityVerification;
  } else if (templateIdentity.identityVerification) {
    payload.requiresIdentityVerification = true;
  }

  return payload;
};

const buildTemplateApiPayload = (template) => {
  const templatePlaceholders = Array.isArray(template?.Placeholders) ? template.Placeholders : [];
  const templateSigners = Array.isArray(template?.Signers) ? template.Signers : [];
  const signerMap = new Map(
    templateSigners
      .map((signer) => {
        const objectId = String(signer?.objectId || signer?.id || "").trim();
        return objectId ? [objectId, signer] : null;
      })
      .filter(Boolean)
  );

  // For New UI format, extract unique roles from widget recipientName
  const isNewUIApiFormat = templatePlaceholders.length > 0 &&
    templatePlaceholders.every(entry => entry?.pageNumber !== undefined && Array.isArray(entry?.pos)) &&
    !templatePlaceholders.some(entry => entry?.Role);

  let interactiveGroups;
  if (isNewUIApiFormat) {
    const roleMap = new Map();
    templatePlaceholders.forEach(page => {
      (page.pos || []).forEach(widget => {
        const isPrefill = widget?.isPrefill || widget?.options?.isPrefill;
        if (isPrefill) return;
        const roleName = widget?.options?.recipientName || widget?.options?.recipientId || "Firmante";
        if (!roleMap.has(roleName)) {
          roleMap.set(roleName, { Role: roleName, pos: [] });
        }
        roleMap.get(roleName).pos.push(widget);
      });
    });
    interactiveGroups = Array.from(roleMap.values());
  } else {
    interactiveGroups = templatePlaceholders.filter(
      (group) => normalizeRoleKey(group?.Role) !== "prefill"
    );
  }

  // Build signers
  const signers = interactiveGroups.map((group, index) => {
    const role = String(group?.Role || `Role ${index + 1}`).trim() || `Role ${index + 1}`;
    const signerObjId = String(group?.signerObjId || group?.signerPtr?.objectId || "").trim();
    const signer =
      (group?.signerPtr && typeof group.signerPtr === "object" ? group.signerPtr : null) ||
      signerMap.get(signerObjId) ||
      null;
    const name = String(signer?.Name || signer?.name || group?.Role || `Signer ${index + 1}`).trim();
    const widgets = extractApiWidgetsFromTemplateGroup(group);
    const mergedIdentity = pickIdentityConfig([
      extractIdentityConfigFromEntity(group),
      ...widgets.map((w) => extractIdentityConfigFromEntity(w))
    ]);
    const signerPayload = {
      role,
      name: "$s{name}",
      email: "$s{email}"
    };
    if (mergedIdentity.identityVerification) {
      signerPayload.identityVerification = mergedIdentity.identityVerification;
    } else if (mergedIdentity.requiresIdentityVerification !== undefined) {
      signerPayload.requiresIdentityVerification = mergedIdentity.requiresIdentityVerification;
    }
    return signerPayload;
  });

  // Build defaults from ALL interactive widgets (auto-generate name if missing)
  const defaults = [];
  let widgetCounter = 0;
  templatePlaceholders.forEach((group) => {
    if (normalizeRoleKey(group?.Role) === "prefill") return;
    const pages = Array.isArray(group?.placeHolder) ? group.placeHolder : Array.isArray(group?.pos) ? [group] : [];
    pages.forEach((page) => {
      const pageNum = page?.pageNumber || 1;
      (page?.pos || []).forEach((widget) => {
        widgetCounter++;
        const widgetType = String(widget?.type || "text").trim().toLowerCase();
        if (widgetType === "signature") return; // signatures can't be prefilled via defaults
        const apiName = widget?.options?.name || widget?.key || `${widgetType}_${pageNum}_${widgetCounter}`;
        const placeholderText = widget?.placeholderText || "";
        const currentValue = String(widget?.options?.response || widget?.options?.defaultValue || "").trim();
        defaults.push({
          name: apiName,
          default: currentValue,
          _hint: placeholderText || `${widgetType} field`
        });
      });
    });
  });

  // Settings
  const settings = {
    sendInOrder: Boolean(template?.SendinOrder),
    automaticReminders: Boolean(template?.AutomaticReminders),
    remindOnceInEvery: Number.parseInt(template?.RemindOnceInEvery, 10) || 5,
    timeToCompleteDays: Number.parseInt(template?.TimeToCompleteDays, 10) || 15,
    isEnableOTP: Boolean(template?.IsEnableOTP),
    allowModifications: Boolean(template?.AllowModifications),
    notifyOnSignatures: Boolean(template?.NotifyOnSignatures),
    isTourEnabled: Boolean(template?.IsTourEnabled),
    bcc: []
  };
  if (template?.RedirectUrl) {
    settings.redirectUrl = String(template.RedirectUrl).trim();
  }

  return {
    signers: signers.length ? signers : [{ role: "Firmante", name: "$s{name}", email: "$s{email}" }],
    defaults,
    settings,
    metadata: {
      externalId: `external-${template?.objectId || "template"}-001`,
      tags: ["template", "api"]
    },
    note: String(template?.Note || "").trim() || "",
    description: String(template?.Description || "").trim() || ""
  };
};

const getDocTitle = (doc, index, t) =>
  doc?.Name ||
  doc?.Title ||
  doc?.FileName ||
  t("new-ui.dashboard.default-document-title", {
    index: index + 1,
    defaultValue: `Document ${index + 1}`
  });

const getOwnerName = (record, t) =>
  record?.ExtUserPtr?.Name ||
  record?.ExtUserPtr?.Email ||
  record?.CreatedBy?.name ||
  record?.CreatedBy?.Name ||
  record?.Owner?.name ||
  record?.Owner?.Name ||
  t("new-ui.dashboard.no-data", { defaultValue: "No data" });

const getDeclinedByName = (record) =>
  String(
    record?.DeclineBy?.Name ||
      record?.DeclineBy?.name ||
      record?.DeclineBy?.username ||
      record?.DeclineBy?.Username ||
      record?.DeclineBy?.Email ||
      record?.DeclineBy?.email ||
      ""
  ).trim();

const getSignerNames = (record, t) => {
  if (!Array.isArray(record?.Signers)) {
    return [];
  }

  return record.Signers.map((signer, index) =>
    signer?.Name ||
    signer?.Email ||
    signer?.name ||
    signer?.email ||
    t("new-ui.dashboard.default-signer", { index: index + 1, defaultValue: `Signer ${index + 1}` })
  );
};

const normalizeEmailAddress = (value) => String(value || "").trim().toLowerCase();
const normalizeSignOrderFlag = (value) => {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "number") {
    return value === 1;
  }
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "true" || normalized === "1" || normalized === "yes";
  }
  return false;
};

const getSignerIdentityId = (signer) =>
  String(
    signer?.objectId ||
      signer?.UserId?.objectId ||
      signer?.userId?.objectId ||
      signer?.UserPtr?.objectId ||
      signer?.Id ||
      ""
  ).trim();

const getSignerIdentityEmail = (signer) =>
  normalizeEmailAddress(
    signer?.Email || signer?.email || signer?.UserId?.Email || signer?.UserPtr?.Email || signer?.User?.Email || ""
  );

const getAuditSignerIdentityId = (entry) =>
  String(
    entry?.UserPtr?.UserId?.objectId ||
      entry?.UserPtr?.objectId ||
      entry?.SignerPtr?.objectId ||
      entry?.ContactPtr?.objectId ||
      ""
  ).trim();

const getAuditSignerIdentityEmail = (entry) =>
  normalizeEmailAddress(
    entry?.UserPtr?.Email || entry?.Email || entry?.email || entry?.SignerPtr?.Email || entry?.ContactPtr?.Email || ""
  );

const getMatchedSignerAuditEntries = (signer, auditTrail = []) => {
  const signerId = getSignerIdentityId(signer);
  const signerEmail = getSignerIdentityEmail(signer);
  return auditTrail.filter((entry) => {
    const auditId = getAuditSignerIdentityId(entry);
    const auditEmail = getAuditSignerIdentityEmail(entry);
    if (signerId && auditId && signerId === auditId) {
      return true;
    }
    if (signerEmail && auditEmail && signerEmail === auditEmail) {
      return true;
    }
    return false;
  });
};

const getSignerAuditStatus = (signer, auditTrail = [], options = {}) => {
  const fallbackStatus =
    String(options?.fallbackStatus || "NOT_SIGNED")
      .trim()
      .toUpperCase() || "NOT_SIGNED";
  const entries = getMatchedSignerAuditEntries(signer, auditTrail);
  if (!entries.length) {
    return fallbackStatus;
  }
  if (entries.some((entry) => String(entry?.Activity || "").trim().toUpperCase() === "SIGNED")) {
    return "SIGNED";
  }
  if (entries.some((entry) => String(entry?.Activity || "").trim().toUpperCase() === "DECLINED")) {
    return "DECLINED";
  }
  if (entries.some((entry) => String(entry?.Activity || "").trim().toUpperCase() === "VIEWED")) {
    return "VIEWED";
  }
  return fallbackStatus;
};

const hasSignerSignedAudit = (signer, auditTrail = []) => {
  return getMatchedSignerAuditEntries(signer, auditTrail).some(
    (entry) => String(entry?.Activity || "").trim().toUpperCase() === "SIGNED"
  );
};

const isSignerMarkedSigned = (signer) => {
  if (!signer || typeof signer !== "object") {
    return false;
  }
  const normalizedStatus = String(
    signer?.Status || signer?.status || signer?.Activity || signer?.activity || ""
  )
    .trim()
    .toUpperCase();
  if (["SIGNED", "COMPLETED", "DONE"].includes(normalizedStatus)) {
    return true;
  }
  return (
    normalizeBooleanFlag(signer?.IsSigned) ||
    normalizeBooleanFlag(signer?.isSigned) ||
    Boolean(signer?.SignedOn || signer?.signedOn || signer?.signedAt)
  );
};

const hasSignatureWidgetResponse = (widget) => {
  if (!widget || typeof widget !== "object") {
    return false;
  }
  const widgetType = String(widget?.type || "").trim().toLowerCase();
  if (!["signature", "initials", "stamp", "image"].includes(widgetType)) {
    return false;
  }
  const response =
    widget?.options?.response ??
    widget?.response ??
    widget?.value ??
    widget?.options?.value ??
    null;
  if (typeof response === "string") {
    return Boolean(response.trim());
  }
  if (Array.isArray(response)) {
    return response.length > 0;
  }
  return Boolean(response);
};

const hasSignerSignedWidgets = (record, signer) => {
  const placeholders = [
    ...(Array.isArray(record?.Placeholders) ? record.Placeholders : []),
    ...(Array.isArray(record?.placeHolder) ? record.placeHolder : [])
  ];
  return placeholders.some((placeholder) => {
    if (!isSameSignerIdentity(signer, placeholder)) {
      return false;
    }
    const widgets = getPlaceholderWidgets(placeholder);
    return widgets.some((widget) => hasSignatureWidgetResponse(widget));
  });
};

const hasSignerCompletedSigning = (record, signer, auditTrail = []) => {
  return (
    hasSignerSignedAudit(signer, auditTrail) ||
    isSignerMarkedSigned(signer)
  );
};

const resolveCurrentSignerForUser = (record, currentUserId = "", currentUserEmail = "") => {
  const signers = Array.isArray(record?.Signers) ? record.Signers : [];
  const fromSigners =
    signers.find((signer) => {
      const signerId = getSignerIdentityId(signer);
      const signerEmail = getSignerIdentityEmail(signer);
      return (
        (currentUserId && signerId && signerId === currentUserId) ||
        (currentUserEmail && signerEmail && signerEmail === currentUserEmail)
      );
    }) || null;
  if (fromSigners) {
    return fromSigners;
  }
  const placeholders = [
    ...(Array.isArray(record?.Placeholders) ? record.Placeholders : []),
    ...(Array.isArray(record?.placeHolder) ? record.placeHolder : [])
  ];
  return (
    placeholders.find((placeholder) => {
      const signerId = getSignerIdentityId(placeholder);
      const signerEmail = getSignerIdentityEmail(placeholder);
      return (
        (currentUserId && signerId && signerId === currentUserId) ||
        (currentUserEmail && signerEmail && signerEmail === currentUserEmail)
      );
    }) || null
  );
};

const getNeedYourSignAccessState = (record, currentUserId = "", currentUserEmail = "") => {
  const signers = Array.isArray(record?.Signers) ? record.Signers : [];
  const currentSigner = resolveCurrentSignerForUser(record, currentUserId, currentUserEmail);
  if (!currentSigner) {
    return {
      isSigner: false,
      alreadySigned: false,
      waitingTurn: false,
      canSign: false
    };
  }
  const auditTrail = Array.isArray(record?.AuditTrail) ? record.AuditTrail : [];
  if (hasSignerCompletedSigning(record, currentSigner, auditTrail)) {
    return {
      isSigner: true,
      alreadySigned: true,
      waitingTurn: false,
      canSign: false
    };
  }
  const isSendInOrder = normalizeSignOrderFlag(record?.SendinOrder);
  if (!isSendInOrder) {
    return {
      isSigner: true,
      alreadySigned: false,
      waitingTurn: false,
      canSign: true
    };
  }
  const nextPendingSigner =
    signers.find((signer) => !hasSignerCompletedSigning(record, signer, auditTrail)) || null;
  if (!nextPendingSigner) {
    return {
      isSigner: true,
      alreadySigned: true,
      waitingTurn: false,
      canSign: false
    };
  }
  const currentSignerId = getSignerIdentityId(currentSigner);
  const currentSignerEmail = getSignerIdentityEmail(currentSigner);
  const nextSignerId = getSignerIdentityId(nextPendingSigner);
  const nextSignerEmail = getSignerIdentityEmail(nextPendingSigner);
  const isNextSigner =
    (currentSignerId && nextSignerId && currentSignerId === nextSignerId) ||
    (currentSignerEmail && nextSignerEmail && currentSignerEmail === nextSignerEmail);
  return {
    isSigner: true,
    alreadySigned: false,
    waitingTurn: !isNextSigner,
    canSign: isNextSigner
  };
};

const isCurrentUserNextSigner = (record, currentUserId = "", currentUserEmail = "") => {
  return getNeedYourSignAccessState(record, currentUserId, currentUserEmail).canSign;
};

const normalizeBooleanFlag = (value) => {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "number") {
    return value === 1;
  }
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return ["1", "true", "yes", "y", "required", "enabled"].includes(normalized);
  }
  return false;
};

const isIdentityRequirementEnabled = (value) => {
  if (!value) {
    return false;
  }
  if (typeof value === "object") {
    return normalizeBooleanFlag(value?.enabled) || normalizeBooleanFlag(value?.required);
  }
  return normalizeBooleanFlag(value);
};

const getPlaceholderWidgets = (placeholder) => {
  if (!placeholder || typeof placeholder !== "object") {
    return [];
  }
  if (Array.isArray(placeholder?.pos)) {
    return placeholder.pos;
  }
  if (Array.isArray(placeholder?.placeHolder)) {
    return placeholder.placeHolder;
  }
  if (Array.isArray(placeholder?.placeholders)) {
    return placeholder.placeholders;
  }
  return [];
};

const hasIdentityVerificationInWidget = (widget) => {
  if (!widget || typeof widget !== "object") {
    return false;
  }

  if (
    isIdentityRequirementEnabled(widget?.IdentityVerification) ||
    isIdentityRequirementEnabled(widget?.identityVerification) ||
    normalizeBooleanFlag(widget?.RequiresIdentityVerification) ||
    normalizeBooleanFlag(widget?.requiresIdentityVerification) ||
    normalizeBooleanFlag(widget?.requireBiometric) ||
    isIdentityRequirementEnabled(widget?.verificationSettings)
  ) {
    return true;
  }

  const options = widget?.options;
  if (!options || typeof options !== "object") {
    return false;
  }

  return (
    isIdentityRequirementEnabled(options?.IdentityVerification) ||
    isIdentityRequirementEnabled(options?.identityVerification) ||
    normalizeBooleanFlag(options?.RequiresIdentityVerification) ||
    normalizeBooleanFlag(options?.requiresIdentityVerification) ||
    normalizeBooleanFlag(options?.requireBiometric) ||
    isIdentityRequirementEnabled(options?.verificationSettings)
  );
};

const hasIdentityVerificationOnSignerOrPlaceholder = (entry) => {
  if (!entry || typeof entry !== "object") {
    return false;
  }

  if (
    isIdentityRequirementEnabled(entry?.IdentityVerification) ||
    isIdentityRequirementEnabled(entry?.identityVerification) ||
    normalizeBooleanFlag(entry?.RequiresIdentityVerification) ||
    normalizeBooleanFlag(entry?.requiresIdentityVerification) ||
    normalizeBooleanFlag(entry?.requireBiometric) ||
    isIdentityRequirementEnabled(entry?.verificationSettings)
  ) {
    return true;
  }

  const widgets = getPlaceholderWidgets(entry);
  return widgets.some((widget) => hasIdentityVerificationInWidget(widget));
};

const isIdentityVerificationRequestedForDocument = (record) => {
  if (!record || typeof record !== "object") {
    return false;
  }

  if (
    isIdentityRequirementEnabled(record?.IdentityVerification) ||
    isIdentityRequirementEnabled(record?.identityVerification) ||
    normalizeBooleanFlag(record?.RequiresIdentityVerification) ||
    normalizeBooleanFlag(record?.requiresIdentityVerification) ||
    normalizeBooleanFlag(record?.requireBiometric) ||
    isIdentityRequirementEnabled(record?.verificationSettings)
  ) {
    return true;
  }

  const placeholders = [
    ...(Array.isArray(record?.Placeholders) ? record.Placeholders : []),
    ...(Array.isArray(record?.placeHolder) ? record.placeHolder : [])
  ];
  if (placeholders.some((placeholder) => hasIdentityVerificationOnSignerOrPlaceholder(placeholder))) {
    return true;
  }

  const signers = Array.isArray(record?.Signers) ? record.Signers : [];
  return signers.some((signer) => hasIdentityVerificationOnSignerOrPlaceholder(signer));
};

const getSignerIdentityCandidateId = (entry) =>
  String(
    entry?.signerObjId ||
      entry?.signerPtr?.objectId ||
      entry?.objectId ||
      entry?.UserId?.objectId ||
      entry?.userId?.objectId ||
      entry?.UserPtr?.objectId ||
      entry?.SignerPtr?.objectId ||
      entry?.ContactPtr?.objectId ||
      ""
  ).trim();

const getSignerIdentityCandidateEmail = (entry) =>
  normalizeEmailAddress(
    entry?.Email ||
      entry?.email ||
      entry?.signerPtr?.Email ||
      entry?.UserId?.Email ||
      entry?.UserPtr?.Email ||
      entry?.SignerPtr?.Email ||
      entry?.ContactPtr?.Email ||
      ""
  );

const isSameSignerIdentity = (signerIdentity, entry) => {
  const signerId = getSignerIdentityId(signerIdentity);
  const signerEmail = getSignerIdentityEmail(signerIdentity);
  const candidateId = getSignerIdentityCandidateId(entry);
  const candidateEmail = getSignerIdentityCandidateEmail(entry);

  if (signerId && candidateId && signerId === candidateId) {
    return true;
  }
  if (signerEmail && candidateEmail && signerEmail === candidateEmail) {
    return true;
  }
  return false;
};

const isIdentityVerificationRequiredForSigner = (record, signerIdentity) => {
  if (!record || !signerIdentity) {
    return false;
  }

  if (
    isIdentityRequirementEnabled(record?.IdentityVerification) ||
    isIdentityRequirementEnabled(record?.identityVerification) ||
    normalizeBooleanFlag(record?.RequiresIdentityVerification) ||
    normalizeBooleanFlag(record?.requiresIdentityVerification) ||
    normalizeBooleanFlag(record?.requireBiometric) ||
    isIdentityRequirementEnabled(record?.verificationSettings)
  ) {
    return true;
  }

  const signers = Array.isArray(record?.Signers) ? record.Signers : [];
  if (
    signers.some(
      (signer) =>
        isSameSignerIdentity(signerIdentity, signer) && hasIdentityVerificationOnSignerOrPlaceholder(signer)
    )
  ) {
    return true;
  }

  const placeholders = [
    ...(Array.isArray(record?.Placeholders) ? record.Placeholders : []),
    ...(Array.isArray(record?.placeHolder) ? record.placeHolder : [])
  ];
  return placeholders.some(
    (placeholder) =>
      isSameSignerIdentity(signerIdentity, placeholder) && hasIdentityVerificationOnSignerOrPlaceholder(placeholder)
  );
};

const isNeedYourSignRecordForCurrentUser = (record, currentUserId, currentUserEmail = "") => {
  if (!currentUserId && !currentUserEmail) {
    return true;
  }
  const accessState = getNeedYourSignAccessState(record, currentUserId, currentUserEmail);
  return accessState.isSigner && !accessState.alreadySigned;
};

const getDocumentStatus = (record, t) => {
  const expiryDate = parseDocDate(record?.ExpiryDate);
  if (expiryDate && expiryDate.getTime() < Date.now()) {
    return {
      key: "expired",
      label: t("sidebar.Documents-Children.Expired", { defaultValue: "Expired" }),
      toneClass: "bg-red-100 text-red-700 border-red-200"
    };
  }
  if (record?.IsDeclined) {
    return {
      key: "declined",
      label: t("sidebar.Documents-Children.Declined", { defaultValue: "Declined" }),
      toneClass: "bg-red-100 text-red-700 border-red-200"
    };
  }
  if (record?.IsCompleted) {
    return {
      key: "completed",
      label: t("sidebar.Documents-Children.Completed", { defaultValue: "Completed" }),
      toneClass: "bg-emerald-100 text-emerald-700 border-emerald-200"
    };
  }
  if (record?.IsDraft || record?.Draft) {
    return {
      key: "draft",
      label: t("sidebar.Documents-Children.Drafts", { defaultValue: "Drafts" }),
      toneClass: "bg-slate-100 text-slate-700 border-slate-200"
    };
  }
  return {
    key: "in-progress",
    label: t("sidebar.Documents-Children.In Progress", { defaultValue: "In progress" }),
    toneClass: "bg-indigo-100 text-indigo-700 border-indigo-200"
  };
};

const resolveDocumentOpenPath = (record) => {
  const docId = String(record?.objectId || "").trim();
  if (!docId) {
    return "";
  }
  const signerExist = record?.Signers;
  const isDeclined = record?.IsDeclined;
  const isPlaceholder = record?.Placeholders;
  const signedUrl = record?.SignedUrl;
  const isSignYourself = record?.IsSignyourself;

  // Keep completed request-sign documents in non-editable recipient flow (legacy behavior).
  if (record?.IsCompleted && signerExist?.length > 0) {
    return `/pdfRequestFiles/${docId}`;
  }
  // For completed self-sign documents, open the finalized summary directly (read-only).
  if (record?.IsCompleted) {
    return `/signaturePdf/${docId}/completed`;
  }
  if ((!signerExist && !isPlaceholder) || isSignYourself) {
    return `/signaturePdf/${docId}`;
  }
  if (isDeclined) {
    return `/pdfRequestFiles/${docId}`;
  }
  if (signerExist?.length > 0 && isPlaceholder?.length > 0 && !signedUrl) {
    return `/placeHolderSign/${docId}`;
  }
  if (isPlaceholder?.length > 0 && signedUrl) {
    return `/pdfRequestFiles/${docId}`;
  }
  if (
    (signerExist?.length > 0 && (!isPlaceholder || isPlaceholder?.length === 0)) ||
    ((!signerExist || signerExist?.length === 0) && isPlaceholder?.length > 0)
  ) {
    return `/placeHolderSign/${docId}`;
  }
  return `/pdfRequestFiles/${docId}`;
};

const getDataMode = (sectionKey, reportId) => {
  if (sectionKey === "report" && reportId === CONTACTS_REPORT_ID) {
    return "contacts";
  }
  if (sectionKey === "report" && reportId === TEMPLATE_REPORT_ID) {
    return "templates";
  }
  if (sectionKey === "template") {
    return "templates";
  }
  if (sectionKey === "report" && reportId) {
    return "documents";
  }
  if (sectionKey === "drive") {
    return "drive";
  }
  if (sectionKey === "form") {
    return "drafts";
  }
  return "none";
};

const getHistoryReportConfig = (reportId) => {
  switch (String(reportId || "")) {
    case NEED_YOUR_SIGN_REPORT_ID:
      return {
        columns: ["title", "expiry", "owner", "signers"],
        actionLabelKey: "btnLabel.Sign",
        actionLabelFallback: "Sign"
      };
    case IN_PROGRESS_REPORT_ID:
      return {
        columns: ["title", "expiry", "signersStatus"],
        actionLabelKey: "btnLabel.View",
        actionLabelFallback: "View"
      };
    case COMPLETED_REPORT_ID:
      return {
        columns: ["title", "owner", "signers"],
        actionLabelKey: "btnLabel.View",
        actionLabelFallback: "View"
      };
    case DECLINED_REPORT_ID:
      return {
        columns: ["title", "reason", "folder", "owner", "signers"],
        actionLabelKey: "btnLabel.View",
        actionLabelFallback: "View"
      };
    case EXPIRED_REPORT_ID:
      return {
        columns: ["title", "note", "folder", "file", "owner", "signers"],
        actionLabelKey: "btnLabel.View",
        actionLabelFallback: "View"
      };
    case DRAFTS_REPORT_ID:
      return {
        columns: ["title", "note", "folder", "file", "owner", "signers"],
        actionLabelKey: "btnLabel.Edit",
        actionLabelFallback: "Edit"
      };
    default:
      return {
        columns: ["title", "status", "activity", "owner"],
        actionLabelKey: "btnLabel.Open",
        actionLabelFallback: "Open"
      };
  }
};

const sectionDefinitions = {
  form: {
    titleKey: "new-ui.sections.form.title",
    titleFallback: "Signing workflows",
    descriptionKey: "new-ui.sections.form.description",
    descriptionFallback:
      "Design and execute workflows for self-signing, signature requests, and template usage.",
    icon: "signature",
    links: [
      {
        labelKey: "sidebar.Sign yourself",
        fallbackLabel: "Sign yourself",
        to: "/form/sHAnZphf69"
      },
      {
        labelKey: "sidebar.Request signatures",
        fallbackLabel: "Request signatures",
        to: "/form/8mZzFxbG1z"
      },
      {
        labelKey: "sidebar.Templates-Children.Create template",
        fallbackLabel: "Create template",
        to: "/form/template"
      }
    ]
  },
  report: {
    titleKey: "new-ui.sections.report.title",
    titleFallback: "Reports center",
    descriptionKey: "new-ui.sections.report.description",
    descriptionFallback: "Review document states, contact reports, and operational traceability.",
    icon: "timeline",
    links: [
      {
        labelKey: "sidebar.Documents-Children.Need your sign",
        fallbackLabel: "Need your sign",
        to: "/report/4Hhwbp482K"
      },
      {
        labelKey: "sidebar.Documents-Children.In Progress",
        fallbackLabel: "In Progress",
        to: "/report/1MwEuxLEkF"
      },
      {
        labelKey: "sidebar.Documents-Children.Completed",
        fallbackLabel: "Completed",
        to: "/report/kQUoW4hUXz"
      },
      {
        labelKey: "sidebar.Documents-Children.Drafts",
        fallbackLabel: "Drafts",
        to: "/report/ByHuevtCFY"
      }
    ]
  },
  drive: {
    titleKey: "new-ui.sections.drive.title",
    titleFallback: "DANASign Drive",
    descriptionKey: "new-ui.sections.drive.description",
    descriptionFallback: "Manage your file repository and document structure.",
    icon: "folder",
    links: []
  },
  settings: {
    titleKey: "new-ui.sections.settings.title",
    titleFallback: "Settings",
    descriptionKey: "new-ui.sections.settings.description",
    descriptionFallback: "Manage signature settings, preferences, users, and API token.",
    icon: "settings",
    links: [
      {
        labelKey: "sidebar.Settings-Children.My Signature",
        fallbackLabel: "My signature",
        to: "/managesign"
      },
      {
        labelKey: "sidebar.Settings-Children.Preferences",
        fallbackLabel: "Preferences",
        to: "/preferences"
      },
      {
        labelKey: "sidebar.Settings-Children.Users",
        fallbackLabel: "Users",
        to: "/users"
      },
      {
        labelKey: "sidebar.Settings-Children.API Token",
        fallbackLabel: "API token",
        to: "/generatetoken"
      }
    ]
  },
  profile: {
    titleKey: "new-ui.sections.profile.title",
    titleFallback: "User profile",
    descriptionKey: "new-ui.sections.profile.description",
    descriptionFallback: "View account details and related access controls.",
    icon: "user",
    links: [
      {
        labelKey: "new-ui.sections.profile.change-password",
        fallbackLabel: "Change password",
        to: "/changepassword"
      },
      {
        labelKey: "new-ui.sections.profile.go-settings",
        fallbackLabel: "Go to settings",
        to: "/managesign"
      }
    ]
  },
  security: {
    titleKey: "new-ui.sections.security.title",
    titleFallback: "Account security",
    descriptionKey: "new-ui.sections.security.description",
    descriptionFallback: "Update credentials and review access controls.",
    icon: "key",
    links: [
      {
        labelKey: "new-ui.sections.security.back-profile",
        fallbackLabel: "Back to profile",
        to: "/profile"
      }
    ]
  },
  template: {
    titleKey: "new-ui.sections.template.title",
    titleFallback: "Template editor",
    descriptionKey: "new-ui.sections.template.description",
    descriptionFallback: "Prepare reusable templates for signature workflows.",
    icon: "templateDoc",
    links: [
      {
        labelKey: "sidebar.Templates-Children.Create template",
        fallbackLabel: "Create template",
        to: "/form/template"
      },
      {
        labelKey: "sidebar.Templates-Children.Manage templates",
        fallbackLabel: "Manage templates",
        to: "/report/6TeaPr321t"
      }
    ]
  },
  signaturePdf: {
    titleKey: "new-ui.sections.signature-pdf.title",
    titleFallback: "Individual signing",
    descriptionKey: "new-ui.sections.signature-pdf.description",
    descriptionFallback: "Run a signature flow on a single document.",
    icon: "signature",
    links: [
      {
        labelKey: "sidebar.Documents-Children.Need your sign",
        fallbackLabel: "Need your sign",
        to: "/report/4Hhwbp482K"
      },
      {
        labelKey: "sidebar.Documents-Children.In Progress",
        fallbackLabel: "In progress",
        to: "/report/1MwEuxLEkF"
      }
    ]
  },
  draftDocument: {
    titleKey: "new-ui.sections.drafts.title",
    titleFallback: "Drafts",
    descriptionKey: "new-ui.sections.drafts.description",
    descriptionFallback: "Control documents in preparation before sending.",
    icon: "layout",
    links: [
      {
        labelKey: "new-ui.sections.drafts.open",
        fallbackLabel: "Open drafts",
        to: "/report/ByHuevtCFY"
      }
    ]
  },
  placeHolderSign: {
    titleKey: "new-ui.sections.placeholder.title",
    titleFallback: "Placeholder sign",
    descriptionKey: "new-ui.sections.placeholder.description",
    descriptionFallback: "Configure placeholders and validations before signing.",
    icon: "flow",
    links: [
      {
        labelKey: "sidebar.Request signatures",
        fallbackLabel: "Request signatures",
        to: "/form/8mZzFxbG1z"
      }
    ]
  }
};

const routeToIcon = (path = "") => {
  if (path.startsWith("/form/template")) return "templateDoc";
  if (path.startsWith("/form")) return "signature";
  if (path.startsWith("/report")) return "timeline";
  if (path.startsWith("/drive")) return "folder";
  if (path.startsWith("/manage") || path.startsWith("/preferences") || path.startsWith("/generate")) return "settings";
  if (path.startsWith("/profile") || path.startsWith("/change")) return "user";
  if (path.startsWith("/users")) return "users";
  return "chevronRight";
};

const formatSectionWithParams = (section, params, t) => {
  if (section === "report" && params?.id) {
    const viewInfo = reportViewById[params.id];
    const viewName = viewInfo ? t(viewInfo.key, { defaultValue: viewInfo.defaultValue }) : `${params.id}`;
    return {
      title: `${t(sectionDefinitions.report.titleKey, {
        defaultValue: sectionDefinitions.report.titleFallback
      })}: ${viewName}`,
      description: t(sectionDefinitions.report.descriptionKey, {
        defaultValue: sectionDefinitions.report.descriptionFallback
      })
    };
  }

  if (section === "form" && params?.id) {
    const workflowName =
      params.id === "sHAnZphf69"
        ? t("sidebar.Sign yourself", { defaultValue: "Sign yourself" })
        : params.id === "8mZzFxbG1z"
          ? t("sidebar.Request signatures", { defaultValue: "Request signatures" })
          : params.id === "template"
            ? t("sidebar.Templates-Children.Create template", { defaultValue: "Create template" })
            : t("new-ui.sections.form.workflow-with-id", {
                id: params.id,
                defaultValue: `Workflow ${params.id}`
              });

    return {
      title: `${t(sectionDefinitions.form.titleKey, {
        defaultValue: sectionDefinitions.form.titleFallback
      })}: ${workflowName}`,
      description: t(sectionDefinitions.form.descriptionKey, {
        defaultValue: sectionDefinitions.form.descriptionFallback
      })
    };
  }

  const selectedSection = sectionDefinitions[section] || sectionDefinitions.form;
  return {
    title: t(selectedSection.titleKey, { defaultValue: selectedSection.titleFallback }),
    description: t(selectedSection.descriptionKey, { defaultValue: selectedSection.descriptionFallback })
  };
};

const flattenMenuLinks = (menuItems, t) =>
  menuItems.flatMap((item) =>
    item.children
      ? item.children.map((child) => ({ ...child, group: item.label }))
      : [
          {
            key: item.key,
            label: item.label,
            to: item.to,
            icon: item.icon,
            group: t("sidebar.General", { defaultValue: "General" })
          }
        ]
  );

function SectionWorkspacePage({ sectionKey = "form" }) {
  const { t, i18n } = useTranslation();
  const { showError, showSuccess } = useAlert();
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [routeQuery, setRouteQuery] = useState("");
  const [dataQuery, setDataQuery] = useState("");
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [dataError, setDataError] = useState("");
  const [rows, setRows] = useState([]);
  const [drivePath, setDrivePath] = useState([{ objectId: "", name: "DANASign Drive" }]);
  const [drivePage, setDrivePage] = useState(1);
  const [driveHasMore, setDriveHasMore] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyHasMore, setHistoryHasMore] = useState(false);
  const [driveTypeFilter, setDriveTypeFilter] = useState("all");
  const [driveStatusFilter, setDriveStatusFilter] = useState("all");
  const [driveOwnerFilter, setDriveOwnerFilter] = useState("all");
  const [driveDateSort, setDriveDateSort] = useState("newest");
  const [isCreateFolderModalOpen, setIsCreateFolderModalOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [newFolderError, setNewFolderError] = useState("");
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [contactModalMode, setContactModalMode] = useState("create");
  const [contactForm, setContactForm] = useState({ objectId: "", Name: "", Email: "", Phone: "" });
  const [contactFormError, setContactFormError] = useState("");
  const [isContactImportModalOpen, setIsContactImportModalOpen] = useState(false);
  const [contactImportRows, setContactImportRows] = useState([]);
  const [contactImportInvalidCount, setContactImportInvalidCount] = useState(0);
  const [contactImportPage, setContactImportPage] = useState(1);
  const [contactImportError, setContactImportError] = useState("");
  const [isParsingContactImport, setIsParsingContactImport] = useState(false);
  const [isImportingContacts, setIsImportingContacts] = useState(false);
  const [isSavingContact, setIsSavingContact] = useState(false);
  const [isDeleteContactModalOpen, setIsDeleteContactModalOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [isDeletingContact, setIsDeletingContact] = useState(false);
  const [isDeleteDraftsConfirmModalOpen, setIsDeleteDraftsConfirmModalOpen] = useState(false);
  const [dataNotice, setDataNotice] = useState("");
  const [dataRefreshToken, setDataRefreshToken] = useState(0);
  const [isCompletedSummaryModalOpen, setIsCompletedSummaryModalOpen] = useState(false);
  const [isLoadingCompletedSummary, setIsLoadingCompletedSummary] = useState(false);
  const [isGeneratingCompletedCertificate, setIsGeneratingCompletedCertificate] = useState(false);
  const [isDownloadingCompletedBundle, setIsDownloadingCompletedBundle] = useState(false);
  const [completedSummaryError, setCompletedSummaryError] = useState("");
  const [isSignersModalOpen, setIsSignersModalOpen] = useState(false);
  const [signersModalRows, setSignersModalRows] = useState([]);
  const [isNeedYourSignDetailsModalOpen, setIsNeedYourSignDetailsModalOpen] = useState(false);
  const [needYourSignDetailsRow, setNeedYourSignDetailsRow] = useState(null);
  const [signerStatusExpanded, setSignerStatusExpanded] = useState({});
  const [activeHistoryDownloadId, setActiveHistoryDownloadId] = useState("");
  const [activeInProgressMenuDocId, setActiveInProgressMenuDocId] = useState("");
  const [historyActionTargetRow, setHistoryActionTargetRow] = useState(null);
  const [isHistoryResendModalOpen, setIsHistoryResendModalOpen] = useState(false);
  const [historyResendRecipientEmail, setHistoryResendRecipientEmail] = useState("");
  const [historyResendSubject, setHistoryResendSubject] = useState("");
  const [historyResendBody, setHistoryResendBody] = useState("");
  const [isHistoryRenameModalOpen, setIsHistoryRenameModalOpen] = useState(false);
  const [historyRenameValue, setHistoryRenameValue] = useState("");
  const [isHistoryRevokeModalOpen, setIsHistoryRevokeModalOpen] = useState(false);
  const [historyRevokeReason, setHistoryRevokeReason] = useState("");
  const [isHistoryDeleteModalOpen, setIsHistoryDeleteModalOpen] = useState(false);
  const [isHistorySaveTemplateModalOpen, setIsHistorySaveTemplateModalOpen] = useState(false);
  const [isHistoryActionLoading, setIsHistoryActionLoading] = useState("");
  const [activeTemplateUseId, setActiveTemplateUseId] = useState("");
  const [activeTemplateEditId, setActiveTemplateEditId] = useState("");
  const [activeTemplateJsonId, setActiveTemplateJsonId] = useState("");
  const [isTemplateRenameModalOpen, setIsTemplateRenameModalOpen] = useState(false);
  const [templateRenameValue, setTemplateRenameValue] = useState("");
  const [templateRenameTargetRow, setTemplateRenameTargetRow] = useState(null);
  const [isTemplateRenameSaving, setIsTemplateRenameSaving] = useState(false);
  const [selectedDraftIds, setSelectedDraftIds] = useState([]);
  const [selectedExpiredIds, setSelectedExpiredIds] = useState([]);
  const [isDeletingSelectedDrafts, setIsDeletingSelectedDrafts] = useState(false);
  const [selectedTemplateIds, setSelectedTemplateIds] = useState([]);
  const [isDeleteTemplatesConfirmModalOpen, setIsDeleteTemplatesConfirmModalOpen] = useState(false);
  const [isDeletingSelectedTemplates, setIsDeletingSelectedTemplates] = useState(false);
  const [completedSummary, setCompletedSummary] = useState({
    docId: "",
    name: "",
    note: "",
    folder: "",
    signedUrl: "",
    certificateUrl: ""
  });

  const fetchReportRows = async (reportId, options = {}) => {
    const payload = {
      reportId,
      skip: Number.isFinite(options?.skip) ? options.skip : 0,
      limit: Number.isFinite(options?.limit) ? options.limit : 100
    };

    const baseUrl = getParseFunctionBaseUrl();
    if (baseUrl) {
      try {
        const response = await axios.post(`${baseUrl}functions/getReport`, payload, {
          headers: getParseFunctionHeaders()
        });
        const data = response?.data || {};
        const result = Object.prototype.hasOwnProperty.call(data, "result") ? data.result : data;
        const serverError = String(result?.error || data?.error || "").trim();
        if (serverError) {
          throw new Error(serverError);
        }
        return normalizeCloudResult(result);
      } catch (requestError) {
        // Fallback to Parse SDK if function endpoint request fails.
      }
    }

    const cloudResponse = await Parse.Cloud.run("getReport", payload);
    const cloudError = String(cloudResponse?.error || cloudResponse?.result?.error || "").trim();
    if (cloudError) {
      throw new Error(cloudError);
    }
    return normalizeCloudResult(cloudResponse);
  };

  const fetchInProgressFallbackRows = async ({ limit = 500 } = {}) => {
    const currentUser = Parse.User.current();
    const extClassRaw = parseStoredJson("Extand_Class");
    const extClassObjectId = String(
      Array.isArray(extClassRaw) ? extClassRaw?.[0]?.objectId || extClassRaw?.[0]?.id || "" : ""
    ).trim();
    const extUserDetails = await Parse.Cloud.run("getUserDetails").catch(() => null);
    const extUserObjectId = String(
      extUserDetails?.objectId || extUserDetails?.id || extClassObjectId || ""
    ).trim();
    const extUserPointer = extUserObjectId
      ? Parse.Object.extend("contracts_Users").createWithoutData(extUserObjectId)
      : null;

    if (!currentUser?.id && !extUserPointer) {
      return [];
    }

    const maxLimit = Math.max(1, Number(limit) || 500);
    const buildBaseQuery = () => {
      const query = new Parse.Query("contracts_Document");
      query.notEqualTo("Type", "Folder");
      query.notEqualTo("IsCompleted", true);
      query.notEqualTo("IsDeclined", true);
      query.notEqualTo("IsArchive", true);
      query.descending("updatedAt");
      query.limit(maxLimit);
      applyInProgressIncludes(query);
      return query;
    };

    const scopeQueries = [];
    if (currentUser?.id) {
      const byCreatedBy = buildBaseQuery();
      byCreatedBy.equalTo("CreatedBy", currentUser);
      scopeQueries.push(byCreatedBy);
    }
    if (extUserPointer) {
      const byExtUser = buildBaseQuery();
      byExtUser.equalTo("ExtUserPtr", extUserPointer);
      scopeQueries.push(byExtUser);
    }

    let records = [];
    if (!scopeQueries.length) {
      records = [];
    } else if (scopeQueries.length === 1) {
      records = await scopeQueries[0].find();
    } else {
      const orQuery = Parse.Query.or(...scopeQueries);
      orQuery.descending("updatedAt");
      orQuery.limit(maxLimit);
      applyInProgressIncludes(orQuery);
      records = await orQuery.find();
    }

    return records
      .map((item) => toPlainObject(item))
      .filter(Boolean)
      .filter((record) => isInProgressDocumentRecord(record))
      .sort((a, b) => {
        const dateA = parseDocDate(a?.updatedAt || a?.createdAt)?.getTime() || 0;
        const dateB = parseDocDate(b?.updatedAt || b?.createdAt)?.getTime() || 0;
        return dateB - dateA;
      });
  };

  const extClass = useMemo(() => parseStoredJson("Extand_Class"), []);
  const tenantId = useMemo(() => {
    const firstExt = Array.isArray(extClass) ? extClass?.[0] || {} : {};
    return String(firstExt?.TenantId?.objectId || localStorage.getItem("TenantId") || "").trim();
  }, [extClass]);

  const role = useMemo(() => {
    const extClass = parseStoredJson("Extand_Class");
    return Array.isArray(extClass) ? extClass?.[0]?.UserRole || "" : "";
  }, []);
  const canManageTemplates = String(role || "").trim() !== "contracts_User";

  const menuItems = useMemo(() => getMenuItems(role, t), [role, t]);
  const shortLinks = useMemo(() => flattenMenuLinks(menuItems, t), [menuItems, t]);
  const section = useMemo(() => formatSectionWithParams(sectionKey, params, t), [sectionKey, params, t]);
  const sectionMeta = sectionDefinitions[sectionKey] || sectionDefinitions.form;
  const dataMode = useMemo(() => getDataMode(sectionKey, params?.id), [sectionKey, params?.id]);
  const activeHistoryReportId = dataMode === "drafts" ? DRAFTS_REPORT_ID : params?.id;
  const isHistorySection = dataMode === "documents" || dataMode === "drafts";
  const isTemplatesSection = dataMode === "templates";
  const isDraftHistorySection = isHistorySection && String(activeHistoryReportId || "") === DRAFTS_REPORT_ID;
  const isExpiredHistorySection = isHistorySection && String(activeHistoryReportId || "") === EXPIRED_REPORT_ID;
  const isSelectableHistorySection = isDraftHistorySection;
  const historyReportConfig = useMemo(
    () => getHistoryReportConfig(activeHistoryReportId),
    [activeHistoryReportId]
  );
  const driveRootLabel = t("sidebar.DANAsign Drive", {
    defaultValue: "Document repository",
    appName: "DANASign"
  });
  const currentDriveFolderId = drivePath[drivePath.length - 1]?.objectId || "";
  const isInProgressHistorySection =
    isHistorySection && String(activeHistoryReportId || "") === IN_PROGRESS_REPORT_ID;
  const isNeedYourSignHistorySection =
    isHistorySection && String(activeHistoryReportId || "") === NEED_YOUR_SIGN_REPORT_ID;
  const isCompletedHistorySection =
    isHistorySection && String(activeHistoryReportId || "") === COMPLETED_REPORT_ID;
  const isDeclinedHistorySection =
    isHistorySection && String(activeHistoryReportId || "") === DECLINED_REPORT_ID;
  const canManageApiTokens = isAdminRole(role);
  const currentSessionUser = Parse.User.current();
  const currentSessionUserId = String(currentSessionUser?.id || currentSessionUser?.objectId || "").trim();
  const currentSessionUserEmail = normalizeEmailAddress(
    currentSessionUser?.get?.("email") ||
      currentSessionUser?.get?.("Email") ||
      currentSessionUser?.attributes?.email ||
      currentSessionUser?.attributes?.Email ||
      currentSessionUser?.email ||
      localStorage.getItem("userEmail") ||
      ""
  );

  useEffect(() => {
    if (!isHistorySection || !dataError) {
      return;
    }
    showError(dataError);
  }, [dataError, isHistorySection, showError]);

  useEffect(() => {
    if (!isHistorySection || !dataNotice) {
      return;
    }
    showSuccess(dataNotice);
  }, [dataNotice, isHistorySection, showSuccess]);

  useEffect(() => {
    if (!isTemplatesSection || !dataError) {
      return;
    }
    showError(dataError);
  }, [dataError, isTemplatesSection, showError]);

  useEffect(() => {
    if (!isTemplatesSection || !dataNotice) {
      return;
    }
    showSuccess(dataNotice);
  }, [dataNotice, isTemplatesSection, showSuccess]);

  const replaceMailVariables = (template = "", variables = {}) => {
    return Object.entries(variables).reduce((result, [key, value]) => {
      const regex = new RegExp(`{{${key}}}`, "g");
      return result.replace(regex, String(value ?? ""));
    }, String(template || ""));
  };

  const getRecipientSigningUrl = (docId, recipient) => {
    const email = String(recipient?.email || "").trim();
    if (!docId || !email) {
      return "";
    }
    const signerObjectId = String(recipient?.signerObjectId || "").trim();
    const payload = signerObjectId ? `${docId}/${email}/${signerObjectId}` : `${docId}/${email}`;
    return `${window.location.origin}/login/${btoa(payload)}`;
  };

  const getInProgressRecipients = (row) => {
    const recipientsByEmail = new Map();
    const auditTrail = Array.isArray(row?.AuditTrail) ? row.AuditTrail : [];

    const attachRecipient = (sourceEmail, sourceName = "", sourceObjectId = "") => {
      const email = String(sourceEmail || "").trim();
      if (!email) {
        return;
      }
      const key = email.toLowerCase();
      if (recipientsByEmail.has(key)) {
        return;
      }
      const signerObjectId = String(sourceObjectId || "").trim();
      const signerIdentity = {
        objectId: signerObjectId,
        Email: email
      };
      recipientsByEmail.set(key, {
        id: signerObjectId || key,
        email,
        name: String(sourceName || "").trim(),
        signerObjectId,
        status: getSignerAuditStatus(signerIdentity, auditTrail)
      });
    };

    if (Array.isArray(row?.Placeholders)) {
      row.Placeholders.forEach((placeholder) => {
        attachRecipient(
          placeholder?.signerPtr?.Email || placeholder?.email,
          placeholder?.signerPtr?.Name || placeholder?.name,
          placeholder?.signerObjId || placeholder?.signerPtr?.objectId
        );
      });
    }

    if (Array.isArray(row?.Signers)) {
      row.Signers.forEach((signer) => {
        attachRecipient(
          signer?.Email || signer?.email,
          signer?.Name || signer?.name,
          signer?.objectId || signer?.UserPtr?.objectId || signer?.UserId?.objectId
        );
      });
    }

    return Array.from(recipientsByEmail.values());
  };

  const buildResendMailForRecipient = (row, recipient) => {
    const docId = String(row?.objectId || "").trim();
    const signingUrl = getRecipientSigningUrl(docId, recipient);
    const requestBodyMode = String(row?.RequestBodyMode || row?.ExtUserPtr?.TenantId?.RequestBodyMode || "").toLowerCase();
    const isPlainTemplate = requestBodyMode === "plain";
    const senderName = String(row?.ExtUserPtr?.Name || "").trim();
    const senderEmail = String(row?.ExtUserPtr?.Email || "").trim();
    const receiverName = String(recipient?.name || "").trim();
    const receiverEmail = String(recipient?.email || "").trim();
    const expireDate = parseDocDate(row?.ExpiryDate);
    const fallbackSubject = t("new-ui.history.resend.default-subject", {
      defaultValue: "{{sender_name}} has requested your signature on \"{{document_title}}\""
    });
    const fallbackBody = t("new-ui.history.resend.default-body", {
      defaultValue:
        "<p>Hello {{receiver_name}},</p><p>{{sender_name}} has requested your signature on <strong>{{document_title}}</strong>.</p><p>Please review and sign using this link:</p><p>{{signing_url}}</p><p>Thanks,<br/>DANASign team</p>"
    });
    const subjectTemplate = String(row?.RequestSubject || row?.ExtUserPtr?.TenantId?.RequestSubject || fallbackSubject);
    const bodyTemplate = String(row?.RequestBody || row?.ExtUserPtr?.TenantId?.RequestBody || fallbackBody);
    const variables = {
      document_title: getDocTitle(row, 0, t),
      note: String(row?.Note || "").trim(),
      sender_name: senderName,
      sender_mail: senderEmail,
      sender_phone: String(row?.ExtUserPtr?.Phone || "").trim(),
      receiver_name: receiverName,
      receiver_email: receiverEmail,
      receiver_phone: "",
      expiry_date: expireDate ? formatDate(expireDate, i18n, true) : "",
      company_name: String(row?.ExtUserPtr?.Company || "").trim(),
      signing_url: isPlainTemplate ? signingUrl : `<a href="${signingUrl}" target="_blank">Sign here</a>`
    };

    return {
      subject: replaceMailVariables(subjectTemplate, variables),
      body: replaceMailVariables(bodyTemplate, variables)
    };
  };

  useEffect(() => {
    setDataQuery("");
  }, [dataMode, params?.id, currentDriveFolderId]);

  useEffect(() => {
    if (!isHistorySection) {
      setHistoryPage(1);
      setHistoryHasMore(false);
      return;
    }
    setHistoryPage(1);
    setHistoryHasMore(false);
  }, [isHistorySection, activeHistoryReportId]);

  useEffect(() => {
    setActiveInProgressMenuDocId("");
    setHistoryActionTargetRow(null);
  }, [activeHistoryReportId, dataMode]);

  useEffect(() => {
    if (!activeInProgressMenuDocId) {
      return;
    }

    const handleClickOutside = (event) => {
      const target = event?.target;
      if (!(target instanceof Element)) {
        return;
      }
      if (target.closest("[data-inprogress-menu-root='true']")) {
        return;
      }
      setActiveInProgressMenuDocId("");
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [activeInProgressMenuDocId]);

  useEffect(() => {
    if (!isSelectableHistorySection) {
      setSelectedDraftIds([]);
      setSelectedExpiredIds([]);
      setIsDeleteDraftsConfirmModalOpen(false);
    }
  }, [isSelectableHistorySection, activeHistoryReportId]);

  useEffect(() => {
    if (!isTemplatesSection) {
      setSelectedTemplateIds([]);
      setIsDeleteTemplatesConfirmModalOpen(false);
    }
  }, [isTemplatesSection]);

  useEffect(() => {
    if (dataMode !== "drive") {
      return;
    }
    setDrivePath([{ objectId: "", name: driveRootLabel }]);
    setDrivePage(1);
    setDriveHasMore(false);
  }, [dataMode]);

  useEffect(() => {
    setDrivePath((current) => {
      if (!current.length) {
        return [{ objectId: "", name: driveRootLabel }];
      }
      const [root, ...rest] = current;
      return [{ ...root, name: driveRootLabel }, ...rest];
    });
  }, [driveRootLabel]);

  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      if (dataMode === "none") {
        setRows([]);
        setDataError("");
        return;
      }

      setIsLoadingData(true);
      setDataError("");
      setDataNotice("");

      try {
        if (dataMode === "drive") {
          const baseUrl = getParseFunctionBaseUrl();
          if (!baseUrl) {
            throw new Error(
              t("new-ui.dashboard.errors.load-failed", {
                defaultValue: "Could not load the data from backend."
              })
            );
          }
          const driveRes = await axios.post(
            `${baseUrl}functions/getDrive`,
            {
              docId: currentDriveFolderId || "",
              skip: Math.max(0, (drivePage - 1) * DRIVE_PAGE_LIMIT),
              limit: DRIVE_PAGE_LIMIT
            },
            {
              headers: getParseFunctionHeaders()
            }
          );
          const drivePayload = driveRes?.data || null;
          const driveError = drivePayload?.result?.error || drivePayload?.error || "";
          if (driveError) {
            throw new Error(driveError);
          }
          const driveRows = normalizeCloudResult(drivePayload);
          if (!cancelled) {
            setRows(driveRows);
            setDriveHasMore(driveRows.length === DRIVE_PAGE_LIMIT);
          }
          return;
        }

        if (dataMode === "contacts") {
          const reportId = params?.id || CONTACTS_REPORT_ID;
          let contactRows = [];

          try {
            contactRows = await fetchReportRows(reportId, {
              skip: 0,
              limit: 500
            });
          } catch (reportError) {
            contactRows = [];
          }

          if (!contactRows.length) {
            const mapParseRows = (items) =>
              Array.isArray(items) ? items.map((item) => toPlainObject(item)).filter(Boolean) : [];
            const queryLimit = 500;
            const currentUser = Parse.User.current();
            const storedTenantId = String(localStorage.getItem("TenantId") || tenantId || "").trim();
            const tenantPointer = storedTenantId
              ? Parse.Object.extend("partners_Tenant").createWithoutData(storedTenantId)
              : null;

            if (currentUser) {
              try {
                const byUserQuery = new Parse.Query("contracts_Contactbook");
                byUserQuery.equalTo("CreatedBy", currentUser);
                byUserQuery.notEqualTo("IsDeleted", true);
                byUserQuery.descending("updatedAt");
                byUserQuery.limit(queryLimit);
                const byUserRows = await byUserQuery.find();
                contactRows = mapParseRows(byUserRows);
              } catch (userQueryError) {
                contactRows = [];
              }
            }

            if (!contactRows.length && tenantPointer) {
              try {
                const byTenantQuery = new Parse.Query("contracts_Contactbook");
                byTenantQuery.equalTo("TenantId", tenantPointer);
                byTenantQuery.notEqualTo("IsDeleted", true);
                byTenantQuery.descending("updatedAt");
                byTenantQuery.limit(queryLimit);
                const byTenantRows = await byTenantQuery.find();
                contactRows = mapParseRows(byTenantRows);
              } catch (tenantQueryError) {
                contactRows = [];
              }
            }

            if (!contactRows.length) {
              const baseUrl = getParseFunctionBaseUrl();
              if (baseUrl) {
                try {
                  const signerResponse = await axios.post(
                    `${baseUrl}functions/getsigners`,
                    { search: "" },
                    { headers: getParseFunctionHeaders() }
                  );
                  const signerRows = signerResponse?.data?.result;
                  contactRows = Array.isArray(signerRows) ? signerRows : [];
                } catch (signersError) {
                  contactRows = [];
                }
              }
            }
          }

          if (!cancelled) {
            setRows(contactRows);
          }
          return;
        }

        const reportId =
          dataMode === "drafts"
            ? DRAFTS_REPORT_ID
            : dataMode === "templates"
              ? TEMPLATE_REPORT_ID
              : params?.id;
        const isPaginatedHistoryLoad = dataMode === "documents" || dataMode === "drafts";
        const reportSkip = isPaginatedHistoryLoad ? Math.max(0, (historyPage - 1) * HISTORY_PAGE_LIMIT) : 0;
        const reportLimit = isPaginatedHistoryLoad ? HISTORY_PAGE_LIMIT : 100;
        let normalizedRows = [];
        let hasMoreRows = false;

        if (reportId === IN_PROGRESS_REPORT_ID) {
          const inProgressRows = await fetchReportRows(reportId, {
            skip: reportSkip,
            limit: reportLimit
          });
          normalizedRows = inProgressRows;
          hasMoreRows = inProgressRows.length === reportLimit;
        } else {
          const baseReportRows = await fetchReportRows(reportId, {
            skip: reportSkip,
            limit: reportLimit
          });
          normalizedRows = baseReportRows;
          hasMoreRows = baseReportRows.length === reportLimit;
        }

        if (reportId === NEED_YOUR_SIGN_REPORT_ID) {
          const parseUser = Parse.User.current();
          const currentUserId = parseUser?.id;
          const currentUserEmail = normalizeEmailAddress(
            parseUser?.get?.("email") ||
              parseUser?.get?.("Email") ||
              parseUser?.attributes?.email ||
              parseUser?.attributes?.Email ||
              parseUser?.email ||
              localStorage.getItem("userEmail") ||
              ""
          );
          normalizedRows = normalizedRows.filter((record) =>
            isNeedYourSignRecordForCurrentUser(record, currentUserId, currentUserEmail)
          );
        }
        if (dataMode === "templates" && sectionKey === "template" && params?.templateId) {
          const selectedTemplateId = String(params.templateId || "").trim();
          if (selectedTemplateId) {
            const explicitTemplate = await fetchTemplateRecordById(selectedTemplateId).catch(() => null);
            if (explicitTemplate?.objectId) {
              normalizedRows = [explicitTemplate];
            } else {
              normalizedRows = normalizedRows.filter(
                (record) => String(record?.objectId || "").trim() === selectedTemplateId
              );
            }
          }
        }

        if (!cancelled) {
          if (isPaginatedHistoryLoad) {
            setHistoryHasMore(hasMoreRows);
          } else {
            setHistoryHasMore(false);
          }
          setRows(normalizedRows);
        }
      } catch (error) {
        console.error("Section workspace data load failed", error);
        if (!cancelled) {
          setRows([]);
          setHistoryHasMore(false);
          setDataError(
            t("new-ui.dashboard.errors.load-failed", {
              defaultValue: "Could not load the data from backend."
            })
          );
        }
      } finally {
        if (!cancelled) {
          setIsLoadingData(false);
        }
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, [dataMode, params?.id, params?.templateId, sectionKey, t, currentDriveFolderId, drivePage, historyPage, dataRefreshToken]);

  // Auto-open template editor when navigating from TemplateCreatePage
  useEffect(() => {
    const autoEditId = location.state?.autoEditTemplateId;
    if (!autoEditId || isLoadingData || !rows.length) {
      return;
    }
    const templateRow = rows.find(
      (row) => String(row?.objectId || "").trim() === autoEditId
    );
    if (templateRow) {
      navigate(location.pathname + (location.search || ""), {
        replace: true,
        state: {}
      });
      openTemplateAsDraft(templateRow, { templateEdit: true });
    }
  }, [location.state?.autoEditTemplateId, isLoadingData, rows]);

  const openDriveFolder = (folderRow) => {
    const folderId = String(folderRow?.objectId || "").trim();
    if (!folderId) {
      return;
    }
    const folderName =
      String(folderRow?.Name || "").trim() ||
      t("new-ui.dashboard.default-document-title", {
        index: drivePath.length + 1,
        defaultValue: `Folder ${drivePath.length + 1}`
      });
    setRows([]);
    setDrivePage(1);
    setDriveHasMore(false);
    setDrivePath((current) => [...current, { objectId: folderId, name: folderName }]);
  };

  const goToDriveCrumb = (index) => {
    setRows([]);
    setDrivePage(1);
    setDriveHasMore(false);
    setDrivePath((current) => current.filter((_, itemIndex) => itemIndex <= index));
  };

  const goToPreviousDrivePage = () => {
    if (dataMode !== "drive") {
      return;
    }
    setDrivePage((current) => Math.max(1, current - 1));
  };

  const goToNextDrivePage = () => {
    if (dataMode !== "drive") {
      return;
    }
    if (!driveHasMore) {
      return;
    }
    setDrivePage((current) => current + 1);
  };

  const goToPreviousHistoryPage = () => {
    if (!isHistorySection) {
      return;
    }
    setHistoryPage((current) => Math.max(1, current - 1));
  };

  const goToNextHistoryPage = () => {
    if (!isHistorySection || !historyHasMore) {
      return;
    }
    setHistoryPage((current) => current + 1);
  };

  const openCreateFolderModal = () => {
    setNewFolderName("");
    setNewFolderError("");
    setIsCreateFolderModalOpen(true);
  };

  const createDriveFolder = async () => {
    if (dataMode !== "drive") {
      return;
    }
    const folderName = String(newFolderName || "").trim();
    if (!folderName) {
      setNewFolderError(t("fill-folder-name", { defaultValue: "Please fill folder name" }));
      return;
    }
    setIsCreatingFolder(true);
    setNewFolderError("");
    try {
      const folderClass = "contracts_Document";
      const folderPointer = currentDriveFolderId
        ? {
            __type: "Pointer",
            className: folderClass,
            objectId: currentDriveFolderId
          }
        : null;
      const currentUserId = Parse.User.current()?.id || "";

      const duplicateQuery = new Parse.Query(folderClass);
      duplicateQuery.equalTo("Name", folderName);
      duplicateQuery.equalTo("Type", "Folder");
      duplicateQuery.notEqualTo("IsArchive", true);
      if (folderPointer) {
        duplicateQuery.equalTo("Folder", folderPointer);
      } else {
        duplicateQuery.doesNotExist("Folder");
      }
      const folderExists = await duplicateQuery.first();
      if (folderExists) {
        setNewFolderError(t("folder-already-exist", { defaultValue: "Folder already exist!" }));
        return;
      }

      const folderRecord = new Parse.Object(folderClass);
      folderRecord.set("Name", folderName);
      folderRecord.set("Type", "Folder");
      if (folderPointer) {
        folderRecord.set("Folder", folderPointer);
      }
      if (currentUserId) {
        folderRecord.set("CreatedBy", {
          __type: "Pointer",
          className: "_User",
          objectId: currentUserId
        });
      }
      const savedFolder = await folderRecord.save();
      const normalizedFolder = {
        objectId: savedFolder.id,
        Name: savedFolder.get("Name"),
        Type: savedFolder.get("Type"),
        Description: savedFolder.get("Description"),
        updatedAt: savedFolder.updatedAt,
        createdAt: savedFolder.createdAt,
        CreatedBy: savedFolder.get("CreatedBy")
      };
      setRows((current) => [normalizedFolder, ...current]);
      setIsCreateFolderModalOpen(false);
      setNewFolderName("");
      setNewFolderError("");
    } catch (error) {
      console.error("Create folder failed", error);
      setNewFolderError(
        error?.message ||
          t("new-ui.dashboard.errors.load-failed", {
            defaultValue: "Could not load the data from backend."
          })
      );
    } finally {
      setIsCreatingFolder(false);
    }
  };

  const secureAssetUrl = async (rawUrl, { docId = "", templateId = "" } = {}) => {
    const value = String(rawUrl || "").trim();
    if (!value) {
      return "";
    }
    try {
      const signed = await Parse.Cloud.run("getsignedurl", {
        url: value,
        ...(docId ? { docId } : {}),
        ...(templateId ? { templateId } : {})
      });
      if (typeof signed === "string" && signed.trim()) {
        return signed;
      }
    } catch (error) {
      // Keep raw URL fallback.
    }
    return value;
  };

  const secureDocumentUrl = async (rawUrl, docId = "") => secureAssetUrl(rawUrl, { docId });

  const fetchCompletedDocumentSummary = async (docId) => {
    if (!docId) {
      return null;
    }
    try {
      const cloudResponse = await Parse.Cloud.run("getDocument", { docId });
      const normalized = normalizeDocumentPayload(cloudResponse);
      if (normalized) {
        return normalized;
      }
    } catch (error) {
      // Try query fallback.
    }
    try {
      const query = new Parse.Query("contracts_Document");
      const found = await query.get(docId);
      return toPlainObject(found);
    } catch (queryError) {
      return null;
    }
  };

  const closeCompletedSummaryModal = () => {
    if (isGeneratingCompletedCertificate || isDownloadingCompletedBundle) {
      return;
    }
    setIsCompletedSummaryModalOpen(false);
    setIsLoadingCompletedSummary(false);
    setIsDownloadingCompletedBundle(false);
    setCompletedSummaryError("");
    setCompletedSummary({ docId: "", name: "", note: "", folder: "", signedUrl: "", certificateUrl: "" });
  };

  const openCompletedSummaryModal = async (row) => {
    const docId = String(row?.objectId || "").trim();
    if (!docId) {
      return;
    }
    const rowFolderLabel =
      String(row?.Folder?.Name || "").trim() ||
      t("new-ui.sections.drive.root-folder-label", { defaultValue: "En la raíz" });
    setCompletedSummaryError("");
    setIsCompletedSummaryModalOpen(true);
    setIsLoadingCompletedSummary(true);
    setIsDownloadingCompletedBundle(false);
    setCompletedSummary({
      docId,
      name:
        String(row?.Name || "").trim() ||
        t("document-title", { defaultValue: "Document title" }),
      note: String(row?.Note || "").trim(),
      folder: rowFolderLabel,
      signedUrl: "",
      certificateUrl: ""
    });
    try {
      const doc = await fetchCompletedDocumentSummary(docId);
      if (!doc) {
        throw new Error(
          t("new-ui.individual-signing.document-not-found", {
            defaultValue: "Document not found or access denied."
          })
        );
      }
      const signedUrl = await secureDocumentUrl(doc?.SignedUrl || doc?.URL || "", docId);
      const certificateUrl = await secureDocumentUrl(doc?.CertificateUrl || "", docId);
      setCompletedSummary({
        docId,
        name:
          String(doc?.Name || "").trim() ||
          String(row?.Name || "").trim() ||
          t("document-title", { defaultValue: "Document title" }),
        note: String(doc?.Note || row?.Note || "").trim(),
        folder: String(doc?.Folder?.Name || "").trim() || rowFolderLabel,
        signedUrl,
        certificateUrl
      });
    } catch (error) {
      setCompletedSummaryError(
        error?.message ||
          t("something-went-wrong-mssg", {
            defaultValue: "Something went wrong."
          })
      );
    } finally {
      setIsLoadingCompletedSummary(false);
    }
  };

  const generateCompletedCertificate = async () => {
    const docId = String(completedSummary?.docId || "").trim();
    if (!docId) {
      return "";
    }
    setIsGeneratingCompletedCertificate(true);
    setCompletedSummaryError("");
    try {
      const response = await Parse.Cloud.run("generatecertificate", { docId });
      const nextUrl = await secureDocumentUrl(response?.CertificateUrl || "", docId);
      if (!nextUrl) {
        throw new Error(
          t("generate-certificate-err", {
            defaultValue:
              "We are currently experiencing an issue with certificate generation. Please try again later."
          })
        );
      }
      setCompletedSummary((current) => ({
        ...current,
        certificateUrl: nextUrl
      }));
      return nextUrl;
    } catch (error) {
      setCompletedSummaryError(
        error?.message ||
          t("generate-certificate-err", {
            defaultValue:
              "We are currently experiencing an issue with certificate generation. Please try again later."
          })
      );
      return "";
    } finally {
      setIsGeneratingCompletedCertificate(false);
    }
  };

  const ensureCompletedCertificateUrl = async () => {
    const currentUrl = String(completedSummary?.certificateUrl || "").trim();
    if (currentUrl) {
      return currentUrl;
    }
    return generateCompletedCertificate();
  };

  const downloadCompletedCertificatePdf = async () => {
    const docId = String(completedSummary?.docId || "").trim();
    if (!docId) {
      return;
    }
    setIsDownloadingCompletedBundle(true);
    setCompletedSummaryError("");
    try {
      const certificateUrl = await ensureCompletedCertificateUrl();
      if (!certificateUrl) {
        throw new Error(
          t("generate-certificate-err", {
            defaultValue:
              "We are currently experiencing an issue with certificate generation. Please try again later."
          })
        );
      }
      triggerDownload(certificateUrl);
    } catch (error) {
      setCompletedSummaryError(
        error?.message ||
          t("something-went-wrong-mssg", {
            defaultValue: "Something went wrong."
          })
      );
    } finally {
      setIsDownloadingCompletedBundle(false);
    }
  };

  const openDriveDocument = async (row) => {
    const docId = String(row?.objectId || "").trim();
    if (!docId) {
      return;
    }
    const status = getDocumentStatus(row, t);
    if (status?.key === "completed") {
      await openCompletedSummaryModal(row);
      return;
    }
    navigate(resolveDocumentOpenPath(row));
  };

  const historyFolderLabel = (row) =>
    String(row?.Folder?.Name || "").trim() ||
    t("new-ui.sections.drive.root-folder-label", { defaultValue: "En la raíz" });

  const mapSignerStatusRows = (row) => {
    const auditTrail = Array.isArray(row?.AuditTrail) ? row.AuditTrail : [];
    const bySignerKey = new Map();
    const signerFallbackStatus = isInProgressHistorySection
      ? "SENT"
      : row?.IsCompleted
        ? "SIGNED"
        : "NOT_SIGNED";

    const attachSigner = (sourceEmail, sourceName = "", sourceObjectId = "") => {
      const email = String(sourceEmail || "").trim();
      if (!email) {
        return;
      }
      const key = email.toLowerCase();
      if (bySignerKey.has(key)) {
        return;
      }
      const signerObjectId = String(sourceObjectId || "").trim();
      const signerIdentity = {
        objectId: signerObjectId,
        Email: email
      };
      const signerAuditEntries = getMatchedSignerAuditEntries(signerIdentity, auditTrail);
      const latestAuditEntry = signerAuditEntries[signerAuditEntries.length - 1] || null;
      bySignerKey.set(key, {
        email,
        name: String(sourceName || "").trim(),
        activity: getSignerAuditStatus(signerIdentity, auditTrail, { fallbackStatus: signerFallbackStatus }),
        requiresBiometric: isIdentityVerificationRequiredForSigner(row, signerIdentity),
        viewedOn: parseDocDate(latestAuditEntry?.ViewedOn?.iso || latestAuditEntry?.ViewedOn),
        signedOn: parseDocDate(latestAuditEntry?.SignedOn?.iso || latestAuditEntry?.SignedOn)
      });
    };

    if (Array.isArray(row?.Placeholders)) {
      row.Placeholders.forEach((placeholder) => {
        attachSigner(
          placeholder?.signerPtr?.Email || placeholder?.email,
          placeholder?.signerPtr?.Name || placeholder?.name,
          placeholder?.signerObjId || placeholder?.signerPtr?.objectId
        );
      });
    }
    if (Array.isArray(row?.Signers)) {
      row.Signers.forEach((signer) => {
        attachSigner(
          signer?.Email || signer?.email,
          signer?.Name || signer?.name,
          signer?.objectId || signer?.UserPtr?.objectId || signer?.UserId?.objectId
        );
      });
    }
    if (auditTrail.length) {
      auditTrail.forEach((entry) => {
        attachSigner(
          entry?.UserPtr?.Email ||
            entry?.Email ||
            entry?.email ||
            entry?.SignerPtr?.Email ||
            entry?.ContactPtr?.Email,
          entry?.UserPtr?.Name || entry?.SignerPtr?.Name || entry?.ContactPtr?.Name,
          entry?.UserPtr?.objectId || entry?.SignerPtr?.objectId || entry?.ContactPtr?.objectId
        );
      });
    }

    return Array.from(bySignerKey.values());
  };

  const getSignerActivityTone = (activity) => {
    const normalized = String(activity || "").trim().toUpperCase();
    if (normalized === "SIGNED") {
      return "border-emerald-300 bg-emerald-100 text-emerald-700";
    }
    if (normalized === "NOT_SIGNED") {
      return "border-amber-300 bg-amber-100 text-amber-800";
    }
    if (normalized === "VIEWED") {
      return "border-sky-300 bg-sky-100 text-sky-700";
    }
    if (normalized === "DECLINED") {
      return "border-rose-300 bg-rose-100 text-rose-700";
    }
    return "border-slate-300 bg-slate-100 text-slate-700";
  };

  const getSignerActivityLabel = (activity) => {
    const normalized = String(activity || "").trim().toUpperCase();
    if (normalized === "SIGNED") {
      return t("new-ui.sections.history.signer-status.signed", { defaultValue: "Signed" });
    }
    if (normalized === "NOT_SIGNED") {
      return t("new-ui.sections.history.signer-status.not-signed", { defaultValue: "Not signed" });
    }
    if (normalized === "VIEWED") {
      return t("new-ui.sections.history.signer-status.viewed", { defaultValue: "Viewed" });
    }
    if (normalized === "DECLINED") {
      return t("new-ui.sections.history.signer-status.declined", { defaultValue: "Declined" });
    }
    if (normalized === "SENT") {
      return t("new-ui.sections.history.signer-status.sent", { defaultValue: "Sent" });
    }
    return normalized || t("new-ui.sections.history.signer-status.not-signed", { defaultValue: "Not signed" });
  };

  const openSignersModal = (row) => {
    const signerRows = mapSignerStatusRows(row);
    setSignersModalRows(signerRows);
    setIsSignersModalOpen(true);
  };

  const openNeedYourSignDetailsModal = (row) => {
    setNeedYourSignDetailsRow(row || null);
    setIsNeedYourSignDetailsModalOpen(true);
  };

  const closeNeedYourSignDetailsModal = () => {
    setIsNeedYourSignDetailsModalOpen(false);
    setNeedYourSignDetailsRow(null);
  };

  const downloadHistoryFile = async (row) => {
    const docId = String(row?.objectId || "").trim();
    const sourceUrl = String(row?.SignedUrl || row?.URL || "").trim();
    if (!sourceUrl) {
      return;
    }
    setActiveHistoryDownloadId(docId);
    try {
      const signedUrl = await secureDocumentUrl(sourceUrl, docId);
      triggerDownload(signedUrl);
    } finally {
      setActiveHistoryDownloadId("");
    }
  };

  const downloadNeedYourSignOriginalFile = async (row) => {
    const docId = String(row?.objectId || "").trim();
    const sourceUrl = String(row?.URL || "").trim();
    if (!docId || !sourceUrl) {
      setDataNotice(
        t("new-ui.history.details.no-original-pdf", {
          defaultValue: "The original unsigned document is not available."
        })
      );
      return;
    }
    setActiveHistoryDownloadId(docId);
    try {
      const signedUrl = await secureDocumentUrl(sourceUrl, docId);
      triggerDownload(signedUrl);
    } finally {
      setActiveHistoryDownloadId("");
    }
  };

  const openHistoryDocumentAction = async (row, actionPath) => {
    if (String(activeHistoryReportId || "") === NEED_YOUR_SIGN_REPORT_ID) {
      const accessState = getNeedYourSignAccessState(
        row,
        currentSessionUserId,
        currentSessionUserEmail
      );
      if (!accessState.canSign) {
        setDataNotice(
          accessState.alreadySigned
            ? t("already-signed-message", {
                defaultValue: "You have already signed this document."
              })
            : t("new-ui.dashboard.pending-documents.waiting-turn", {
                defaultValue: "Waiting for previous signer."
              })
        );
        return;
      }
    }
    if (!actionPath) {
      return;
    }
    const status = getDocumentStatus(row, t);
    if (status?.key === "completed") {
      await openCompletedSummaryModal(row);
      return;
    }
    if (actionPath) {
      navigate(actionPath);
    }
  };

  const resolveHistoryActionPath = (row) => {
    const docId = String(row?.objectId || "").trim();
    if (!docId) {
      return "";
    }
    if (String(activeHistoryReportId || "") === NEED_YOUR_SIGN_REPORT_ID) {
      const accessState = getNeedYourSignAccessState(
        row,
        currentSessionUserId,
        currentSessionUserEmail
      );
      if (!accessState.canSign) {
        return "";
      }
      return `/internalSign/${docId}`;
    }
    return resolveDocumentOpenPath(row);
  };

  const openTemplateAsDraft = async (row, { templateEdit = false } = {}) => {
    if (templateEdit && !canManageTemplates) {
      setDataError(
        t("something-went-wrong-mssg", {
          defaultValue: "Something went wrong."
        })
      );
      return;
    }
    const templateId = String(row?.objectId || params?.templateId || "").trim();
    if (
      !templateId ||
      activeTemplateUseId ||
      activeTemplateEditId
    ) {
      return;
    }
    if (templateEdit) {
      setActiveTemplateEditId(templateId);
    } else {
      setActiveTemplateUseId(templateId);
    }
    setDataError("");
    setDataNotice("");
    try {
      const template = await fetchTemplateRecordById(templateId);
      if (!template?.objectId || !String(template?.URL || "").trim()) {
        throw new Error(
          t("template-not-found", {
            defaultValue: "Template not found."
          })
        );
      }

      const currentUser = Parse.User.current();
      if (!currentUser?.id) {
        throw new Error(
          t("invalid-session", {
            defaultValue: "Invalid session."
          })
        );
      }

      const currentExtClass = Array.isArray(extClass) ? extClass?.[0] || {} : {};
      const extUserId = String(
        currentExtClass?.objectId || template?.ExtUserPtr?.objectId || ""
      ).trim();
      const creatorId = String(
        currentExtClass?.UserId?.objectId || currentUser?.id || template?.CreatedBy?.objectId || ""
      ).trim();

      const recipients = buildTemplateRecipients(template);
      const signers = Array.from(
        new Set(
          recipients
            .map((recipient) => String(recipient?.objectId || "").trim())
            .filter(Boolean)
        )
      ).map((objectId) => ({
        __type: "Pointer",
        className: "contracts_Contactbook",
        objectId
      }));

      const bccPointers = (Array.isArray(template?.Bcc) ? template.Bcc : [])
        .map((contact) => String(contact?.objectId || contact?.id || "").trim())
        .filter(Boolean)
        .map((objectId) => ({
          __type: "Pointer",
          className: "contracts_Contactbook",
          objectId
        }));

      const draftDoc = new Parse.Object("contracts_Document");
      draftDoc.set("Name", String(template?.Name || "").trim() || `Template ${templateId}`);
      draftDoc.set("URL", String(template.URL || "").trim());
      if (template?.SignedUrl) {
        draftDoc.set("SignedUrl", template.SignedUrl);
      }
      if (template?.Description) {
        draftDoc.set("Description", template.Description);
      }
      if (template?.Note) {
        draftDoc.set("Note", template.Note);
      }
      if (Array.isArray(template?.Placeholders)) {
        draftDoc.set("Placeholders", template.Placeholders);
      }
      if (Array.isArray(template?.SignatureType)) {
        draftDoc.set("SignatureType", template.SignatureType);
      }
      if (bccPointers.length) {
        draftDoc.set("Bcc", bccPointers);
      }
      if (signers.length) {
        draftDoc.set("Signers", signers);
      }
      if (recipients.length) {
        draftDoc.set("NewUIRecipients", recipients);
      }

      draftDoc.set("SentToOthers", true);
      draftDoc.set("IsSignyourself", false);
      draftDoc.set("IsDraft", true);
      draftDoc.set("SendinOrder", Boolean(template?.SendinOrder));
      draftDoc.set("AutomaticReminders", Boolean(template?.AutomaticReminders));
      draftDoc.set("RemindOnceInEvery", Number.parseInt(template?.RemindOnceInEvery, 10) || 5);
      draftDoc.set("IsEnableOTP", Boolean(template?.IsEnableOTP));
      draftDoc.set("TimeToCompleteDays", Number.parseInt(template?.TimeToCompleteDays, 10) || 15);
      draftDoc.set("AllowModifications", Boolean(template?.AllowModifications));
      if (template?.NotifyOnSignatures !== undefined) {
        draftDoc.set("NotifyOnSignatures", Boolean(template?.NotifyOnSignatures));
      }
      if (template?.RedirectUrl) {
        draftDoc.set("RedirectUrl", template.RedirectUrl);
      }
      if (template?.FileAdapterId) {
        draftDoc.set("FileAdapterId", template.FileAdapterId);
      }
      if (extUserId) {
        draftDoc.set("ExtUserPtr", {
          __type: "Pointer",
          className: "contracts_Users",
          objectId: extUserId
        });
      }
      if (creatorId) {
        draftDoc.set("CreatedBy", {
          __type: "Pointer",
          className: "_User",
          objectId: creatorId
        });
      }
      draftDoc.set("TemplateId", {
        __type: "Pointer",
        className: "contracts_Template",
        objectId: templateId
      });

      const savedDoc = await draftDoc.save();
      const templateQuery = templateEdit
        ? `?templateEdit=1&templateId=${encodeURIComponent(templateId)}`
        : "";
      navigate(`/requestSignPdf/${savedDoc?.id}${templateQuery}`, {
        state: {
          title: templateEdit
            ? t("btnLabel.Edit", { defaultValue: "Edit" })
            : t("new-ui.templates.use-template", { defaultValue: "Use template" }),
          recipients,
          backTo: `${location.pathname}${location.search || ""}`,
          ...(templateEdit ? { templateEdit: true } : {}),
          templateId
        }
      });
    } catch (error) {
      setDataError(
        error?.message ||
          t("something-went-wrong-mssg", {
            defaultValue: "Something went wrong."
          })
      );
    } finally {
      if (templateEdit) {
        setActiveTemplateEditId("");
      } else {
        setActiveTemplateUseId("");
      }
    }
  };

  const exportTemplateJsonPayload = async (row) => {
    const templateId = String(row?.objectId || params?.templateId || "").trim();
    if (!templateId || activeTemplateJsonId || activeTemplateUseId) {
      return;
    }

    setActiveTemplateJsonId(templateId);
    setDataError("");
    setDataNotice("");

    try {
      const template = await fetchTemplateRecordById(templateId);
      if (!template?.objectId) {
        throw new Error(
          t("template-not-found", {
            defaultValue: "Template not found."
          })
        );
      }

      const sourceUrl = String(template?.URL || template?.SignedUrl || "").trim();
      if (!sourceUrl) {
        throw new Error(
          t("new-ui.templates.json.missing-file-url", {
            defaultValue: "The template does not have a PDF source URL."
          })
        );
      }

      const signedUrl = await secureAssetUrl(sourceUrl, { templateId: template.objectId });
      const pdfResponse = await fetch(signedUrl);
      if (!pdfResponse.ok) {
        throw new Error(
          t("new-ui.templates.json.fetch-failed", {
            defaultValue: "Could not download the template PDF."
          })
        );
      }
      const pdfBuffer = await pdfResponse.arrayBuffer();
      if (!pdfBuffer || pdfBuffer.byteLength === 0) {
        throw new Error(
          t("new-ui.templates.json.empty-file", {
            defaultValue: "Template PDF is empty."
          })
        );
      }

      const fileBase64 = arrayBufferToBase64(pdfBuffer);
      const payload = buildCreateDocumentPayloadFromTemplate(template, fileBase64);
      if (!Array.isArray(payload?.placeholders) || payload.placeholders.length === 0) {
        throw new Error(
          t("new-ui.templates.json.missing-placeholders", {
            defaultValue: "The template does not contain interactive placeholders."
          })
        );
      }
      if (!Array.isArray(payload?.signers) || payload.signers.length === 0) {
        throw new Error(
          t("new-ui.templates.json.missing-signers", {
            defaultValue: "The template does not contain signers."
          })
        );
      }
      const payloadJson = JSON.stringify(payload, null, 2);

      const jsonFileName = `${sanitizeJsonFileName(
        template?.Name || template?.FileName || `template-${templateId}`,
        `template-${templateId}`
      )}-createdocument.json`;
      triggerTextDownload(jsonFileName, payloadJson);

      setDataNotice(
        t("new-ui.templates.json.ready", {
          defaultValue: "JSON generated successfully."
        })
      );
    } catch (error) {
      console.error("Failed to generate template JSON payload", error);
      setDataError(
        error?.message ||
          t("something-went-wrong-mssg", {
            defaultValue: "Something went wrong."
          })
      );
    } finally {
      setActiveTemplateJsonId("");
    }
  };

  const copyTemplateApiPayload = async (row) => {
    const templateId = String(row?.objectId || "").trim();
    if (!templateId) return;
    try {
      const template = await fetchTemplateRecordById(templateId);
      if (!template?.objectId) {
        showError(t("template-not-found", { defaultValue: "Template not found." }));
        return;
      }
      const payload = buildTemplateApiPayload(template);
      const json = JSON.stringify(payload, null, 2);
      await navigator.clipboard.writeText(json);
      showSuccess(
        t("new-ui.templates.api-json-copied", {
          defaultValue: "API JSON copied to clipboard. Use POST /api/1.0/createdocument/{{id}}",
          id: templateId
        })
      );
    } catch (error) {
      console.error("Failed to copy API payload", error);
      showError(error?.message || t("something-went-wrong-mssg", { defaultValue: "Something went wrong." }));
    }
  };

  const closeTemplateRenameModal = () => {
    if (isTemplateRenameSaving) {
      return;
    }
    setIsTemplateRenameModalOpen(false);
    setTemplateRenameValue("");
    setTemplateRenameTargetRow(null);
  };

  const openTemplateRenameModal = (row) => {
    if (!canManageTemplates || isTemplateRenameSaving) {
      return;
    }
    const templateId = String(row?.objectId || "").trim();
    if (!templateId) {
      return;
    }
    const currentName = String(row?.Name || row?.Title || "").trim();
    setTemplateRenameTargetRow(row);
    setTemplateRenameValue(currentName);
    setIsTemplateRenameModalOpen(true);
  };

  const runTemplateRename = async () => {
    if (!canManageTemplates) {
      return;
    }
    const templateId = String(templateRenameTargetRow?.objectId || "").trim();
    const nextName = String(templateRenameValue || "").trim();
    if (!templateId || !nextName) {
      return;
    }

    setIsTemplateRenameSaving(true);
    setDataError("");
    setDataNotice("");
    try {
      const templateObject = new Parse.Object("contracts_Template");
      templateObject.id = templateId;
      templateObject.set("Name", nextName);
      await templateObject.save();
      setRows((current) =>
        current.map((item) =>
          String(item?.objectId || "").trim() === templateId
            ? { ...item, Name: nextName, Title: nextName }
            : item
        )
      );
      setDataNotice(
        t("new-ui.templates.rename-success", {
          defaultValue: "Template updated."
        })
      );
      setIsTemplateRenameModalOpen(false);
      setTemplateRenameValue("");
      setTemplateRenameTargetRow(null);
    } catch (error) {
      setDataError(
        error?.message ||
          t("something-went-wrong-mssg", {
            defaultValue: "Something went wrong."
          })
      );
    } finally {
      setIsTemplateRenameSaving(false);
    }
  };

  const closeAllInProgressActionModals = () => {
    if (isHistoryActionLoading) {
      return;
    }
    setIsHistoryResendModalOpen(false);
    setIsHistoryRenameModalOpen(false);
    setIsHistoryRevokeModalOpen(false);
    setIsHistoryDeleteModalOpen(false);
    setIsHistorySaveTemplateModalOpen(false);
    setHistoryActionTargetRow(null);
    setHistoryRenameValue("");
    setHistoryRevokeReason("");
    setHistoryResendRecipientEmail("");
    setHistoryResendSubject("");
    setHistoryResendBody("");
  };

  const openInProgressActionMenu = (docId) => {
    const normalizedDocId = String(docId || "").trim();
    if (!normalizedDocId) {
      return;
    }
    setActiveInProgressMenuDocId((current) => (current === normalizedDocId ? "" : normalizedDocId));
  };

  const openInProgressActionModal = (action, row) => {
    const docId = String(row?.objectId || "").trim();
    if (!docId) {
      return;
    }
    setActiveInProgressMenuDocId("");
    setHistoryActionTargetRow(row);

    if (action === "resend") {
      const recipients = getInProgressRecipients(row);
      const initialRecipient = recipients.find((recipient) => recipient.status !== "SIGNED") || recipients[0] || null;
      setHistoryResendRecipientEmail(String(initialRecipient?.email || "").trim().toLowerCase());
      const template = initialRecipient ? buildResendMailForRecipient(row, initialRecipient) : { subject: "", body: "" };
      setHistoryResendSubject(template.subject);
      setHistoryResendBody(template.body);
      setIsHistoryResendModalOpen(true);
      return;
    }
    if (action === "rename") {
      setHistoryRenameValue(String(row?.Name || "").trim());
      setIsHistoryRenameModalOpen(true);
      return;
    }
    if (action === "revoke") {
      setHistoryRevokeReason("");
      setIsHistoryRevokeModalOpen(true);
      return;
    }
    if (action === "delete") {
      setIsHistoryDeleteModalOpen(true);
      return;
    }
    if (action === "saveastemplate") {
      setIsHistorySaveTemplateModalOpen(true);
    }
  };

  const handleHistoryResendRecipientChange = (nextEmail) => {
    const normalizedEmail = String(nextEmail || "").trim().toLowerCase();
    setHistoryResendRecipientEmail(normalizedEmail);
    const recipients = getInProgressRecipients(historyActionTargetRow || {});
    const selectedRecipient = recipients.find(
      (recipient) => String(recipient?.email || "").trim().toLowerCase() === normalizedEmail
    );
    if (selectedRecipient) {
      const nextTemplate = buildResendMailForRecipient(historyActionTargetRow || {}, selectedRecipient);
      setHistoryResendSubject(nextTemplate.subject);
      setHistoryResendBody(nextTemplate.body);
    }
  };

  const runHistoryRename = async () => {
    const row = historyActionTargetRow;
    const docId = String(row?.objectId || "").trim();
    const nextName = String(historyRenameValue || "").trim();
    if (!docId || !nextName) {
      return;
    }
    setIsHistoryActionLoading("rename");
    setDataError("");
    setDataNotice("");
    try {
      const query = new Parse.Query("contracts_Document");
      const documentObj = await query.get(docId);
      documentObj.set("Name", nextName);
      await documentObj.save();
      setRows((current) =>
        current.map((item) => (String(item?.objectId || "").trim() === docId ? { ...item, Name: nextName } : item))
      );
      setDataNotice(t("document-updated", { defaultValue: "Document updated." }));
      closeAllInProgressActionModals();
    } catch (error) {
      setDataError(
        error?.message ||
          t("something-went-wrong-mssg", {
            defaultValue: "Something went wrong."
          })
      );
    } finally {
      setIsHistoryActionLoading("");
    }
  };

  const runHistoryRevoke = async () => {
    const row = historyActionTargetRow;
    const docId = String(row?.objectId || "").trim();
    if (!docId) {
      return;
    }
    setIsHistoryActionLoading("revoke");
    setDataError("");
    setDataNotice("");
    try {
      const documentObj = new Parse.Object("contracts_Document");
      documentObj.id = docId;
      documentObj.set("IsDeclined", true);
      documentObj.set("DeclineReason", String(historyRevokeReason || "").trim());
      const currentUserId = String(Parse.User.current()?.id || "").trim();
      if (currentUserId) {
        documentObj.set("DeclineBy", {
          __type: "Pointer",
          className: "_User",
          objectId: currentUserId
        });
      }
      await documentObj.save();
      setRows((current) => current.filter((item) => String(item?.objectId || "").trim() !== docId));
      setDataNotice(t("record-revoke-alert", { defaultValue: "Record revoked successfully!" }));
      closeAllInProgressActionModals();
    } catch (error) {
      setDataError(
        error?.message ||
          t("something-went-wrong-mssg", {
            defaultValue: "Something went wrong."
          })
      );
    } finally {
      setIsHistoryActionLoading("");
    }
  };

  const runHistoryDelete = async () => {
    const row = historyActionTargetRow;
    const docId = String(row?.objectId || "").trim();
    if (!docId) {
      return;
    }
    setIsHistoryActionLoading("delete");
    setDataError("");
    setDataNotice("");
    try {
      const documentObj = new Parse.Object("contracts_Document");
      documentObj.id = docId;
      documentObj.set("IsArchive", true);
      await documentObj.save();
      setRows((current) => current.filter((item) => String(item?.objectId || "").trim() !== docId));
      setDataNotice(t("record-archive-alert", { defaultValue: "Record archived successfully!" }));
      closeAllInProgressActionModals();
    } catch (error) {
      setDataError(
        error?.message ||
          t("something-went-wrong-mssg", {
            defaultValue: "Something went wrong."
          })
      );
    } finally {
      setIsHistoryActionLoading("");
    }
  };

  const runHistorySaveAsTemplate = async () => {
    const row = historyActionTargetRow;
    const docId = String(row?.objectId || "").trim();
    if (!docId) {
      return;
    }
    setIsHistoryActionLoading("save-template");
    setDataError("");
    setDataNotice("");
    try {
      const templateRes = await Parse.Cloud.run("saveastemplate", { docId });
      const createdTemplateId = String(templateRes?.id || templateRes?.objectId || "").trim();
      setDataNotice(
        createdTemplateId
          ? t("template-created", { defaultValue: "Template created." })
          : t("template-created", { defaultValue: "Template created." })
      );
      closeAllInProgressActionModals();
    } catch (error) {
      setDataError(
        error?.message ||
          t("something-went-wrong-mssg", {
            defaultValue: "Something went wrong."
          })
      );
    } finally {
      setIsHistoryActionLoading("");
    }
  };

  const runHistoryResend = async () => {
    const row = historyActionTargetRow;
    const docId = String(row?.objectId || "").trim();
    if (!docId) {
      return;
    }
    const recipients = getInProgressRecipients(row || {});
    const selectedRecipient = recipients.find(
      (recipient) =>
        String(recipient?.email || "").trim().toLowerCase() === String(historyResendRecipientEmail || "").toLowerCase()
    );
    if (!selectedRecipient) {
      setDataError(
        t("new-ui.history.resend.select-recipient", {
          defaultValue: "Select a recipient before sending."
        })
      );
      return;
    }

    setIsHistoryActionLoading("resend");
    setDataError("");
    setDataNotice("");
    try {
      const payload = {
        replyto: row?.ExtUserPtr?.Email || "",
        extUserId: row?.ExtUserPtr?.objectId || "",
        recipient: selectedRecipient.email,
        subject: String(historyResendSubject || "").trim(),
        from: row?.ExtUserPtr?.Email || "",
        html: String(historyResendBody || "")
      };
      const response = await Parse.Cloud.run("sendmailv3", payload);
      if (response?.status !== "success") {
        throw new Error(
          t("something-went-wrong-mssg", {
            defaultValue: "Something went wrong."
          })
        );
      }
      setDataNotice(t("mail-sent-alert", { defaultValue: "Mail sent successfully." }));
      closeAllInProgressActionModals();
    } catch (error) {
      setDataError(
        error?.message ||
          t("something-went-wrong-mssg", {
            defaultValue: "Something went wrong."
          })
      );
    } finally {
      setIsHistoryActionLoading("");
    }
  };

  const openCreateContactModal = () => {
    setContactModalMode("create");
    setContactForm({ objectId: "", Name: "", Email: "", Phone: "" });
    setContactFormError("");
    setIsContactModalOpen(true);
  };

  useEffect(() => {
    if (dataMode !== "contacts") {
      return;
    }
    const queryParams = new URLSearchParams(location.search || "");
    if (queryParams.get("action") !== "create") {
      return;
    }
    setContactModalMode("create");
    setContactForm({ objectId: "", Name: "", Email: "", Phone: "" });
    setContactFormError("");
    setIsContactModalOpen(true);
    queryParams.delete("action");
    const remainingQuery = queryParams.toString();
    navigate(`/report/contacts${remainingQuery ? `?${remainingQuery}` : ""}`, { replace: true });
  }, [dataMode, location.search, navigate]);

  const openContactImportModal = () => {
    setContactImportRows([]);
    setContactImportInvalidCount(0);
    setContactImportPage(1);
    setContactImportError("");
    setIsContactImportModalOpen(true);
  };

  const closeContactImportModal = (force = false) => {
    if ((isImportingContacts || isParsingContactImport) && !force) {
      return;
    }
    setIsContactImportModalOpen(false);
    setContactImportRows([]);
    setContactImportInvalidCount(0);
    setContactImportPage(1);
    setContactImportError("");
  };

  const checkRequiredHeaders = (headers) => {
    const normalized = new Set(headers.map((header) => resolveContactImportHeader(header)).filter(Boolean));
    return ["Name", "Email"].every((header) => normalized.has(header));
  };

  const normalizeImportedContactRow = (row) => {
    const base = {};
    Object.entries(row || {}).forEach(([key, value]) => {
      const normalizedKey = resolveContactImportHeader(key);
      if (!normalizedKey) {
        return;
      }
      base[normalizedKey] = String(value ?? "")
        .replace(/\r/g, "")
        .trim();
    });
    return {
      Name: String(base.Name || "").trim(),
      Email: String(base.Email || "")
        .trim()
        .toLowerCase(),
      Phone: String(base.Phone || "").trim()
    };
  };

  const processContactImportRows = (rawRows = []) => {
    if (!Array.isArray(rawRows) || rawRows.length === 0) {
      setContactImportRows([]);
      setContactImportInvalidCount(0);
      setContactImportPage(1);
      setContactImportError("");
      return;
    }

    const headers = [...new Set(rawRows.flatMap((item) => Object.keys(item || {})))];
    if (!checkRequiredHeaders(headers)) {
      setContactImportRows([]);
      setContactImportInvalidCount(0);
      setContactImportPage(1);
      setContactImportError(t("invalid-data", { defaultValue: "Invalid data" }));
      return;
    }

    if (rawRows.length > CONTACT_IMPORT_MAX_ROWS) {
      setContactImportRows([]);
      setContactImportInvalidCount(0);
      setContactImportPage(1);
      setContactImportError(
        t("100-records-only", { defaultValue: "Currently you can only import up to 100 records." })
      );
      return;
    }

    const normalizedRows = rawRows
      .map((item) => normalizeImportedContactRow(item))
      .filter((item) => item.Name || item.Email || item.Phone);
    const validRows = normalizedRows.filter((item) => CONTACT_EMAIL_REGEX.test(item.Email));
    const invalidCount = Math.max(0, normalizedRows.length - validRows.length);

    setContactImportRows(validRows);
    setContactImportInvalidCount(invalidCount);
    setContactImportPage(1);
    setContactImportError("");
  };

  const handleContactImportFile = async (event) => {
    const file = event.target?.files?.[0];
    if (!file) {
      setContactImportRows([]);
      setContactImportInvalidCount(0);
      setContactImportPage(1);
      setContactImportError("");
      return;
    }

    const extension = String(file.name || "")
      .split(".")
      .pop()
      ?.toLowerCase();
    if (!["csv", "xlsx", "xls"].includes(extension || "")) {
      setContactImportRows([]);
      setContactImportInvalidCount(0);
      setContactImportPage(1);
      setContactImportError(
        t("csv-excel-support-only", {
          defaultValue: "Upload a file in one of the following formats: CSV, XLSX or XLS."
        })
      );
      event.target.value = "";
      return;
    }

    setIsParsingContactImport(true);
    setContactImportError("");
    try {
      let parsedRows = [];
      if (extension === "csv") {
        const csvText = await file.text();
        const csvWorkbook = XLSX.read(csvText, { type: "string" });
        const firstSheetName = csvWorkbook.SheetNames?.[0];
        const firstSheet = firstSheetName ? csvWorkbook.Sheets[firstSheetName] : null;
        parsedRows = firstSheet ? XLSX.utils.sheet_to_json(firstSheet, { defval: "", raw: false }) : [];
      } else {
        const buffer = await file.arrayBuffer();
        const workbook = XLSX.read(new Uint8Array(buffer), { type: "array" });
        const firstSheetName = workbook.SheetNames?.[0];
        const firstSheet = firstSheetName ? workbook.Sheets[firstSheetName] : null;
        parsedRows = firstSheet ? XLSX.utils.sheet_to_json(firstSheet, { defval: "", raw: false }) : [];
      }
      processContactImportRows(parsedRows);
    } catch (error) {
      setContactImportRows([]);
      setContactImportInvalidCount(0);
      setContactImportPage(1);
      setContactImportError(t("invalid-data", { defaultValue: "Invalid data" }));
    } finally {
      setIsParsingContactImport(false);
    }
  };

  const saveImportedContacts = async () => {
    if (dataMode !== "contacts") {
      return;
    }
    if (!contactImportRows.length) {
      setContactImportError(t("invalid-data", { defaultValue: "Invalid data" }));
      return;
    }

    setIsImportingContacts(true);
    setContactImportError("");
    setDataNotice("");
    try {
      let effectiveTenantId = tenantId;
      if (!effectiveTenantId) {
        const currentUserId = Parse.User.current()?.id || "";
        if (currentUserId) {
          try {
            const tenantPayload = await Parse.Cloud.run("gettenant", { userId: currentUserId });
            effectiveTenantId = String(tenantPayload?.objectId || tenantPayload?.id || "").trim();
          } catch (tenantError) {
            effectiveTenantId = "";
          }
        }
      }

      const contactsPayload = contactImportRows.map((item) => ({
        Name: item.Name,
        Email: item.Email,
        Phone: item.Phone,
        TenantId: effectiveTenantId || ""
      }));

      const response = await Parse.Cloud.run("createbatchcontact", {
        contacts: JSON.stringify(contactsPayload)
      });
      const imported = Number(response?.success || 0);
      const failed = Number(response?.failed || 0);
      setDataNotice(
        t("contact-imported", {
          imported,
          failed,
          defaultValue: `${imported} contacts were imported. ${failed} contacts failed to import.`
        })
      );
      closeContactImportModal(true);
      setDataRefreshToken((current) => current + 1);
    } catch (error) {
      setContactImportError(
        String(error?.message || "").trim() ||
          t("something-went-wrong-mssg", {
            defaultValue: "Something went wrong."
          })
      );
    } finally {
      setIsImportingContacts(false);
    }
  };

  const openEditContactModal = (row) => {
    setContactModalMode("edit");
    setContactForm({
      objectId: String(row?.objectId || "").trim(),
      Name: String(row?.Name || "").trim(),
      Email: String(row?.Email || "").trim().toLowerCase(),
      Phone: String(row?.Phone || "").trim()
    });
    setContactFormError("");
    setIsContactModalOpen(true);
  };

  const closeContactModal = (force = false) => {
    if (isSavingContact && !force) {
      return;
    }
    setIsContactModalOpen(false);
    setContactFormError("");
    setContactForm({ objectId: "", Name: "", Email: "", Phone: "" });
  };

  const saveContact = async () => {
    if (dataMode !== "contacts") {
      return;
    }
    const name = String(contactForm?.Name || "").trim();
    const email = String(contactForm?.Email || "")
      .trim()
      .toLowerCase();
    const phone = String(contactForm?.Phone || "").trim();

    if (!name || !email) {
      setContactFormError(t("input-required", { defaultValue: "Please fill out this field." }));
      return;
    }
    if (!CONTACT_EMAIL_REGEX.test(email)) {
      setContactFormError(t("email-error", { defaultValue: "Please enter a valid email address." }));
      return;
    }

    setIsSavingContact(true);
    setContactFormError("");
    setDataNotice("");
    try {
      let effectiveTenantId = tenantId;
      if (!effectiveTenantId) {
        const currentUserId = Parse.User.current()?.id || "";
        if (currentUserId) {
          try {
            const tenantPayload = await Parse.Cloud.run("gettenant", { userId: currentUserId });
            effectiveTenantId = String(tenantPayload?.objectId || tenantPayload?.id || "").trim();
          } catch (tenantError) {
            effectiveTenantId = "";
          }
        }
      }

      if (contactModalMode === "edit") {
        const contactId = String(contactForm?.objectId || "").trim();
        if (!contactId) {
          throw new Error(t("something-went-wrong-mssg", { defaultValue: "Something went wrong." }));
        }
        if (!effectiveTenantId) {
          throw new Error(
            t("new-ui.settings.profile.tenant-missing", {
              defaultValue: "Tenant information is missing."
            })
          );
        }
        await Parse.Cloud.run("editcontact", {
          contactId,
          name,
          email,
          phone,
          tenantId: effectiveTenantId
        });
      } else {
        const createPayload = {
          name,
          email,
          phone
        };
        if (effectiveTenantId) {
          createPayload.tenantId = effectiveTenantId;
        }
        await Parse.Cloud.run("savecontact", createPayload);
      }
      closeContactModal(true);
      setDataNotice(
        contactModalMode === "edit"
          ? t("new-ui.contacts.updated", { defaultValue: "Contact updated successfully." })
          : t("new-ui.contacts.saved", { defaultValue: "Contact saved successfully." })
      );
      setDataRefreshToken((current) => current + 1);
    } catch (error) {
      const message = String(error?.message || "");
      if (message.toLowerCase().includes("already exists")) {
        setContactFormError(t("add-signer-alert", { defaultValue: "Contact already exists!" }));
      } else {
        setContactFormError(
          message ||
            t("something-went-wrong-mssg", {
              defaultValue: "Something went wrong."
            })
        );
      }
    } finally {
      setIsSavingContact(false);
    }
  };

  const openDeleteContactModal = (row) => {
    setContactToDelete(row || null);
    setIsDeleteContactModalOpen(true);
  };

  const closeDeleteContactModal = (force = false) => {
    if (isDeletingContact && !force) {
      return;
    }
    setIsDeleteContactModalOpen(false);
    setContactToDelete(null);
  };

  const deleteContact = async () => {
    const contactId = String(contactToDelete?.objectId || "").trim();
    if (!contactId) {
      closeDeleteContactModal();
      return;
    }
    setIsDeletingContact(true);
    setDataNotice("");
    try {
      const contact = new Parse.Object("contracts_Contactbook");
      contact.id = contactId;
      contact.set("IsDeleted", true);
      await contact.save();
      closeDeleteContactModal(true);
      setDataNotice(t("record-delete-alert", { defaultValue: "Record deleted successfully!" }));
      setDataRefreshToken((current) => current + 1);
    } catch (error) {
      setDataError(
        error?.message ||
          t("something-went-wrong-mssg", {
            defaultValue: "Something went wrong."
          })
      );
    } finally {
      setIsDeletingContact(false);
    }
  };

  const filteredRoutes = useMemo(() => {
    const q = routeQuery.trim().toLowerCase();
    if (!q) return shortLinks;
    return shortLinks.filter(
      (item) =>
        item.label.toLowerCase().includes(q) ||
        item.group.toLowerCase().includes(q) ||
        item.to.toLowerCase().includes(q)
    );
  }, [shortLinks, routeQuery]);

  const driveOwnerOptions = useMemo(() => {
    if (dataMode !== "drive") {
      return [];
    }
    const ownerSet = new Set(
      rows.map((row) => getOwnerName(row, t)).filter((owner) => String(owner || "").trim().length > 0)
    );
    return Array.from(ownerSet).sort((a, b) => String(a).localeCompare(String(b), undefined, { sensitivity: "base" }));
  }, [dataMode, rows, t]);

  const filteredRows = useMemo(() => {
    const q = dataQuery.trim().toLowerCase();

    if (dataMode === "drive") {
      let workingRows = [...rows];

      if (q) {
        workingRows = workingRows.filter((row, index) => {
          const terms = [getDocTitle(row, index, t), row?.Description, row?.Type, row?.objectId]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          return terms.includes(q);
        });
      }

      if (driveTypeFilter !== "all") {
        workingRows = workingRows.filter((row) => {
          const rowType = String(row?.Type || "").toLowerCase();
          if (driveTypeFilter === "document") {
            return rowType !== "folder";
          }
          return rowType === driveTypeFilter;
        });
      }

      if (driveStatusFilter !== "all") {
        workingRows = workingRows.filter((row) => {
          const rowType = String(row?.Type || "").toLowerCase();
          if (rowType === "folder") {
            return false;
          }
          return getDocumentStatus(row, t).key === driveStatusFilter;
        });
      }

      if (driveOwnerFilter !== "all") {
        workingRows = workingRows.filter((row) => getOwnerName(row, t) === driveOwnerFilter);
      }

      workingRows.sort((a, b) => {
        const dateA = parseDocDate(a?.updatedAt || a?.createdAt)?.getTime() || 0;
        const dateB = parseDocDate(b?.updatedAt || b?.createdAt)?.getTime() || 0;
        return driveDateSort === "oldest" ? dateA - dateB : dateB - dateA;
      });

      return workingRows;
    }

    if (!q) {
      return rows;
    }

    return rows.filter((row, index) => {
      if (dataMode === "contacts") {
        const terms = [row?.Name, row?.Email, row?.Phone].filter(Boolean).join(" ").toLowerCase();
        return terms.includes(q);
      }

      if (dataMode === "templates") {
        const templateName = String(row?.Name || getDocTitle(row, index, t) || "")
          .trim()
          .toLowerCase();
        const templateId = String(row?.objectId || "").trim().toLowerCase();
        return templateName.includes(q) || templateId.includes(q);
      }

      if (isHistorySection && String(activeHistoryReportId || "") === NEED_YOUR_SIGN_REPORT_ID) {
        const documentName = String(getDocTitle(row, index, t) || "").trim().toLowerCase();
        return documentName.includes(q);
      }

      const terms = [
        getDocTitle(row, index, t),
        getOwnerName(row, t),
        getSignerNames(row, t).join(" "),
        row?.objectId
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return terms.includes(q);
    });
  }, [
    rows,
    dataMode,
    dataQuery,
    t,
    driveTypeFilter,
    driveStatusFilter,
    driveOwnerFilter,
    driveDateSort,
    isHistorySection,
    activeHistoryReportId
  ]);

  const selectedHistoryIds = useMemo(
    () => (isExpiredHistorySection ? selectedExpiredIds : selectedDraftIds),
    [isExpiredHistorySection, selectedExpiredIds, selectedDraftIds]
  );

  const visibleSelectableRowIds = useMemo(() => {
    if (!isSelectableHistorySection) {
      return [];
    }
    return filteredRows
      .map((row) => String(row?.objectId || "").trim())
      .filter(Boolean);
  }, [filteredRows, isSelectableHistorySection]);

  const areAllVisibleHistoryRowsSelected = useMemo(() => {
    if (!visibleSelectableRowIds.length) {
      return false;
    }
    return visibleSelectableRowIds.every((docId) => selectedHistoryIds.includes(docId));
  }, [visibleSelectableRowIds, selectedHistoryIds]);

  useEffect(() => {
    const rowIdSet = new Set(rows.map((row) => String(row?.objectId || "").trim()).filter(Boolean));
    if (selectedDraftIds.length) {
      setSelectedDraftIds((current) => current.filter((docId) => rowIdSet.has(docId)));
    }
    if (selectedExpiredIds.length) {
      setSelectedExpiredIds((current) => current.filter((docId) => rowIdSet.has(docId)));
    }
    if (selectedTemplateIds.length) {
      setSelectedTemplateIds((current) => current.filter((templateId) => rowIdSet.has(templateId)));
    }
  }, [rows, selectedDraftIds.length, selectedExpiredIds.length, selectedTemplateIds.length]);

  const updateSelectedHistoryIds = (updater) => {
    if (isExpiredHistorySection) {
      setSelectedExpiredIds(updater);
      return;
    }
    setSelectedDraftIds(updater);
  };

  const toggleHistorySelection = (docId) => {
    const normalizedId = String(docId || "").trim();
    if (!normalizedId || !isSelectableHistorySection) {
      return;
    }
    updateSelectedHistoryIds((current) =>
      current.includes(normalizedId) ? current.filter((item) => item !== normalizedId) : [...current, normalizedId]
    );
  };

  const toggleSelectAllVisibleHistoryRows = () => {
    if (!visibleSelectableRowIds.length || !isSelectableHistorySection) {
      return;
    }
    updateSelectedHistoryIds((current) => {
      if (visibleSelectableRowIds.every((docId) => current.includes(docId))) {
        return current.filter((docId) => !visibleSelectableRowIds.includes(docId));
      }
      const next = new Set(current);
      visibleSelectableRowIds.forEach((docId) => next.add(docId));
      return Array.from(next);
    });
  };

  const openDeleteSelectedHistoryConfirmModal = () => {
    if (!selectedHistoryIds.length || isDeletingSelectedDrafts) {
      return;
    }
    setIsDeleteDraftsConfirmModalOpen(true);
  };

  const closeDeleteSelectedHistoryConfirmModal = () => {
    if (isDeletingSelectedDrafts) {
      return;
    }
    setIsDeleteDraftsConfirmModalOpen(false);
  };

  const deleteSelectedHistoryRows = async () => {
    if (!selectedHistoryIds.length || isDeletingSelectedDrafts || !isSelectableHistorySection) {
      return;
    }
    setIsDeleteDraftsConfirmModalOpen(false);
    setIsDeletingSelectedDrafts(true);
    setDataError("");
    setDataNotice("");
    try {
      const deleteResults = await Promise.allSettled(
        selectedHistoryIds.map(async (docId) => {
          const documentObj = new Parse.Object("contracts_Document");
          documentObj.id = docId;
          documentObj.set("IsArchive", true);
          await documentObj.save();
          return docId;
        })
      );
      const deletedIds = deleteResults
        .filter((result) => result.status === "fulfilled")
        .map((result) => result.value);
      const failedCount = deleteResults.length - deletedIds.length;

      if (deletedIds.length) {
        setRows((current) => current.filter((row) => !deletedIds.includes(String(row?.objectId || "").trim())));
        updateSelectedHistoryIds((current) => current.filter((docId) => !deletedIds.includes(docId)));
        setDataNotice(t("record-delete-alert", { defaultValue: "Record deleted successfully!" }));
      }

      if (failedCount) {
        setDataError(
          t("new-ui.sections.history.delete-selected-failed", {
            defaultValue: "Some selected documents could not be deleted."
          })
        );
      }
    } catch (error) {
      setDataError(
        error?.message ||
          t("something-went-wrong-mssg", {
            defaultValue: "Something went wrong."
          })
      );
    } finally {
      setIsDeletingSelectedDrafts(false);
    }
  };

  const visibleTemplateRowIds = useMemo(() => {
    if (!isTemplatesSection) {
      return [];
    }
    return filteredRows
      .map((row) => String(row?.objectId || "").trim())
      .filter(Boolean);
  }, [filteredRows, isTemplatesSection]);

  const areAllVisibleTemplateRowsSelected = useMemo(() => {
    if (!visibleTemplateRowIds.length) {
      return false;
    }
    return visibleTemplateRowIds.every((templateId) => selectedTemplateIds.includes(templateId));
  }, [visibleTemplateRowIds, selectedTemplateIds]);

  const toggleTemplateSelection = (templateId) => {
    const normalizedTemplateId = String(templateId || "").trim();
    if (!normalizedTemplateId || !isTemplatesSection) {
      return;
    }
    setSelectedTemplateIds((current) =>
      current.includes(normalizedTemplateId)
        ? current.filter((item) => item !== normalizedTemplateId)
        : [...current, normalizedTemplateId]
    );
  };

  const toggleSelectAllVisibleTemplateRows = () => {
    if (!visibleTemplateRowIds.length || !isTemplatesSection) {
      return;
    }
    setSelectedTemplateIds((current) => {
      if (visibleTemplateRowIds.every((templateId) => current.includes(templateId))) {
        return current.filter((templateId) => !visibleTemplateRowIds.includes(templateId));
      }
      const next = new Set(current);
      visibleTemplateRowIds.forEach((templateId) => next.add(templateId));
      return Array.from(next);
    });
  };

  const openDeleteSelectedTemplatesConfirmModal = () => {
    if (!canManageTemplates || !selectedTemplateIds.length || isDeletingSelectedTemplates) {
      return;
    }
    setIsDeleteTemplatesConfirmModalOpen(true);
  };

  const closeDeleteSelectedTemplatesConfirmModal = () => {
    if (isDeletingSelectedTemplates) {
      return;
    }
    setIsDeleteTemplatesConfirmModalOpen(false);
  };

  const deleteSelectedTemplates = async () => {
    if (!canManageTemplates || !selectedTemplateIds.length || isDeletingSelectedTemplates || !isTemplatesSection) {
      return;
    }
    setIsDeleteTemplatesConfirmModalOpen(false);
    setIsDeletingSelectedTemplates(true);
    setDataError("");
    setDataNotice("");
    try {
      const deleteResults = await Promise.allSettled(
        selectedTemplateIds.map(async (templateId) => {
          const templateObject = new Parse.Object("contracts_Template");
          templateObject.id = templateId;
          templateObject.set("IsArchive", true);
          await templateObject.save();
          return templateId;
        })
      );
      const deletedIds = deleteResults
        .filter((result) => result.status === "fulfilled")
        .map((result) => result.value);
      const failedCount = deleteResults.length - deletedIds.length;

      if (deletedIds.length) {
        setRows((current) => current.filter((row) => !deletedIds.includes(String(row?.objectId || "").trim())));
        setSelectedTemplateIds((current) => current.filter((templateId) => !deletedIds.includes(templateId)));
        setDataNotice(t("record-delete-alert", { defaultValue: "Record deleted successfully!" }));
      }

      if (failedCount) {
        setDataError(
          t("new-ui.templates.delete-selected-failed", {
            defaultValue: "Some selected templates could not be deleted."
          })
        );
      }
    } catch (error) {
      setDataError(
        error?.message ||
          t("something-went-wrong-mssg", {
            defaultValue: "Something went wrong."
          })
      );
    } finally {
      setIsDeletingSelectedTemplates(false);
    }
  };

  const totalContactImportPages = Math.max(1, Math.ceil(contactImportRows.length / CONTACT_IMPORT_PAGE_SIZE));
  const contactImportPreviewRows = useMemo(() => {
    const startIndex = (contactImportPage - 1) * CONTACT_IMPORT_PAGE_SIZE;
    return contactImportRows.slice(startIndex, startIndex + CONTACT_IMPORT_PAGE_SIZE);
  }, [contactImportRows, contactImportPage]);
  const historyResendRecipients = useMemo(
    () => getInProgressRecipients(historyActionTargetRow || {}),
    [historyActionTargetRow]
  );
  const selectedHistoryResendRecipient = useMemo(
    () =>
      historyResendRecipients.find(
        (recipient) =>
          String(recipient?.email || "").trim().toLowerCase() === String(historyResendRecipientEmail || "").toLowerCase()
      ) || null,
    [historyResendRecipients, historyResendRecipientEmail]
  );

  useEffect(() => {
    setContactImportPage((currentPage) => Math.min(currentPage, totalContactImportPages));
  }, [totalContactImportPages]);

  const openLegacyRoute = () => {
    const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const legacyPath = currentPath || "/";
    window.location.assign(legacyPath);
  };

  const dataPanelTitle = (() => {
    if (dataMode === "contacts") {
      return t("sidebar.Contactbook", { defaultValue: "Contactbook" });
    }
    if (dataMode === "templates") {
      return t("sidebar.Templates", { defaultValue: "Templates" });
    }
    if (dataMode === "drive") {
      return t("sidebar.DANAsign Drive", {
        defaultValue: "DANASign Drive",
        appName: "DANASign"
      });
    }
    if (isHistorySection) {
      const historyLabel = t("new-ui.sidebar.history", { defaultValue: "History" });
      const activeView = reportViewById[activeHistoryReportId];
      const subsectionLabel = activeView
        ? t(activeView.key, { defaultValue: activeView.defaultValue })
        : dataMode === "drafts"
          ? t("sidebar.Documents-Children.Drafts", { defaultValue: "Drafts" })
          : historyLabel;
      return `${historyLabel}: ${subsectionLabel}`;
    }
    return t("new-ui.sidebar.history", { defaultValue: "History" });
  })();
  const showWorkspaceInfoCards =
    dataMode !== "drive" &&
    dataMode !== "contacts" &&
    dataMode !== "templates" &&
    !isHistorySection;

  return (
    <AppShell>
      <div className="dana-section-grid">
        {showWorkspaceInfoCards ? (
          <Card className="animate-fade-up">
            <div className="dana-section-head">
              <span className="dana-section-icon">
                <Icon name={sectionMeta.icon} />
              </span>
              <div>
                <p className="ui-kicker">{t("new-ui.sections.kicker", { defaultValue: "DANASign Internal UI" })}</p>
                <h1 className="ui-title dana-section-title">{section.title}</h1>
              </div>
            </div>
            <p className="ui-copy mt-3">{section.description}</p>
            <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-3">
              <div className="rounded-lg border border-app-line bg-white px-3 py-2">
                <p className="text-[10px] uppercase tracking-[0.05em] text-app-ink-muted">
                  {t("new-ui.sections.metrics.module-links", { defaultValue: "Module links" })}
                </p>
                <p className="text-sm font-semibold text-app-ink">{sectionMeta.links.length}</p>
              </div>
              <div className="rounded-lg border border-app-line bg-white px-3 py-2">
                <p className="text-[10px] uppercase tracking-[0.05em] text-app-ink-muted">
                  {t("new-ui.sections.metrics.available-routes", { defaultValue: "Available routes" })}
                </p>
                <p className="text-sm font-semibold text-app-ink">{shortLinks.length}</p>
              </div>
              <div className="rounded-lg border border-app-line bg-white px-3 py-2">
                <p className="text-[10px] uppercase tracking-[0.05em] text-app-ink-muted">
                  {t("new-ui.sections.current-path", { defaultValue: "Current path" })}
                </p>
                <p className="truncate text-xs font-semibold text-app-ink" title={location.pathname}>
                  {location.pathname}
                </p>
              </div>
            </div>
          </Card>
        ) : null}

        {dataMode !== "none" ? (
          <Card className="animate-fade-up [animation-delay:40ms]">
            {dataMode === "drive" && drivePath.length > 1 ? (
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-app-line bg-slate-50 px-3 py-2">
                <div className="flex min-w-[220px] flex-wrap items-center gap-1.5 text-xs text-app-ink-muted">
                  {drivePath.map((crumb, index) => (
                    <span key={`${crumb.objectId || "root"}-${index}`} className="inline-flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => goToDriveCrumb(index)}
                        className={`rounded-md px-2 py-1 text-left transition ${
                          index === drivePath.length - 1
                            ? "bg-white font-semibold text-app-ink"
                            : "hover:bg-white hover:text-app-ink"
                        }`}
                      >
                        {crumb.name}
                      </button>
                      {index < drivePath.length - 1 ? <Icon name="chevronRight" className="h-3.5 w-3.5" /> : null}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="dana-panel-head">
              <h2 className="dana-title-with-icon">
                <span className="dana-title-icon" aria-hidden="true">
                  <Icon
                    name={
                      dataMode === "contacts"
                        ? "addressBook"
                        : dataMode === "templates"
                          ? "templateDoc"
                          : dataMode === "drive"
                            ? "folder"
                            : "timeline"
                    }
                  />
                </span>
                {dataPanelTitle}
              </h2>
              <input
                type="search"
                value={dataQuery}
                onChange={(event) => setDataQuery(event.target.value)}
                placeholder={
                  dataMode === "contacts"
                    ? t("new-ui.contacts.search-placeholder", {
                        defaultValue: "Search contact"
                      })
                    : dataMode === "templates"
                      ? t("new-ui.templates.search-by-name", {
                          defaultValue: "Buscar template por nombre"
                        })
                      : isNeedYourSignHistorySection
                        ? t("new-ui.history.need-your-sign.search-by-docname", {
                            defaultValue: "Buscar documento por nombre"
                          })
                      : t("new-ui.dashboard.recent-sent-documents.search-placeholder", {
                          defaultValue: "Search"
                        })
                }
                className="w-[170px] sm:w-[240px] rounded-lg border border-app-line bg-white px-2.5 py-1 text-[11px] text-app-ink outline-none transition focus:border-app-brand focus:ring-2 focus:ring-app-brand/20"
              />
              {dataMode === "drive" ? (
                <Button
                  type="button"
                  variant="success"
                  onClick={openCreateFolderModal}
                  className="inline-flex items-center gap-1.5"
                  disabled={isCreatingFolder}
                >
                  <Icon name="plus" className="h-4 w-4" />
                  <span>{t("create-folder", { defaultValue: "Create folder" })}</span>
                </Button>
              ) : null}
              {dataMode === "contacts" ? (
                <div className="inline-flex items-center gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={openContactImportModal}
                    className="inline-flex items-center gap-1.5"
                  >
                    <Icon name="plus" className="h-4 w-4" />
                    <span>{t("new-ui.contacts.import-action", { defaultValue: "Importar" })}</span>
                  </Button>
                  <Button
                    type="button"
                    onClick={openCreateContactModal}
                    className="inline-flex items-center gap-1.5"
                  >
                    <Icon name="plus" className="h-4 w-4" />
                    <span>{t("add-contact", { defaultValue: "Add contact" })}</span>
                  </Button>
                </div>
              ) : null}
              {isSelectableHistorySection ? (
                <Button
                  type="button"
                  variant="danger"
                  onClick={openDeleteSelectedHistoryConfirmModal}
                  disabled={!selectedHistoryIds.length || isDeletingSelectedDrafts}
                  className="inline-flex items-center gap-1.5"
                >
                  <Icon name="trash" className="h-4 w-4" />
                  {isDeletingSelectedDrafts
                    ? t("loading", { defaultValue: "Loading..." })
                    : t("new-ui.sections.history.delete-selected", { defaultValue: "Delete selected" })}
                </Button>
              ) : null}
              {isTemplatesSection && canManageTemplates ? (
                <Button
                  type="button"
                  variant="danger"
                  onClick={openDeleteSelectedTemplatesConfirmModal}
                  disabled={!selectedTemplateIds.length || isDeletingSelectedTemplates}
                  className="inline-flex items-center gap-1.5"
                >
                  <Icon name="trash" className="h-4 w-4" />
                  {isDeletingSelectedTemplates
                    ? t("loading", { defaultValue: "Loading..." })
                    : t("new-ui.templates.delete-selected", { defaultValue: "Eliminar seleccionados" })}
                </Button>
              ) : null}
            </div>

            {isLoadingData ? (
              <div className="mb-3 flex items-center justify-center gap-2 rounded-xl border border-app-line bg-white px-3 py-3 text-app-ink-muted">
                <span
                  className="inline-flex h-5 w-5 animate-spin rounded-full border-2 border-app-brand/25 border-t-app-brand"
                  aria-hidden="true"
                />
                <p className="m-0 text-sm font-medium">
                  {t("new-ui.dashboard.loading-documents", { defaultValue: "Loading documents..." })}
                </p>
              </div>
            ) : null}
            {!isTemplatesSection && dataError ? <p className="text-app-danger">{dataError}</p> : null}
            {!isTemplatesSection && dataNotice ? <p className="text-emerald-700">{dataNotice}</p> : null}

            {!isLoadingData && !dataError ? (
              dataMode === "drive" || dataMode === "contacts" || isHistorySection || filteredRows.length ? (
                <div
                  className={`rounded-xl border border-app-line bg-white shadow-soft ${
                    isHistorySection ? "overflow-visible" : "overflow-x-auto"
                  }`}
                >
                  {dataMode === "drive" ? (
                    <table className="w-full table-fixed border-collapse text-left">
                      <thead className="bg-app-sand">
                        <tr>
                          <th className="w-[34%] px-2 py-1.5 text-[11px] font-semibold text-app-ink-muted">
                            {t("report-heading.Title", { defaultValue: "Title" })}
                          </th>
                          <th className="w-[12%] px-2 py-1.5 text-[11px] font-semibold text-app-ink-muted">
                            {t("Type", { defaultValue: "Type" })}
                          </th>
                          <th className="w-[14%] px-2 py-1.5 text-[11px] font-semibold text-app-ink-muted">
                            {t("report-heading.Status", { defaultValue: "Status" })}
                          </th>
                          <th className="w-[14%] px-2 py-1.5 text-[11px] font-semibold text-app-ink-muted">
                            {t("new-ui.dashboard.rows.last-activity", { defaultValue: "Last activity" })}
                          </th>
                          <th className="w-[16%] px-2 py-1.5 text-[11px] font-semibold text-app-ink-muted">
                            {t("report-heading.Owner", { defaultValue: "Owner" })}
                          </th>
                          <th className="w-[10%] px-2 py-1.5 text-[11px] font-semibold text-app-ink-muted">
                            {t("action", { defaultValue: "Action" })}
                          </th>
                        </tr>
                        <tr className="border-t border-app-line bg-white">
                          <th className="px-2 py-1.5 text-[10px] font-medium text-app-ink-muted">
                            {t("new-ui.sections.drive.filters.search-with-box", {
                              defaultValue: "Use search box"
                            })}
                          </th>
                          <th className="px-2 py-1.5">
                            <select
                              value={driveTypeFilter}
                              onChange={(event) => setDriveTypeFilter(event.target.value)}
                              className="w-full rounded-md border border-app-line bg-white px-1.5 py-1 text-[10px] text-app-ink outline-none transition focus:border-app-brand focus:ring-1 focus:ring-app-brand/20"
                            >
                              <option value="all">{t("all", { defaultValue: "All" })}</option>
                              <option value="document">
                                {t("new-ui.sections.drive.document", { defaultValue: "Document" })}
                              </option>
                              <option value="folder">
                                {t("new-ui.sections.drive.folder", { defaultValue: "Folder" })}
                              </option>
                            </select>
                          </th>
                          <th className="px-2 py-1.5">
                            <select
                              value={driveStatusFilter}
                              onChange={(event) => setDriveStatusFilter(event.target.value)}
                              className="w-full rounded-md border border-app-line bg-white px-1.5 py-1 text-[10px] text-app-ink outline-none transition focus:border-app-brand focus:ring-1 focus:ring-app-brand/20"
                            >
                              <option value="all">{t("all", { defaultValue: "All" })}</option>
                              <option value="in-progress">
                                {t("sidebar.Documents-Children.In Progress", { defaultValue: "In progress" })}
                              </option>
                              <option value="completed">
                                {t("sidebar.Documents-Children.Completed", { defaultValue: "Completed" })}
                              </option>
                              <option value="draft">
                                {t("sidebar.Documents-Children.Drafts", { defaultValue: "Drafts" })}
                              </option>
                              <option value="declined">
                                {t("sidebar.Documents-Children.Declined", { defaultValue: "Declined" })}
                              </option>
                              <option value="expired">
                                {t("sidebar.Documents-Children.Expired", { defaultValue: "Expired" })}
                              </option>
                            </select>
                          </th>
                          <th className="px-2 py-1.5">
                            <select
                              value={driveDateSort}
                              onChange={(event) => setDriveDateSort(event.target.value)}
                              className="w-full rounded-md border border-app-line bg-white px-1.5 py-1 text-[10px] text-app-ink outline-none transition focus:border-app-brand focus:ring-1 focus:ring-app-brand/20"
                            >
                              <option value="newest">
                                {t("new-ui.sections.drive.filters.newest", { defaultValue: "Most recent" })}
                              </option>
                              <option value="oldest">
                                {t("new-ui.sections.drive.filters.oldest", { defaultValue: "Oldest" })}
                              </option>
                            </select>
                          </th>
                          <th className="px-2 py-1.5">
                            <select
                              value={driveOwnerFilter}
                              onChange={(event) => setDriveOwnerFilter(event.target.value)}
                              className="w-full rounded-md border border-app-line bg-white px-1.5 py-1 text-[10px] text-app-ink outline-none transition focus:border-app-brand focus:ring-1 focus:ring-app-brand/20"
                            >
                              <option value="all">{t("all", { defaultValue: "All" })}</option>
                              {driveOwnerOptions.map((owner) => (
                                <option key={owner} value={owner}>
                                  {owner}
                                </option>
                              ))}
                            </select>
                          </th>
                          <th className="px-2 py-1.5 text-[10px] text-app-ink-muted">--</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredRows.length ? (
                          filteredRows.map((row, index) => {
                            const docTitle = getDocTitle(row, index, t);
                            const isFolder = String(row?.Type || "").toLowerCase() === "folder";
                            const status = isFolder ? null : getDocumentStatus(row, t);
                            const updatedDate = parseDocDate(row?.updatedAt || row?.createdAt);
                            const ownerName = getOwnerName(row, t);
                            return (
                              <tr key={row.objectId || `${docTitle}-${index}`} className="border-t border-app-line align-top">
                                <td className="px-2 py-1.5 text-[12px] leading-tight">
                                  <div className="inline-flex min-w-0 items-center gap-1.5">
                                    <span
                                      className={`inline-flex h-5 w-5 items-center justify-center rounded-md ${
                                        isFolder ? "bg-amber-100 text-amber-600" : "bg-indigo-100 text-indigo-600"
                                      }`}
                                    >
                                      <Icon name={isFolder ? "folder" : "templateDoc"} className="h-3.5 w-3.5" />
                                    </span>
                                    <p className="m-0 truncate font-medium text-app-ink" title={docTitle}>
                                      {docTitle}
                                    </p>
                                  </div>
                                  {row?.Description ? (
                                    <p className="m-0 mt-0.5 truncate text-[10px] text-app-ink-muted" title={row.Description}>
                                      {row.Description}
                                    </p>
                                  ) : null}
                                </td>
                                <td className="px-2 py-1.5 text-[11px] text-app-ink-muted">
                                  <span
                                    className={`inline-flex rounded-full border px-2 py-1 ${
                                      isFolder
                                        ? "bg-amber-100 text-amber-700 border-amber-200"
                                        : "bg-indigo-100 text-indigo-700 border-indigo-200"
                                    }`}
                                  >
                                    {isFolder
                                      ? t("new-ui.sections.drive.folder", { defaultValue: "Folder" })
                                      : t("new-ui.sections.drive.document", { defaultValue: "Document" })}
                                  </span>
                                </td>
                                <td className="px-2 py-1.5 text-[11px] text-app-ink-muted">
                                  {status ? (
                                    <span className={`inline-flex rounded-full border px-2 py-1 ${status.toneClass}`}>
                                      {status.label}
                                    </span>
                                  ) : (
                                    "--"
                                  )}
                                </td>
                                <td className="px-2 py-1.5 text-[11px] text-app-ink-muted">{formatDate(updatedDate, i18n, true)}</td>
                                <td className="px-2 py-1.5 text-[11px] text-app-ink-muted truncate" title={ownerName}>
                                  {ownerName}
                                </td>
                                <td className="px-2 py-1.5 text-[11px] text-app-ink-muted">
                                  {isFolder ? (
                                    <button
                                      type="button"
                                      onClick={() => openDriveFolder(row)}
                                      className="text-indigo-600 hover:text-indigo-700 hover:underline"
                                    >
                                      {t("btnLabel.Open", { defaultValue: "Open" })}
                                    </button>
                                  ) : row?.objectId && status?.key === "completed" ? (
                                    <div className="inline-flex items-center gap-1.5">
                                      <button
                                        type="button"
                                        onClick={() => openCompletedSummaryModal(row)}
                                        className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-app-line bg-white text-app-ink transition hover:bg-slate-50"
                                        title={t("new-ui.history.need-your-sign.view-details", {
                                          defaultValue: "View document details"
                                        })}
                                        aria-label={t("new-ui.history.need-your-sign.view-details", {
                                          defaultValue: "View document details"
                                        })}
                                      >
                                        <Icon name="eye" className="h-4 w-4" />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => downloadHistoryFile(row)}
                                        disabled={
                                          activeHistoryDownloadId === row?.objectId ||
                                          !String(row?.SignedUrl || row?.URL || "").trim()
                                        }
                                        className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-app-brand text-white transition hover:bg-app-brand/90 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400 disabled:opacity-60"
                                        title={t("download", { defaultValue: "Download" })}
                                        aria-label={t("download", { defaultValue: "Download" })}
                                      >
                                        <Icon
                                          name={activeHistoryDownloadId === row?.objectId ? "clock" : "download"}
                                          className="h-4 w-4"
                                        />
                                      </button>
                                    </div>
                                  ) : row?.objectId ? (
                                    <button
                                      type="button"
                                      onClick={() => downloadHistoryFile(row)}
                                      disabled={
                                        activeHistoryDownloadId === row?.objectId ||
                                        !String(row?.SignedUrl || row?.URL || "").trim()
                                      }
                                      className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-app-brand text-white transition hover:bg-app-brand/90 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400 disabled:opacity-60"
                                      title={t("download", { defaultValue: "Download" })}
                                      aria-label={t("download", { defaultValue: "Download" })}
                                    >
                                      <Icon
                                        name={activeHistoryDownloadId === row?.objectId ? "clock" : "download"}
                                        className="h-4 w-4"
                                      />
                                    </button>
                                  ) : (
                                    "--"
                                  )}
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr className="border-t border-app-line">
                            <td colSpan={6} className="px-3 py-4 text-center text-[12px] text-app-ink-muted">
                              {t("new-ui.dashboard.recent-sent-documents.no-results", {
                                defaultValue: "No results for your search."
                              })}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  ) : dataMode === "contacts" ? (
                    <table className="w-full table-fixed border-collapse text-left">
                      <thead className="bg-app-sand">
                        <tr>
                          <th className="w-[30%] px-2 py-1.5 text-[11px] font-semibold text-app-ink-muted">
                            {t("report-heading.Name", { defaultValue: "Name" })}
                          </th>
                          <th className="w-[35%] px-2 py-1.5 text-[11px] font-semibold text-app-ink-muted">
                            {t("report-heading.Email", { defaultValue: "Email" })}
                          </th>
                          <th className="w-[20%] px-2 py-1.5 text-[11px] font-semibold text-app-ink-muted">
                            {t("report-heading.Phone", { defaultValue: "Phone" })}
                          </th>
                          <th className="w-[15%] px-2 py-1.5 text-[11px] font-semibold text-app-ink-muted">
                            {t("action", { defaultValue: "Action" })}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredRows.length ? (
                          filteredRows.map((row, index) => (
                            <tr key={row.objectId || `${row.Email}-${index}`} className="border-t border-app-line">
                              <td className="px-2 py-1.5 text-[12px] font-medium text-app-ink">
                                {row?.Name || t("new-ui.dashboard.no-data", { defaultValue: "No data" })}
                              </td>
                              <td className="px-2 py-1.5 text-[11px] text-app-ink-muted">{row?.Email || "--"}</td>
                              <td className="px-2 py-1.5 text-[11px] text-app-ink-muted">{row?.Phone || "--"}</td>
                              <td className="px-2 py-1.5 text-[11px] text-app-ink-muted">
                                <div className="inline-flex items-center gap-2">
                                  <button
                                    type="button"
                                    onClick={() => openEditContactModal(row)}
                                    className="text-indigo-600 transition hover:text-indigo-700"
                                    title={t("edit", { defaultValue: "Edit" })}
                                  >
                                    <Icon name="edit" className="h-4 w-4" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => openDeleteContactModal(row)}
                                    className="text-red-600 transition hover:text-red-700"
                                    title={t("delete", { defaultValue: "Delete" })}
                                  >
                                    <Icon name="trash" className="h-4 w-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr className="border-t border-app-line">
                            <td colSpan={4} className="px-3 py-4 text-center text-[12px] text-app-ink-muted">
                              {t("new-ui.dashboard.recent-sent-documents.no-results", {
                                defaultValue: "No results for your search."
                              })}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  ) : isHistorySection ? (
                    <table className="w-full table-auto border-collapse text-left">
                      <thead className="bg-app-sand">
                        <tr>
                          {isSelectableHistorySection ? (
                            <th className="w-[46px] px-2 py-1.5 text-center text-[11px] font-semibold text-app-ink-muted">
                              <input
                                type="checkbox"
                                checked={areAllVisibleHistoryRowsSelected}
                                onChange={toggleSelectAllVisibleHistoryRows}
                                aria-label={t("new-ui.sections.history.select-all-drafts", {
                                  defaultValue: "Select all rows"
                                })}
                              />
                            </th>
                          ) : null}
                          {historyReportConfig.columns.includes("title") ? (
                            <th className="min-w-[220px] px-2 py-1.5 text-[11px] font-semibold text-app-ink-muted">
                              {t("report-heading.Title", { defaultValue: "Title" })}
                            </th>
                          ) : null}
                          {historyReportConfig.columns.includes("expiry") ? (
                            <th className="min-w-[130px] px-2 py-1.5 text-[11px] font-semibold text-app-ink-muted">
                              {t("expiry-date", { defaultValue: "Expiry date" })}
                            </th>
                          ) : null}
                          {historyReportConfig.columns.includes("note") ? (
                            <th className="min-w-[150px] px-2 py-1.5 text-[11px] font-semibold text-app-ink-muted">
                              {t("report-heading.Note", { defaultValue: "Note" })}
                            </th>
                          ) : null}
                          {historyReportConfig.columns.includes("reason") ? (
                            <th className="min-w-[150px] px-2 py-1.5 text-[11px] font-semibold text-app-ink-muted">
                              {t("Reason", { defaultValue: "Reason" })}
                            </th>
                          ) : null}
                          {historyReportConfig.columns.includes("folder") ? (
                            <th className="min-w-[130px] px-2 py-1.5 text-[11px] font-semibold text-app-ink-muted">
                              {t("report-heading.Folder", { defaultValue: "Folder" })}
                            </th>
                          ) : null}
                          {historyReportConfig.columns.includes("file") ? (
                            <th className="min-w-[110px] px-2 py-1.5 text-[11px] font-semibold text-app-ink-muted">
                              {t("report-heading.File", { defaultValue: "File" })}
                            </th>
                          ) : null}
                          {historyReportConfig.columns.includes("owner") ? (
                            <th className="min-w-[140px] px-2 py-1.5 text-[11px] font-semibold text-app-ink-muted">
                              {t("report-heading.Owner", { defaultValue: "Owner" })}
                            </th>
                          ) : null}
                          {historyReportConfig.columns.includes("verification") ? (
                            <th className="min-w-[140px] px-2 py-1.5 text-[11px] font-semibold text-app-ink-muted">
                              {t("new-ui.sections.history.verification-column", {
                                defaultValue: "ID/Biometría"
                              })}
                            </th>
                          ) : null}
                          {historyReportConfig.columns.includes("signers") ? (
                            <th className="min-w-[120px] px-2 py-1.5 text-[11px] font-semibold text-app-ink-muted">
                              {isDeclinedHistorySection
                                ? t("decline-by", { defaultValue: "Declined/revoked by" })
                                : t("report-heading.Signers", { defaultValue: "Signers" })}
                            </th>
                          ) : null}
                          {historyReportConfig.columns.includes("signersStatus") ? (
                            <th className="min-w-[260px] px-2 py-1.5 text-[11px] font-semibold text-app-ink-muted">
                              {t("report-heading.Signers", { defaultValue: "Signers" })}
                            </th>
                          ) : null}
                          <th
                            className={`px-2 py-1.5 text-[11px] font-semibold text-app-ink-muted ${
                              isInProgressHistorySection ? "min-w-[186px]" : "min-w-[100px]"
                            }`}
                          >
                            {t("action", { defaultValue: "Action" })}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredRows.length ? (
                          filteredRows.map((row, index) => {
                            const docTitle = getDocTitle(row, index, t);
                            const actionPath = resolveHistoryActionPath(row);
                            const needYourSignAccessState = isNeedYourSignHistorySection
                              ? getNeedYourSignAccessState(
                                  row,
                                  currentSessionUserId,
                                  currentSessionUserEmail
                                )
                              : null;
                            const blockedSignLabel =
                              needYourSignAccessState?.alreadySigned
                                ? t("already-signed-message", {
                                    defaultValue: "You have already signed this document."
                                  })
                                : t("new-ui.history.need-your-sign.waiting-turn-short", {
                                    defaultValue: "Esperando firma anterior"
                                  });
                            const signerStatusRows = mapSignerStatusRows(row);
                            const declineByName = getDeclinedByName(row);
                            const declineByEmail = normalizeEmailAddress(
                              row?.DeclineBy?.Email || row?.DeclineBy?.email || ""
                            );
                            const declinedSignerRows = signerStatusRows.filter(
                              (signerRow) => String(signerRow?.activity || "").trim().toUpperCase() === "DECLINED"
                            );
                            const hasDeclineBySignerAlready = declinedSignerRows.some((signerRow) => {
                              const signerEmail = normalizeEmailAddress(signerRow?.email || "");
                              const signerName = String(signerRow?.name || "").trim().toLowerCase();
                              const declineByNameNormalized = declineByName.toLowerCase();
                              return (
                                (declineByEmail && signerEmail && declineByEmail === signerEmail) ||
                                (declineByNameNormalized &&
                                  signerName &&
                                  declineByNameNormalized === signerName)
                              );
                            });
                            const declinedSignerRowsForDisplay =
                              declineByName && !hasDeclineBySignerAlready
                                ? [
                                    {
                                      activity: "DECLINED",
                                      name: declineByName,
                                      email: row?.DeclineBy?.Email || row?.DeclineBy?.email || ""
                                    },
                                    ...declinedSignerRows
                                  ]
                                : declinedSignerRows;
                            const isExpanded = Boolean(signerStatusExpanded[row?.objectId]);
                            const visibleSignerRows = isExpanded ? signerStatusRows : signerStatusRows.slice(0, 3);
                            const rowId = String(row?.objectId || "").trim();
                            const isSelectedHistoryRow = rowId ? selectedHistoryIds.includes(rowId) : false;
                            const isRowMenuOpen = rowId && activeInProgressMenuDocId === rowId;
                            const createdAtDate = parseDocDate(row?.createdAt);
                            const expiryDate = parseDocDate(row?.ExpiryDate);
                            const showCreatedMetaDate = isNeedYourSignHistorySection || isInProgressHistorySection;
                            const metaDate = showCreatedMetaDate ? createdAtDate : expiryDate;
                            return (
                              <tr key={row.objectId || `${docTitle}-${index}`} className="border-t border-app-line align-top">
                                {isSelectableHistorySection ? (
                                  <td className="px-2 py-1.5 text-center">
                                    {rowId ? (
                                      <input
                                        type="checkbox"
                                        checked={isSelectedHistoryRow}
                                        onChange={() => toggleHistorySelection(rowId)}
                                        aria-label={t("new-ui.sections.history.select-draft", {
                                          defaultValue: "Select row"
                                        })}
                                      />
                                    ) : null}
                                  </td>
                                ) : null}
                                {historyReportConfig.columns.includes("title") ? (
                                  <td className="px-2 py-1.5 text-[12px] text-app-ink">
                                    <p className="m-0 max-w-[300px] truncate font-medium" title={docTitle}>
                                      {docTitle}
                                    </p>
                                    {metaDate ? (
                                      <p className="m-0 mt-0.5 text-[10px] text-app-ink-muted">
                                        {showCreatedMetaDate
                                          ? t("report-heading.created-date", {
                                              defaultValue: "Fecha de creación"
                                            })
                                          : t("expiry-date", { defaultValue: "Expiry date" })}
                                        : {formatDate(metaDate, i18n, true)}
                                      </p>
                                    ) : null}
                                  </td>
                                ) : null}
                                {historyReportConfig.columns.includes("expiry") ? (
                                  <td className="px-2 py-1.5 text-[11px] text-app-ink-muted">
                                    {row?.ExpiryDate ? formatDate(parseDocDate(row?.ExpiryDate), i18n, true) : "--"}
                                  </td>
                                ) : null}
                                {historyReportConfig.columns.includes("note") ? (
                                  <td className="px-2 py-1.5 text-[11px] text-app-ink-muted">
                                    <p className="m-0 max-w-[220px] truncate" title={row?.Note || "--"}>
                                      {row?.Note || "--"}
                                    </p>
                                  </td>
                                ) : null}
                                {historyReportConfig.columns.includes("reason") ? (
                                  <td className="px-2 py-1.5 text-[11px] text-app-ink-muted">
                                    <p className="m-0 max-w-[220px] truncate" title={row?.DeclineReason || "--"}>
                                      {row?.DeclineReason || "--"}
                                    </p>
                                  </td>
                                ) : null}
                                {historyReportConfig.columns.includes("folder") ? (
                                  <td className="px-2 py-1.5 text-[11px] text-app-ink-muted">
                                    {historyFolderLabel(row)}
                                  </td>
                                ) : null}
                                {historyReportConfig.columns.includes("file") ? (
                                  <td className="px-2 py-1.5 text-[11px] text-app-ink-muted">
                                    {row?.URL || row?.SignedUrl ? (
                                      <button
                                        type="button"
                                        onClick={() => downloadHistoryFile(row)}
                                        className="text-indigo-600 hover:text-indigo-700 hover:underline"
                                        disabled={activeHistoryDownloadId === row?.objectId}
                                      >
                                        {activeHistoryDownloadId === row?.objectId
                                          ? t("loading", { defaultValue: "Loading..." })
                                          : t("download", { defaultValue: "Download" })}
                                      </button>
                                    ) : (
                                      "--"
                                    )}
                                  </td>
                                ) : null}
                                {historyReportConfig.columns.includes("owner") ? (
                                  <td
                                    className="px-2 py-1.5 text-[11px] text-app-ink-muted"
                                    title={getOwnerName(row, t)}
                                  >
                                    <p className="m-0 max-w-[180px] truncate">{getOwnerName(row, t)}</p>
                                  </td>
                                ) : null}
                                {historyReportConfig.columns.includes("verification") ? (
                                  <td className="px-2 py-1.5 text-[11px] text-app-ink-muted">
                                    {isIdentityVerificationRequestedForDocument(row) ? (
                                      <span className="inline-flex rounded-full border border-amber-300 bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
                                        {t("new-ui.sections.history.verification-required", {
                                          defaultValue: "Requerida"
                                        })}
                                      </span>
                                    ) : (
                                      <span className="inline-flex rounded-full border border-slate-300 bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-700">
                                        {t("new-ui.sections.history.verification-not-required", {
                                          defaultValue: "No requerida"
                                        })}
                                      </span>
                                    )}
                                  </td>
                                ) : null}
                                {historyReportConfig.columns.includes("signers") ? (
                                  <td className="px-2 py-1.5 text-[11px] text-app-ink-muted">
                                    {isDeclinedHistorySection ? (
                                      declinedSignerRowsForDisplay.length ? (
                                        <div className="grid grid-cols-1 gap-1">
                                          {declinedSignerRowsForDisplay.map((signerRow, signerIndex) => {
                                            const signerLabel = signerRow?.name || signerRow?.email || "--";
                                            return (
                                              <div
                                                key={`${signerRow.email || signerLabel}-${signerIndex}`}
                                                className="inline-flex items-center gap-1.5"
                                              >
                                                <span className="inline-flex min-w-[62px] justify-center rounded-full border border-rose-300 bg-rose-100 px-2 py-0.5 text-[10px] font-semibold text-rose-700">
                                                  {t("new-ui.sections.history.signer-status.declined", {
                                                    defaultValue: "Declined"
                                                  })}
                                                </span>
                                                <span className="max-w-[190px] truncate text-[11px]" title={signerLabel}>
                                                  {signerLabel}
                                                </span>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      ) : (
                                        "--"
                                      )
                                    ) : (
                                      signerStatusRows.length ? (
                                        <button
                                          type="button"
                                          onClick={() => openSignersModal(row)}
                                          className="text-indigo-600 hover:text-indigo-700 hover:underline"
                                        >
                                          {t("view", { defaultValue: "View" })}
                                        </button>
                                      ) : (
                                        "--"
                                      )
                                    )}
                                  </td>
                                ) : null}
                                {historyReportConfig.columns.includes("signersStatus") ? (
                                  <td className="px-2 py-1.5 text-[11px] text-app-ink-muted">
                                    {visibleSignerRows.length ? (
                                      <div className="grid grid-cols-1 gap-1">
                                        {visibleSignerRows.map((signerRow, signerIndex) => (
                                          <div
                                            key={`${signerRow.email}-${signerIndex}`}
                                            className="inline-flex items-center gap-1.5"
                                          >
                                            <span
                                              className={`inline-flex min-w-[62px] justify-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${getSignerActivityTone(
                                                signerRow.activity
                                              )}`}
                                            >
                                              {getSignerActivityLabel(signerRow.activity)}
                                            </span>
                                            <span className="max-w-[190px] truncate text-[11px]" title={signerRow.email}>
                                              {signerRow.email}
                                            </span>
                                          </div>
                                        ))}
                                        {signerStatusRows.length > 3 ? (
                                          <button
                                            type="button"
                                            onClick={() =>
                                              setSignerStatusExpanded((current) => ({
                                                ...current,
                                                [row?.objectId]: !current[row?.objectId]
                                              }))
                                            }
                                            className="w-fit text-[10px] text-indigo-600 hover:text-indigo-700 hover:underline"
                                          >
                                            {isExpanded
                                              ? t("new-ui.sections.history.hide", { defaultValue: "Hide" })
                                              : t("new-ui.sections.history.show-more", { defaultValue: "Show more" })}
                                          </button>
                                        ) : null}
                                      </div>
                                    ) : (
                                      "--"
                                    )}
                                  </td>
                                ) : null}
                                <td className="px-2 py-1.5 text-[11px] text-app-ink-muted">
                                  {row?.objectId ? (
                                    isInProgressHistorySection ? (
                                      <div
                                        className="relative inline-flex items-center gap-1.5"
                                        data-inprogress-menu-root="true"
                                      >
                                        <button
                                          type="button"
                                          onClick={() => openInProgressActionModal("resend", row)}
                                          className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-app-brand text-white transition hover:bg-app-brand/90"
                                          title={t("resend", { defaultValue: "Resend" })}
                                        >
                                          <Icon name="mail" className="h-4 w-4" />
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => openNeedYourSignDetailsModal(row)}
                                          className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-app-line bg-white text-app-ink transition hover:bg-slate-50"
                                          title={t("new-ui.history.need-your-sign.view-details", {
                                            defaultValue: "View document details"
                                          })}
                                          aria-label={t("new-ui.history.need-your-sign.view-details", {
                                            defaultValue: "View document details"
                                          })}
                                        >
                                          <Icon name="eye" className="h-4 w-4" />
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => openInProgressActionMenu(rowId)}
                                          className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-app-line bg-white text-app-ink transition hover:bg-slate-50"
                                          title={t("new-ui.history.more-actions", { defaultValue: "More actions" })}
                                        >
                                          <Icon name="moreVertical" className="h-4 w-4" />
                                        </button>
                                        {isRowMenuOpen ? (
                                          <div className="absolute right-0 top-9 z-[60] w-56 rounded-xl border border-app-line bg-white p-1.5 shadow-soft">
                                            <button
                                              type="button"
                                              onClick={() => openInProgressActionModal("resend", row)}
                                              className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-xs text-app-ink transition hover:bg-slate-50"
                                            >
                                              <Icon name="mail" className="h-4 w-4" />
                                              <span>{t("resend", { defaultValue: "Resend" })}</span>
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() => openInProgressActionModal("rename", row)}
                                              className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-xs text-app-ink transition hover:bg-slate-50"
                                            >
                                              <Icon name="edit" className="h-4 w-4" />
                                              <span>{t("rename", { defaultValue: "Rename" })}</span>
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() => openInProgressActionModal("revoke", row)}
                                              className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-xs text-app-ink transition hover:bg-slate-50"
                                            >
                                              <Icon name="close" className="h-4 w-4" />
                                              <span>{t("revoke-document", { defaultValue: "Revoke" })}</span>
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() => openInProgressActionModal("saveastemplate", row)}
                                              className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-xs text-app-ink transition hover:bg-slate-50"
                                            >
                                              <Icon name="templateDoc" className="h-4 w-4" />
                                              <span>{t("btnLabel.Save as template", { defaultValue: "Save as template" })}</span>
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() => openInProgressActionModal("delete", row)}
                                              className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-xs text-red-600 transition hover:bg-red-50"
                                            >
                                              <Icon name="trash" className="h-4 w-4" />
                                              <span>{t("archive", { defaultValue: "Archive" })}</span>
                                            </button>
                                          </div>
                                        ) : null}
                                      </div>
                                    ) : isNeedYourSignHistorySection ? (
                                      <div className="inline-flex items-center gap-1.5">
                                        <button
                                          type="button"
                                          onClick={() => actionPath && openHistoryDocumentAction(row, actionPath)}
                                          disabled={!actionPath}
                                          className={`inline-flex h-8 w-8 items-center justify-center rounded-full transition ${
                                            actionPath
                                              ? "bg-app-brand text-white hover:bg-app-brand/90"
                                              : "cursor-not-allowed bg-slate-200 text-slate-400 opacity-60"
                                          }`}
                                          title={
                                            actionPath
                                              ? t("btnLabel.Sign", { defaultValue: "Sign" })
                                              : blockedSignLabel
                                          }
                                          aria-label={
                                            actionPath
                                              ? t("btnLabel.Sign", { defaultValue: "Sign" })
                                              : blockedSignLabel
                                          }
                                        >
                                          <Icon name="edit" className="h-4 w-4" />
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => openNeedYourSignDetailsModal(row)}
                                          className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-app-line bg-white text-app-ink transition hover:bg-slate-50"
                                          title={t("new-ui.history.need-your-sign.view-details", {
                                            defaultValue: "View document details"
                                          })}
                                          aria-label={t("new-ui.history.need-your-sign.view-details", {
                                            defaultValue: "View document details"
                                          })}
                                        >
                                          <Icon name="eye" className="h-4 w-4" />
                                        </button>
                                      </div>
                                    ) : isDeclinedHistorySection ? (
                                      <button
                                        type="button"
                                        onClick={() => downloadNeedYourSignOriginalFile(row)}
                                        disabled={
                                          activeHistoryDownloadId === row?.objectId ||
                                          !String(row?.URL || "").trim()
                                        }
                                        className={`inline-flex h-8 w-8 items-center justify-center rounded-full border transition ${
                                          String(row?.URL || "").trim()
                                            ? "border-app-line bg-white text-app-ink hover:bg-slate-50"
                                            : "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400 opacity-60"
                                        }`}
                                        title={t("new-ui.history.declined.download-original", {
                                          defaultValue: "Download original document"
                                        })}
                                        aria-label={t("new-ui.history.declined.download-original", {
                                          defaultValue: "Download original document"
                                        })}
                                      >
                                        <Icon
                                          name={activeHistoryDownloadId === row?.objectId ? "clock" : "eye"}
                                          className="h-4 w-4"
                                        />
                                      </button>
                                    ) : isCompletedHistorySection ? (
                                      <div className="inline-flex items-center gap-1.5">
                                        <button
                                          type="button"
                                          onClick={() => openCompletedSummaryModal(row)}
                                          className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-app-line bg-white text-app-ink transition hover:bg-slate-50"
                                          title={t("new-ui.history.need-your-sign.view-details", {
                                            defaultValue: "View document details"
                                          })}
                                          aria-label={t("new-ui.history.need-your-sign.view-details", {
                                            defaultValue: "View document details"
                                          })}
                                        >
                                          <Icon name="eye" className="h-4 w-4" />
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => downloadHistoryFile(row)}
                                          disabled={
                                            activeHistoryDownloadId === row?.objectId ||
                                            !String(row?.SignedUrl || row?.URL || "").trim()
                                          }
                                          className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-app-brand text-white transition hover:bg-app-brand/90 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400 disabled:opacity-60"
                                          title={t("download", { defaultValue: "Download" })}
                                          aria-label={t("download", { defaultValue: "Download" })}
                                        >
                                          <Icon name={activeHistoryDownloadId === row?.objectId ? "clock" : "download"} className="h-4 w-4" />
                                        </button>
                                      </div>
                                    ) : (
                                      actionPath ? (
                                        <button
                                          type="button"
                                          onClick={() => openHistoryDocumentAction(row, actionPath)}
                                          className="text-indigo-600 hover:text-indigo-700 hover:underline"
                                        >
                                          {t(historyReportConfig.actionLabelKey, {
                                            defaultValue: historyReportConfig.actionLabelFallback
                                          })}
                                        </button>
                                      ) : (
                                        "--"
                                      )
                                    )
                                  ) : (
                                    "--"
                                  )}
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr className="border-t border-app-line">
                            <td
                              colSpan={historyReportConfig.columns.length + 1 + (isSelectableHistorySection ? 1 : 0)}
                              className="px-3 py-4 text-center text-[12px] text-app-ink-muted"
                            >
                              {t("new-ui.dashboard.recent-sent-documents.no-results", {
                                defaultValue: "No results for your search."
                              })}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  ) : (
                    <table className="w-full table-fixed border-collapse text-left">
                      {(() => {
                        const showStatusColumn = dataMode !== "templates";
                        const showTemplateSelection = dataMode === "templates" && canManageTemplates;
                        return (
                          <>
                            <thead className="bg-app-sand">
                              <tr>
                                {showTemplateSelection ? (
                                  <th className="w-[8%] px-2 py-1.5 text-[11px] font-semibold text-app-ink-muted">
                                    <input
                                      type="checkbox"
                                      checked={areAllVisibleTemplateRowsSelected}
                                      onChange={toggleSelectAllVisibleTemplateRows}
                                      disabled={!visibleTemplateRowIds.length || isDeletingSelectedTemplates}
                                      aria-label={t("new-ui.templates.select-all", {
                                        defaultValue: "Select all templates"
                                      })}
                                    />
                                  </th>
                                ) : null}
                                <th className="w-[30%] px-2 py-1.5 text-[11px] font-semibold text-app-ink-muted">
                                  {t("report-heading.Title", { defaultValue: "Title" })}
                                </th>
                                {!showStatusColumn ? (
                                  <th className="w-[14%] px-2 py-1.5 text-[11px] font-semibold text-app-ink-muted">
                                    ID
                                  </th>
                                ) : null}
                                {showStatusColumn ? (
                                  <th className="w-[18%] px-2 py-1.5 text-[11px] font-semibold text-app-ink-muted">
                                    {t("report-heading.Status", { defaultValue: "Status" })}
                                  </th>
                                ) : null}
                                <th className="w-[17%] px-2 py-1.5 text-[11px] font-semibold text-app-ink-muted">
                                  {showStatusColumn
                                    ? t("new-ui.dashboard.rows.last-activity", {
                                        defaultValue: "Last activity"
                                      })
                                    : t("new-ui.templates.last-updated", {
                                        defaultValue: "Última actualización"
                                      })}
                                </th>
                                <th className="w-[19%] px-2 py-1.5 text-[11px] font-semibold text-app-ink-muted">
                                  {t("report-heading.Owner", { defaultValue: "Owner" })}
                                </th>
                                <th className="w-[10%] px-2 py-1.5 text-[11px] font-semibold text-app-ink-muted">
                                  {t("action", { defaultValue: "Action" })}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredRows.map((row, index) => {
                                const status = showStatusColumn ? getDocumentStatus(row, t) : null;
                                const docTitle = getDocTitle(row, index, t);
                                const updatedDate = parseDocDate(row?.updatedAt);
                                const templateRowId = String(row?.objectId || "").trim();
                                const isPreparingTemplate = activeTemplateUseId === templateRowId;
                                const isEditingTemplate = activeTemplateEditId === templateRowId;
                                const isGeneratingTemplateJson = activeTemplateJsonId === templateRowId;
                                const isRenamingTemplate =
                                  isTemplateRenameSaving &&
                                  String(templateRenameTargetRow?.objectId || "").trim() === templateRowId;
                                const actionPath =
                                  dataMode === "templates"
                                    ? `/template/${row.objectId}`
                                    : resolveDocumentOpenPath(row);
                                const signers = getSignerNames(row, t);

                                return (
                                  <tr key={row.objectId || `${docTitle}-${index}`} className="border-t border-app-line align-top">
                                    {showTemplateSelection ? (
                                      <td className="px-2 py-1.5 text-[11px] text-app-ink-muted">
                                        <input
                                          type="checkbox"
                                          checked={selectedTemplateIds.includes(templateRowId)}
                                          onChange={() => toggleTemplateSelection(templateRowId)}
                                          disabled={!templateRowId || isDeletingSelectedTemplates}
                                          aria-label={t("new-ui.templates.select-row", {
                                            defaultValue: "Select template"
                                          })}
                                        />
                                      </td>
                                    ) : null}
                                    <td className="px-2 py-1.5 text-[12px] leading-tight">
                                      <div className="flex items-start gap-2">
                                        {dataMode === "templates" && canManageTemplates ? (
                                          <button
                                            type="button"
                                            onClick={() => openTemplateRenameModal(row)}
                                            disabled={
                                              isEditingTemplate ||
                                              isPreparingTemplate ||
                                              isGeneratingTemplateJson ||
                                              isRenamingTemplate
                                            }
                                            className="dana-action-tooltip-target mt-[1px] inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-amber-50 text-amber-600 transition hover:bg-amber-100 hover:text-amber-700 disabled:cursor-not-allowed disabled:opacity-60"
                                            title={t("rename", { defaultValue: "Rename" })}
                                            aria-label={t("rename", { defaultValue: "Rename" })}
                                            data-tooltip={t("rename", { defaultValue: "Rename" })}
                                          >
                                            <Icon name={isRenamingTemplate ? "clock" : "edit"} className="h-3.5 w-3.5" />
                                          </button>
                                        ) : null}
                                        <div className="min-w-0 grow">
                                          <p className="m-0 truncate font-medium text-app-ink" title={docTitle}>
                                            {docTitle}
                                          </p>
                                          {signers.length ? (
                                            <p
                                              className="m-0 mt-0.5 truncate text-[10px] text-app-ink-muted"
                                              title={signers.join(", ")}
                                            >
                                              {t("report-heading.Signers", { defaultValue: "Signers" })}: {signers.join(", ")}
                                            </p>
                                          ) : null}
                                        </div>
                                      </div>
                                    </td>
                                    {!showStatusColumn ? (
                                      <td className="px-2 py-1.5 text-[11px] text-app-ink-muted font-mono">
                                        <div className="flex items-center gap-1">
                                          <span className="truncate" title={templateRowId}>{templateRowId}</span>
                                          <button
                                            type="button"
                                            onClick={() => {
                                              navigator.clipboard.writeText(templateRowId);
                                            }}
                                            className="shrink-0 inline-flex h-5 w-5 items-center justify-center rounded text-slate-400 hover:text-app-brand hover:bg-slate-100 transition"
                                            title={t("copy", { defaultValue: "Copy" })}
                                            aria-label={t("copy", { defaultValue: "Copy" })}
                                          >
                                            <Icon name="copy" className="h-3 w-3" />
                                          </button>
                                        </div>
                                      </td>
                                    ) : null}
                                    {showStatusColumn ? (
                                      <td className="px-2 py-1.5 text-[11px] text-app-ink-muted">
                                        <span className={`inline-flex rounded-full border px-2 py-1 ${status.toneClass}`}>
                                          {status.label}
                                        </span>
                                      </td>
                                    ) : null}
                                    <td className="px-2 py-1.5 text-[11px] text-app-ink-muted">
                                      {formatDate(updatedDate, i18n, true)}
                                    </td>
                                    <td className="px-2 py-1.5 text-[11px] text-app-ink-muted truncate" title={getOwnerName(row, t)}>
                                      {getOwnerName(row, t)}
                                    </td>
                                    <td className="px-2 py-1.5 text-[11px] text-app-ink-muted">
                                      {row?.objectId ? (
                                        dataMode === "templates" ? (
                                          <div className="flex items-center gap-2">
                                            {(() => {
                                              const editLabel = isEditingTemplate
                                                ? t("new-ui.templates.preparing", { defaultValue: "Preparing..." })
                                                : t("btnLabel.Edit", { defaultValue: "Edit" });
                                              const useTemplateLabel = isPreparingTemplate
                                                ? t("new-ui.templates.preparing", { defaultValue: "Preparing..." })
                                                : t("new-ui.templates.use-template", { defaultValue: "Use template" });
                                              const jsonLabel = isGeneratingTemplateJson
                                                ? t("new-ui.templates.json.generating", { defaultValue: "Generating..." })
                                                : "JSON";

                                              return (
                                                <>
                                                  {canManageTemplates ? (
                                                    <button
                                                      type="button"
                                                      onClick={() => openTemplateAsDraft(row, { templateEdit: true })}
                                                      disabled={isEditingTemplate || isPreparingTemplate}
                                                      className="dana-action-tooltip-target inline-flex h-8 w-8 items-center justify-center rounded-full bg-indigo-50 text-indigo-600 transition hover:bg-indigo-100 hover:text-indigo-700"
                                                      title={editLabel}
                                                      aria-label={editLabel}
                                                      data-tooltip={editLabel}
                                                    >
                                                      <Icon name={isEditingTemplate ? "clock" : "edit"} className="h-4 w-4" />
                                                    </button>
                                                  ) : null}
                                                  <button
                                                    type="button"
                                                    onClick={() => openTemplateAsDraft(row)}
                                                    disabled={isPreparingTemplate || isEditingTemplate}
                                                    className="dana-action-tooltip-target inline-flex h-8 w-8 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 transition hover:bg-emerald-100 hover:text-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                                                    title={useTemplateLabel}
                                                    aria-label={useTemplateLabel}
                                                    data-tooltip={useTemplateLabel}
                                                  >
                                                    <Icon
                                                      name={isPreparingTemplate ? "clock" : "templateDoc"}
                                                      className="h-4 w-4"
                                                    />
                                                  </button>
                                                  {canManageApiTokens ? (
                                                    <>
                                                    <button
                                                      type="button"
                                                      onClick={() => exportTemplateJsonPayload(row)}
                                                      disabled={isGeneratingTemplateJson}
                                                      className="dana-action-tooltip-target inline-flex h-8 w-8 items-center justify-center rounded-full bg-sky-50 text-sky-600 transition hover:bg-sky-100 hover:text-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
                                                      title={jsonLabel}
                                                      aria-label={jsonLabel}
                                                      data-tooltip={jsonLabel}
                                                    >
                                                      <Icon
                                                        name={isGeneratingTemplateJson ? "clock" : "copy"}
                                                        className="h-4 w-4"
                                                      />
                                                    </button>
                                                    <button
                                                      type="button"
                                                      onClick={() => copyTemplateApiPayload(row)}
                                                      className="dana-action-tooltip-target inline-flex h-8 w-8 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 transition hover:bg-emerald-100 hover:text-emerald-700"
                                                      title="API"
                                                      aria-label="Copy API JSON"
                                                      data-tooltip="API"
                                                    >
                                                      <Icon name="key" className="h-4 w-4" />
                                                    </button>
                                                    </>
                                                  ) : null}
                                                </>
                                              );
                                            })()}
                                          </div>
                                        ) : isDeclinedHistorySection ? (
                                          <button
                                            type="button"
                                            onClick={() => downloadNeedYourSignOriginalFile(row)}
                                            disabled={
                                              activeHistoryDownloadId === row?.objectId ||
                                              !String(row?.URL || "").trim()
                                            }
                                            className={`inline-flex h-8 w-8 items-center justify-center rounded-full border transition ${
                                              String(row?.URL || "").trim()
                                                ? "border-app-line bg-white text-app-ink hover:bg-slate-50"
                                                : "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400 opacity-60"
                                            }`}
                                            title={t("new-ui.history.declined.download-original", {
                                              defaultValue: "Download original document"
                                            })}
                                            aria-label={t("new-ui.history.declined.download-original", {
                                              defaultValue: "Download original document"
                                            })}
                                          >
                                            <Icon
                                              name={activeHistoryDownloadId === row?.objectId ? "clock" : "eye"}
                                              className="h-4 w-4"
                                            />
                                          </button>
                                        ) : isCompletedHistorySection ? (
                                          <div className="inline-flex items-center gap-1.5">
                                            <button
                                              type="button"
                                              onClick={() => openCompletedSummaryModal(row)}
                                              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-app-line bg-white text-app-ink transition hover:bg-slate-50"
                                              title={t("new-ui.history.need-your-sign.view-details", {
                                                defaultValue: "View document details"
                                              })}
                                              aria-label={t("new-ui.history.need-your-sign.view-details", {
                                                defaultValue: "View document details"
                                              })}
                                            >
                                              <Icon name="eye" className="h-4 w-4" />
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() => downloadHistoryFile(row)}
                                              disabled={
                                                activeHistoryDownloadId === row?.objectId ||
                                                !String(row?.SignedUrl || row?.URL || "").trim()
                                              }
                                              className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-app-brand text-white transition hover:bg-app-brand/90 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400 disabled:opacity-60"
                                              title={t("download", { defaultValue: "Download" })}
                                              aria-label={t("download", { defaultValue: "Download" })}
                                            >
                                              <Icon
                                                name={activeHistoryDownloadId === row?.objectId ? "clock" : "download"}
                                                className="h-4 w-4"
                                              />
                                            </button>
                                          </div>
                                        ) : (
                                          <Link to={actionPath} className="text-indigo-600 hover:text-indigo-700 hover:underline">
                                            {t("btnLabel.View", { defaultValue: "Open" })}
                                          </Link>
                                        )
                                      ) : (
                                        "--"
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </>
                        );
                      })()}
                    </table>
                  )}
                </div>
              ) : (
                <p className="dana-muted">
                  {t("new-ui.dashboard.recent-sent-documents.no-results", {
                    defaultValue: "No results for your search."
                  })}
                </p>
              )
            ) : null}

            {!isLoadingData && dataMode === "drive" ? (
              <div className="mt-3 flex items-center justify-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={goToPreviousDrivePage}
                  disabled={drivePage <= 1 || isLoadingData}
                >
                  {t("previous", { defaultValue: "Previous" })}
                </Button>
                <span className="text-xs font-medium text-app-ink-muted">
                  {t("new-ui.sections.drive.page-label", {
                    defaultValue: "Page {{page}}",
                    page: drivePage
                  })}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={goToNextDrivePage}
                  disabled={!driveHasMore || isLoadingData}
                >
                  {t("next", { defaultValue: "Next" })}
                </Button>
              </div>
            ) : null}

            {!isLoadingData && isHistorySection ? (
              <div className="mt-3 flex items-center justify-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={goToPreviousHistoryPage}
                  disabled={historyPage <= 1 || isLoadingData}
                >
                  {t("previous", { defaultValue: "Previous" })}
                </Button>
                <span className="text-xs font-medium text-app-ink-muted">
                  {t("new-ui.sections.history.page-label", {
                    defaultValue: "Page {{page}}",
                    page: historyPage
                  })}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={goToNextHistoryPage}
                  disabled={!historyHasMore || isLoadingData}
                >
                  {t("next", { defaultValue: "Next" })}
                </Button>
              </div>
            ) : null}
          </Card>
        ) : null}

        {showWorkspaceInfoCards ? (
          <Card className="animate-fade-up [animation-delay:50ms]">
            <div className="dana-panel-head">
              <h2>{t("new-ui.sections.workspace.actions-title", { defaultValue: "Module actions" })}</h2>
            </div>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">
              {sectionMeta.links
                .filter((item) => canManageTemplates || item.to !== "/form/template")
                .map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className="rounded-xl border border-app-line bg-white px-3 py-3 text-app-ink no-underline transition hover:border-indigo-200 hover:bg-indigo-50/30"
                >
                  <div className="mb-2 inline-flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100 text-indigo-600">
                    <Icon name={routeToIcon(item.to)} />
                  </div>
                  <p className="m-0 text-sm font-semibold">
                    {t(item.labelKey, { defaultValue: item.fallbackLabel })}
                  </p>
                  <p className="m-0 mt-1 text-[11px] text-app-ink-muted">{item.to}</p>
                </Link>
                ))}
            </div>
          </Card>
        ) : null}

        {showWorkspaceInfoCards ? (
          <Card className="animate-fade-up [animation-delay:90ms]">
            <div className="dana-panel-head">
              <h2>{t("new-ui.sections.app-navigation", { defaultValue: "Application navigation" })}</h2>
              <input
                type="search"
                value={routeQuery}
                onChange={(event) => setRouteQuery(event.target.value)}
                placeholder={t("new-ui.sections.workspace.search-routes", { defaultValue: "Search routes" })}
                className="w-[170px] sm:w-[230px] rounded-lg border border-app-line bg-white px-2.5 py-1 text-[11px] text-app-ink outline-none transition focus:border-app-brand focus:ring-2 focus:ring-app-brand/20"
              />
            </div>
            <div className="dana-link-grid">
              {filteredRoutes.map((item) => (
                <Link key={`${item.group}-${item.to}`} to={item.to} className="dana-link-row">
                  <span className="dana-link-row-icon">
                    <Icon name={item.icon} />
                  </span>
                  <span>{item.label}</span>
                  <small>{item.group}</small>
                </Link>
              ))}
              {!filteredRoutes.length ? (
                <p className="m-0 text-xs text-app-ink-muted">
                  {t("new-ui.sections.workspace.no-routes", { defaultValue: "No routes match your search." })}
                </p>
              ) : null}
            </div>
          </Card>
        ) : null}

        {showWorkspaceInfoCards ? (
          <Card className="animate-fade-up [animation-delay:120ms]">
            <div className="dana-panel-head">
              <h2>{t("new-ui.sections.compatibility.title", { defaultValue: "Compatibility" })}</h2>
            </div>
            <p className="ui-copy">
              {t("new-ui.sections.compatibility.description", {
                defaultValue:
                  "If you need the legacy implementation while we finish the internal redesign, you can open this same route in the previous frontend."
              })}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button onClick={openLegacyRoute}>
                {t("new-ui.sections.compatibility.open-legacy", { defaultValue: "Open this route in legacy" })}
              </Button>
              <Link to="/dashboard/35KBoSgoAK">
                <Button variant="ghost">
                  {t("new-ui.sections.back-dashboard", { defaultValue: "Back to dashboard" })}
                </Button>
              </Link>
            </div>
          </Card>
        ) : null}
      </div>

      <Modal
        isOpen={isContactImportModalOpen}
        title={t("bulk-import", { defaultValue: "Bulk import" })}
        onClose={closeContactImportModal}
        closeIconOnly
        closeLabel={t("close", { defaultValue: "Close" })}
      >
        <div className="grid grid-cols-1 gap-3">
          <label htmlFor="contacts-import-file" className="block text-xs font-medium text-app-ink">
            {t("contacts-file", { defaultValue: "Contacts file (xlsx, csv)" })}
            <span className="ml-1 text-app-danger">*</span>
          </label>
          <input
            id="contacts-import-file"
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={handleContactImportFile}
            className="w-full rounded-lg border border-app-line bg-white px-3 py-2 text-xs text-app-ink file:mr-3 file:rounded-md file:border-0 file:bg-app-brand file:px-3 file:py-1 file:text-xs file:font-semibold file:text-white hover:file:bg-app-brand/90"
            disabled={isImportingContacts || isParsingContactImport}
          />

          <div className="grid grid-cols-1 gap-1 text-xs text-app-ink-muted sm:grid-cols-2">
            <p className="m-0">
              {t("new-ui.contacts.import.total-found", {
                defaultValue: "Total records found: {{count}}",
                count: contactImportRows.length
              })}
            </p>
            <p className="m-0">
              {t("new-ui.contacts.import.invalid-found", {
                defaultValue: "Invalid records found: {{count}}",
                count: contactImportInvalidCount
              })}
            </p>
          </div>

          {isParsingContactImport ? (
            <div className="flex items-center gap-2 rounded-lg border border-app-line bg-slate-50 px-3 py-2 text-xs text-app-ink-muted">
              <span
                className="inline-flex h-4 w-4 animate-spin rounded-full border-2 border-app-brand/25 border-t-app-brand"
                aria-hidden="true"
              />
              <span>{t("loading", { defaultValue: "Loading..." })}</span>
            </div>
          ) : null}

          {contactImportRows.length ? (
            <div className="overflow-hidden rounded-lg border border-app-line">
              <div className="max-h-[240px] overflow-auto">
                <table className="w-full table-fixed border-collapse text-left">
                  <thead className="bg-app-sand">
                    <tr>
                      <th className="w-[34%] px-2 py-1.5 text-[11px] font-semibold text-app-ink-muted">
                        {t("report-heading.Name", { defaultValue: "Name" })}
                      </th>
                      <th className="w-[42%] px-2 py-1.5 text-[11px] font-semibold text-app-ink-muted">
                        {t("report-heading.Email", { defaultValue: "Email" })}
                      </th>
                      <th className="w-[24%] px-2 py-1.5 text-[11px] font-semibold text-app-ink-muted">
                        {t("report-heading.Phone", { defaultValue: "Phone" })}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {contactImportPreviewRows.map((row, index) => (
                      <tr key={`${row.Email}-${index}`} className="border-t border-app-line">
                        <td className="truncate px-2 py-1.5 text-[11px] text-app-ink" title={row.Name || "--"}>
                          {row.Name || "--"}
                        </td>
                        <td className="truncate px-2 py-1.5 text-[11px] text-app-ink-muted" title={row.Email || "--"}>
                          {row.Email || "--"}
                        </td>
                        <td className="truncate px-2 py-1.5 text-[11px] text-app-ink-muted" title={row.Phone || "--"}>
                          {row.Phone || "--"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {totalContactImportPages > 1 ? (
                <div className="flex items-center justify-between border-t border-app-line bg-white px-2 py-2">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setContactImportPage((current) => Math.max(1, current - 1))}
                    disabled={contactImportPage <= 1}
                  >
                    {t("previous", { defaultValue: "Previous" })}
                  </Button>
                  <span className="text-xs text-app-ink-muted">
                    {t("new-ui.contacts.import.page-of", {
                      defaultValue: "Page {{page}} of {{total}}",
                      page: contactImportPage,
                      total: totalContactImportPages
                    })}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setContactImportPage((current) => Math.min(totalContactImportPages, current + 1))}
                    disabled={contactImportPage >= totalContactImportPages}
                  >
                    {t("next", { defaultValue: "Next" })}
                  </Button>
                </div>
              ) : null}
            </div>
          ) : null}

          {contactImportError ? <p className="m-0 text-sm text-app-danger">{contactImportError}</p> : null}

          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="ghost" onClick={closeContactImportModal} disabled={isImportingContacts}>
              {t("cancel", { defaultValue: "Cancel" })}
            </Button>
            <Button
              type="button"
              onClick={saveImportedContacts}
              disabled={!contactImportRows.length || isImportingContacts || isParsingContactImport}
            >
              {isImportingContacts
                ? t("loading", { defaultValue: "Loading..." })
                : t("new-ui.contacts.import-action", { defaultValue: "Import" })}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isContactModalOpen}
        title={
          contactModalMode === "edit"
            ? t("edit", { defaultValue: "Edit" })
            : t("add-contact", { defaultValue: "Add contact" })
        }
        onClose={closeContactModal}
        closeIconOnly
        closeLabel={t("close", { defaultValue: "Close" })}
      >
        <div className="grid grid-cols-1 gap-3">
          <Input
            id="contact-name"
            label={t("name", { defaultValue: "Name" })}
            value={contactForm.Name}
            onChange={(event) => {
              setContactForm((current) => ({ ...current, Name: event.target.value }));
              setContactFormError("");
            }}
            maxLength={120}
          />
          <Input
            id="contact-email"
            type="email"
            label={t("email", { defaultValue: "Email" })}
            value={contactForm.Email}
            onChange={(event) => {
              setContactForm((current) => ({
                ...current,
                Email: String(event.target.value || "").toLowerCase().replace(/\s/g, "")
              }));
              setContactFormError("");
            }}
            maxLength={160}
          />
          <Input
            id="contact-phone"
            label={t("phone", { defaultValue: "Phone" })}
            value={contactForm.Phone}
            onChange={(event) => {
              setContactForm((current) => ({ ...current, Phone: event.target.value }));
              setContactFormError("");
            }}
            placeholder={t("phone-optional", { defaultValue: "optional" })}
            maxLength={40}
          />
          {contactFormError ? <p className="m-0 text-sm text-app-danger">{contactFormError}</p> : null}
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="ghost" onClick={closeContactModal} disabled={isSavingContact}>
              {t("cancel", { defaultValue: "Cancel" })}
            </Button>
            <Button type="button" onClick={saveContact} disabled={isSavingContact}>
              {isSavingContact
                ? t("loading", { defaultValue: "Loading..." })
                : t("submit", { defaultValue: "Submit" })}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isDeleteContactModalOpen}
        title={t("delete", { defaultValue: "Delete" })}
        onClose={closeDeleteContactModal}
        closeIconOnly
        closeLabel={t("close", { defaultValue: "Close" })}
      >
        <div className="grid grid-cols-1 gap-3">
          <p className="m-0 text-sm text-app-ink-muted">
            {t("contact-delete-alert", {
              defaultValue: "Are you sure you want to delete this contact?"
            })}
          </p>
          <div className="rounded-lg border border-app-line bg-slate-50 px-3 py-2">
            <p className="m-0 truncate text-sm font-semibold text-app-ink">
              {contactToDelete?.Name || t("new-ui.dashboard.no-data", { defaultValue: "No data" })}
            </p>
            <p className="m-0 mt-1 truncate text-xs text-app-ink-muted">{contactToDelete?.Email || "--"}</p>
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="ghost" onClick={closeDeleteContactModal} disabled={isDeletingContact}>
              {t("cancel", { defaultValue: "Cancel" })}
            </Button>
            <Button type="button" variant="danger" onClick={deleteContact} disabled={isDeletingContact}>
              {isDeletingContact
                ? t("loading", { defaultValue: "Loading..." })
                : t("delete", { defaultValue: "Delete" })}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isDeleteDraftsConfirmModalOpen}
        title={t("new-ui.sections.history.delete-selected-confirm-title", {
          defaultValue: isExpiredHistorySection
            ? "Delete selected expired documents"
            : "Delete selected drafts"
        })}
        onClose={closeDeleteSelectedHistoryConfirmModal}
        closeIconOnly
        closeLabel={t("close", { defaultValue: "Close" })}
      >
        <div className="grid grid-cols-1 gap-3">
          <p className="m-0 text-sm text-app-ink-muted">
            {t("new-ui.sections.history.delete-selected-confirm-description", {
              defaultValue: isExpiredHistorySection
                ? "Do you want to delete the selected expired documents? This action will archive {{count}} document(s)."
                : "Do you want to delete the selected drafts? This action will archive {{count}} document(s).",
              count: selectedHistoryIds.length
            })}
          </p>
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={closeDeleteSelectedHistoryConfirmModal}
              disabled={isDeletingSelectedDrafts}
            >
              {t("cancel", { defaultValue: "Cancel" })}
            </Button>
            <Button
              type="button"
              variant="danger"
              onClick={deleteSelectedHistoryRows}
              disabled={isDeletingSelectedDrafts}
            >
              {isDeletingSelectedDrafts
                ? t("loading", { defaultValue: "Loading..." })
                : t("delete", { defaultValue: "Delete" })}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isDeleteTemplatesConfirmModalOpen}
        title={t("new-ui.templates.delete-selected-confirm-title", {
          defaultValue: "Eliminar templates seleccionados"
        })}
        onClose={closeDeleteSelectedTemplatesConfirmModal}
        closeIconOnly
        closeLabel={t("close", { defaultValue: "Close" })}
      >
        <div className="grid grid-cols-1 gap-3">
          <p className="m-0 text-sm text-app-ink-muted">
            {t("new-ui.templates.delete-selected-confirm-description", {
              defaultValue:
                "¿Deseas eliminar los templates seleccionados? Esta acción archivará {{count}} template(s).",
              count: selectedTemplateIds.length
            })}
          </p>
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={closeDeleteSelectedTemplatesConfirmModal}
              disabled={isDeletingSelectedTemplates}
            >
              {t("cancel", { defaultValue: "Cancel" })}
            </Button>
            <Button
              type="button"
              variant="danger"
              onClick={deleteSelectedTemplates}
              disabled={isDeletingSelectedTemplates}
            >
              {isDeletingSelectedTemplates
                ? t("loading", { defaultValue: "Loading..." })
                : t("delete", { defaultValue: "Delete" })}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isSignersModalOpen}
        title={t("signers", { defaultValue: "Signers" })}
        onClose={() => {
          setIsSignersModalOpen(false);
          setSignersModalRows([]);
        }}
        closeIconOnly
        closeLabel={t("close", { defaultValue: "Close" })}
      >
        <div className="grid grid-cols-1 gap-2">
          {signersModalRows.length ? (
            signersModalRows.map((signerRow, index) => (
              <div key={`${signerRow.email}-${index}`} className="rounded-lg border border-app-line bg-slate-50 px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <p className="m-0 truncate text-sm font-semibold text-app-ink" title={signerRow.email}>
                    {signerRow.name || signerRow.email}
                  </p>
                  <span
                    className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold ${getSignerActivityTone(
                      signerRow.activity
                    )}`}
                  >
                    {getSignerActivityLabel(signerRow.activity)}
                  </span>
                </div>
                {signerRow.name ? (
                  <p className="m-0 mt-0.5 truncate text-xs text-app-ink-muted" title={signerRow.email}>
                    {signerRow.email}
                  </p>
                ) : null}
                <p className="m-0 mt-1 text-[11px] text-app-ink-muted">
                  {t("new-ui.sections.history.viewed-on", { defaultValue: "Viewed on" })}:{" "}
                  {signerRow.viewedOn ? formatDate(signerRow.viewedOn, i18n) : "--"}
                </p>
                <p className="m-0 text-[11px] text-app-ink-muted">
                  {t("new-ui.sections.history.signed-on", { defaultValue: "Signed on" })}:{" "}
                  {signerRow.signedOn ? formatDate(signerRow.signedOn, i18n) : "--"}
                </p>
                <p className="m-0 text-[11px] text-app-ink-muted">
                  {t("new-ui.sections.history.signer-biometric", { defaultValue: "Biometría" })}:{" "}
                  {signerRow.requiresBiometric
                    ? t("new-ui.sections.history.signer-biometric-required", {
                        defaultValue: "Requerida"
                      })
                    : t("new-ui.sections.history.signer-biometric-not-required", {
                        defaultValue: "No requerida"
                      })}
                </p>
              </div>
            ))
          ) : (
            <p className="m-0 text-sm text-app-ink-muted">
              {t("new-ui.dashboard.recent-sent-documents.no-results", {
                defaultValue: "No results for your search."
              })}
            </p>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={isNeedYourSignDetailsModalOpen}
        title={t("new-ui.history.need-your-sign.details-title", {
          defaultValue: "Document details"
        })}
        onClose={closeNeedYourSignDetailsModal}
        closeIconOnly
        closeLabel={t("close", { defaultValue: "Close" })}
      >
        <div className="grid grid-cols-1 gap-3">
          <div className="rounded-lg border border-app-line bg-slate-50 px-3 py-2">
            <p className="m-0 text-[11px] font-semibold uppercase tracking-[0.08em] text-app-ink-muted">
              {t("report-heading.Title", { defaultValue: "Title" })}
            </p>
            <p className="m-0 mt-1 text-sm font-semibold text-app-ink">
              {getDocTitle(needYourSignDetailsRow || {}, 0, t)}
            </p>
          </div>
          <div className="rounded-lg border border-app-line bg-slate-50 px-3 py-2">
            <p className="m-0 text-[11px] font-semibold uppercase tracking-[0.08em] text-app-ink-muted">
              {t("new-ui.history.need-your-sign.requester-note", {
                defaultValue: "Nota del solicitante"
              })}
            </p>
            <p className="m-0 mt-1 text-sm text-app-ink">
              {String(needYourSignDetailsRow?.Note || "").trim() || "--"}
            </p>
          </div>
          <div className="rounded-lg border border-app-line bg-slate-50 px-3 py-2">
            <p className="m-0 text-[11px] font-semibold uppercase tracking-[0.08em] text-app-ink-muted">
              {t("report-heading.Folder", { defaultValue: "Folder" })}
            </p>
            <p className="m-0 mt-1 text-sm text-app-ink">
              {historyFolderLabel(needYourSignDetailsRow || {})}
            </p>
          </div>
          <div className="flex justify-end">
            <Button
              type="button"
              variant="ghost"
              onClick={() => downloadNeedYourSignOriginalFile(needYourSignDetailsRow || {})}
              disabled={
                !String(needYourSignDetailsRow?.objectId || "").trim() ||
                activeHistoryDownloadId === String(needYourSignDetailsRow?.objectId || "").trim() ||
                !String(needYourSignDetailsRow?.URL || "").trim()
              }
              className="inline-flex items-center gap-1.5"
            >
              <Icon name="download" className="h-4 w-4" />
              <span>
                {activeHistoryDownloadId === String(needYourSignDetailsRow?.objectId || "").trim()
                  ? t("loading", { defaultValue: "Loading..." })
                  : t("new-ui.history.need-your-sign.download-original", {
                      defaultValue: "Download original PDF"
                    })}
              </span>
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isHistoryResendModalOpen}
        title={t("resend-mail", { defaultValue: "Resend mail" })}
        onClose={closeAllInProgressActionModals}
        closeIconOnly
        closeLabel={t("close", { defaultValue: "Close" })}
      >
        <div className="grid grid-cols-1 gap-3">
          <p className="m-0 text-xs text-app-ink-muted">
            {t("new-ui.history.resend.description", {
              defaultValue: "Select signer and edit the message before resending."
            })}
          </p>
          {historyResendRecipients.length ? (
            <>
              <label className="text-xs font-medium text-app-ink" htmlFor="history-resend-recipient">
                {t("signer", { defaultValue: "Signer" })}
              </label>
              <select
                id="history-resend-recipient"
                value={historyResendRecipientEmail}
                onChange={(event) => handleHistoryResendRecipientChange(event.target.value)}
                className="w-full rounded-lg border border-app-line bg-white px-3 py-2 text-sm text-app-ink outline-none transition focus:border-app-brand focus:ring-2 focus:ring-app-brand/20"
                disabled={isHistoryActionLoading === "resend"}
              >
                {historyResendRecipients.map((recipient) => (
                  <option key={recipient.id} value={recipient.email}>
                    {recipient.name ? `${recipient.name} (${recipient.email})` : recipient.email} - {recipient.status}
                  </option>
                ))}
              </select>
              <label className="text-xs font-medium text-app-ink" htmlFor="history-resend-subject">
                {t("subject", { defaultValue: "Subject" })}
              </label>
              <input
                id="history-resend-subject"
                value={historyResendSubject}
                onChange={(event) => setHistoryResendSubject(event.target.value)}
                className="w-full rounded-lg border border-app-line bg-white px-3 py-2 text-sm text-app-ink outline-none transition focus:border-app-brand focus:ring-2 focus:ring-app-brand/20"
                disabled={isHistoryActionLoading === "resend"}
              />
              <label className="text-xs font-medium text-app-ink" htmlFor="history-resend-body">
                {t("body", { defaultValue: "Body" })}
              </label>
              <textarea
                id="history-resend-body"
                rows={8}
                value={historyResendBody}
                onChange={(event) => setHistoryResendBody(event.target.value)}
                className="w-full rounded-lg border border-app-line bg-white px-3 py-2 text-sm text-app-ink outline-none transition focus:border-app-brand focus:ring-2 focus:ring-app-brand/20"
                disabled={isHistoryActionLoading === "resend"}
              />
              {String(selectedHistoryResendRecipient?.status || "").toUpperCase() === "SIGNED" ? (
                <p className="m-0 text-xs text-app-ink-muted">
                  {t("new-ui.history.resend.signed-note", {
                    defaultValue: "This signer already completed the document."
                  })}
                </p>
              ) : null}
            </>
          ) : (
            <p className="m-0 text-sm text-app-ink-muted">
              {t("new-ui.history.resend.no-recipients", {
                defaultValue: "No recipients available for this document."
              })}
            </p>
          )}
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="ghost" onClick={closeAllInProgressActionModals} disabled={Boolean(isHistoryActionLoading)}>
              {t("cancel", { defaultValue: "Cancel" })}
            </Button>
            <Button
              type="button"
              onClick={runHistoryResend}
              disabled={
                !historyResendRecipients.length ||
                !selectedHistoryResendRecipient ||
                String(selectedHistoryResendRecipient?.status || "").toUpperCase() === "SIGNED" ||
                isHistoryActionLoading === "resend"
              }
            >
              {isHistoryActionLoading === "resend"
                ? t("loading", { defaultValue: "Loading..." })
                : t("resend", { defaultValue: "Resend" })}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isHistoryRenameModalOpen}
        title={t("rename", { defaultValue: "Rename" })}
        onClose={closeAllInProgressActionModals}
        closeIconOnly
        closeLabel={t("close", { defaultValue: "Close" })}
      >
        <div className="grid grid-cols-1 gap-3">
          <label className="text-xs font-medium text-app-ink" htmlFor="history-rename-input">
            {t("name", { defaultValue: "Name" })}
          </label>
          <input
            id="history-rename-input"
            value={historyRenameValue}
            onChange={(event) => setHistoryRenameValue(event.target.value)}
            className="w-full rounded-lg border border-app-line bg-white px-3 py-2 text-sm text-app-ink outline-none transition focus:border-app-brand focus:ring-2 focus:ring-app-brand/20"
            maxLength={180}
            disabled={isHistoryActionLoading === "rename"}
          />
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={closeAllInProgressActionModals}
              disabled={isHistoryActionLoading === "rename"}
            >
              {t("cancel", { defaultValue: "Cancel" })}
            </Button>
            <Button
              type="button"
              onClick={runHistoryRename}
              disabled={!String(historyRenameValue || "").trim() || isHistoryActionLoading === "rename"}
            >
              {isHistoryActionLoading === "rename"
                ? t("loading", { defaultValue: "Loading..." })
                : t("save", { defaultValue: "Save" })}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isTemplateRenameModalOpen}
        title={t("new-ui.templates.rename-title", { defaultValue: "Rename template" })}
        onClose={closeTemplateRenameModal}
        closeIconOnly
        closeLabel={t("close", { defaultValue: "Close" })}
      >
        <div className="grid grid-cols-1 gap-3">
          <label className="text-xs font-medium text-app-ink" htmlFor="template-rename-input">
            {t("name", { defaultValue: "Name" })}
          </label>
          <input
            id="template-rename-input"
            value={templateRenameValue}
            onChange={(event) => setTemplateRenameValue(event.target.value)}
            className="w-full rounded-lg border border-app-line bg-white px-3 py-2 text-sm text-app-ink outline-none transition focus:border-app-brand focus:ring-2 focus:ring-app-brand/20"
            maxLength={180}
            disabled={isTemplateRenameSaving}
          />
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={closeTemplateRenameModal}
              disabled={isTemplateRenameSaving}
            >
              {t("cancel", { defaultValue: "Cancel" })}
            </Button>
            <Button
              type="button"
              onClick={runTemplateRename}
              disabled={!String(templateRenameValue || "").trim() || isTemplateRenameSaving}
            >
              {isTemplateRenameSaving
                ? t("loading", { defaultValue: "Loading..." })
                : t("save", { defaultValue: "Save" })}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isHistoryRevokeModalOpen}
        title={t("revoke-document", { defaultValue: "Revoke document" })}
        onClose={closeAllInProgressActionModals}
        closeIconOnly
        closeLabel={t("close", { defaultValue: "Close" })}
      >
        <div className="grid grid-cols-1 gap-3">
          <p className="m-0 text-sm text-app-ink-muted">
            {t("revoke-document-alert", {
              defaultValue: "Are you sure you want to revoke this document?"
            })}
          </p>
          <label className="text-xs font-medium text-app-ink" htmlFor="history-revoke-reason">
            {t("reason", { defaultValue: "Reason" })}
          </label>
          <textarea
            id="history-revoke-reason"
            rows={3}
            value={historyRevokeReason}
            onChange={(event) => setHistoryRevokeReason(event.target.value)}
            placeholder={t("reason-optional", { defaultValue: "Reason (optional)" })}
            className="w-full rounded-lg border border-app-line bg-white px-3 py-2 text-sm text-app-ink outline-none transition focus:border-app-brand focus:ring-2 focus:ring-app-brand/20"
            disabled={isHistoryActionLoading === "revoke"}
          />
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={closeAllInProgressActionModals}
              disabled={isHistoryActionLoading === "revoke"}
            >
              {t("cancel", { defaultValue: "Cancel" })}
            </Button>
            <Button type="button" variant="danger" onClick={runHistoryRevoke} disabled={isHistoryActionLoading === "revoke"}>
              {isHistoryActionLoading === "revoke"
                ? t("loading", { defaultValue: "Loading..." })
                : t("revoke-document", { defaultValue: "Revoke" })}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isHistorySaveTemplateModalOpen}
        title={t("btnLabel.Save as template", { defaultValue: "Save as template" })}
        onClose={closeAllInProgressActionModals}
        closeIconOnly
        closeLabel={t("close", { defaultValue: "Close" })}
      >
        <div className="grid grid-cols-1 gap-3">
          <p className="m-0 text-sm text-app-ink-muted">
            {t("save-as-template-?", {
              defaultValue: "Do you want to save this document as a template?"
            })}
          </p>
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={closeAllInProgressActionModals}
              disabled={isHistoryActionLoading === "save-template"}
            >
              {t("cancel", { defaultValue: "Cancel" })}
            </Button>
            <Button
              type="button"
              onClick={runHistorySaveAsTemplate}
              disabled={isHistoryActionLoading === "save-template"}
            >
              {isHistoryActionLoading === "save-template"
                ? t("loading", { defaultValue: "Loading..." })
                : t("btnLabel.Save as template", { defaultValue: "Save as template" })}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isHistoryDeleteModalOpen}
        title={t("archive", { defaultValue: "Archive" })}
        onClose={closeAllInProgressActionModals}
        closeIconOnly
        closeLabel={t("close", { defaultValue: "Close" })}
      >
        <div className="grid grid-cols-1 gap-3">
          <p className="m-0 text-sm text-app-ink-muted">
            {t("history-archive-confirm", {
              defaultValue: "Do you want to archive this in-progress document?"
            })}
          </p>
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={closeAllInProgressActionModals}
              disabled={isHistoryActionLoading === "delete"}
            >
              {t("cancel", { defaultValue: "Cancel" })}
            </Button>
            <Button type="button" variant="danger" onClick={runHistoryDelete} disabled={isHistoryActionLoading === "delete"}>
              {isHistoryActionLoading === "delete"
                ? t("loading", { defaultValue: "Loading..." })
                : t("archive", { defaultValue: "Archive" })}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isCompletedSummaryModalOpen}
        title={t("new-ui.individual-signing.completed-kicker", {
          defaultValue: "Signing completed"
        })}
        onClose={closeCompletedSummaryModal}
        closeIconOnly
        closeLabel={t("close", { defaultValue: "Close" })}
      >
        <div className="grid grid-cols-1 gap-3">
          <p className="m-0 text-lg font-semibold text-app-ink">
            {t("new-ui.individual-signing.completed-title", {
              defaultValue: "Document finalized"
            })}
          </p>
          <div className="rounded-lg border border-app-line bg-slate-50 px-3 py-2">
            <p className="m-0 text-[11px] font-semibold uppercase tracking-[0.08em] text-app-ink-muted">
              {t("document-title", { defaultValue: "Document title" })}
            </p>
            <p className="m-0 mt-1 text-sm font-semibold text-app-ink">{completedSummary?.name || "--"}</p>
          </div>
          <div className="rounded-lg border border-app-line bg-slate-50 px-3 py-2">
            <p className="m-0 text-[11px] font-semibold uppercase tracking-[0.08em] text-app-ink-muted">
              {t("new-ui.history.need-your-sign.requester-note", {
                defaultValue: "Nota del solicitante"
              })}
            </p>
            <p className="m-0 mt-1 text-sm text-app-ink">{completedSummary?.note || "--"}</p>
          </div>
          <div className="rounded-lg border border-app-line bg-slate-50 px-3 py-2">
            <p className="m-0 text-[11px] font-semibold uppercase tracking-[0.08em] text-app-ink-muted">
              {t("report-heading.Folder", { defaultValue: "Folder" })}
            </p>
            <p className="m-0 mt-1 text-sm text-app-ink">{completedSummary?.folder || "--"}</p>
          </div>
          {completedSummaryError ? <p className="m-0 text-sm text-app-danger">{completedSummaryError}</p> : null}
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              onClick={() => triggerDownload(completedSummary?.signedUrl)}
              disabled={isLoadingCompletedSummary || !completedSummary?.signedUrl}
            >
              {t("new-ui.individual-signing.download-pdf", {
                defaultValue: "Download pdf"
              })}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={downloadCompletedCertificatePdf}
              disabled={isLoadingCompletedSummary || isGeneratingCompletedCertificate || isDownloadingCompletedBundle}
            >
              {isDownloadingCompletedBundle
                ? t("loading", { defaultValue: "Loading..." })
                : t("new-ui.individual-signing.download-certificate", {
                    defaultValue: "Download certificate"
                  })}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isCreateFolderModalOpen}
        title={t("add-new-folder", { defaultValue: "Add a new folder" })}
        onClose={() => {
          if (isCreatingFolder) {
            return;
          }
          setIsCreateFolderModalOpen(false);
          setNewFolderName("");
          setNewFolderError("");
        }}
        closeIconOnly
        closeLabel={t("close", { defaultValue: "Close" })}
      >
        <div className="grid grid-cols-1 gap-3">
          <Input
            id="new-drive-folder-name"
            label={t("name", { defaultValue: "Name" })}
            value={newFolderName}
            onChange={(event) => {
              setNewFolderName(event.target.value);
              setNewFolderError("");
            }}
            error={newFolderError}
            placeholder={t("create-folder", { defaultValue: "Create folder" })}
            maxLength={120}
          />
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              disabled={isCreatingFolder}
              onClick={() => {
                setIsCreateFolderModalOpen(false);
                setNewFolderName("");
                setNewFolderError("");
              }}
            >
              {t("cancel", { defaultValue: "Cancel" })}
            </Button>
            <Button type="button" variant="success" onClick={createDriveFolder} disabled={isCreatingFolder}>
              {isCreatingFolder
                ? t("loading", { defaultValue: "Loading..." })
                : t("add-folder", { defaultValue: "Add folder" })}
            </Button>
          </div>
        </div>
      </Modal>
    </AppShell>
  );
}

export default SectionWorkspacePage;
